class Responsive {
    static currentBreakpoint() {
        const fallback = "desk";
        if (window.matchMedia('screen and (max-width: 767px)').matches) {
            return 'phone';
        }
        if (window.matchMedia('screen and (min-width: 768px) and (max-width: 991px)').matches) {
            return 'tablet';
        }
        if (window.matchMedia('screen and (min-width: 992px) and (max-width: 1279px)').matches) {
            return 'lap';
        }
        return fallback;
    }
    static matchesBreakpoint(breakpoint) {
        switch (breakpoint) {
            case 'phone':
                return window.matchMedia('screen and (max-width: 767px)').matches;
            case 'tablet':
                return window.matchMedia('screen and (min-width: 768px) and (max-width: 991px)').matches;
            case 'tablet-and-up':
                return window.matchMedia('screen and (min-width: 768px)').matches;
            case 'pocket':
                return window.matchMedia('screen and (max-width: 991px)').matches;
            case 'lap':
                return window.matchMedia('screen and (min-width: 992px) and (max-width: 1279px)').matches;
            case 'lap-and-up':
                return window.matchMedia('screen and (min-width: 992px)').matches;
            case 'desk':
                return window.matchMedia('screen and (min-width: 1280px)').matches;
            case 'widescreen':
                return window.matchMedia('screen and (min-width: 1600px)').matches;
            case 'supports-hover':
                return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
            default:
                return false;
        }
    }
    constructor() {
        this.windowWidth = window.innerWidth;
        this.currentBreakpoint = Responsive.currentBreakpoint();
        this.resizeHandler = Responsive.debounce(() => {
            const newViewpoint = Responsive.currentBreakpoint();
            if (this.currentBreakpoint === newViewpoint)
                return;
            document.dispatchEvent(new CustomEvent('breakpoint:changed', {
                detail: {
                    previousBreakpoint: this.currentBreakpoint,
                    currentBreakpoint: newViewpoint
                }
            }));
            this.currentBreakpoint = newViewpoint;
        }, 150);
        const headerSection = document.querySelector('.shopify-section--shop-header');
        window.addEventListener('resize', () => {
            this.resizeHandler();
            if (headerSection)
                this.handleResize(headerSection);
        });
    }
    handleResize(headerSection) {
        //@ts-expect-error
        fastdom.measure(() => {
            const newWidth = window.innerWidth;
            if (newWidth === this.windowWidth)
                return;
            this.windowWidth = newWidth;
            //@ts-expect-error
            fastdom.mutate(() => {
                document.documentElement.style.setProperty('--window-height', `${window.innerHeight}px`);
                if (headerSection) {
                    document.documentElement.style.setProperty('--header-height', `${headerSection.clientHeight}px`);
                }
            });
        });
    }
    static debounce(fn, delay) {
        let timer;
        return () => {
            clearTimeout(timer);
            timer = setTimeout(fn, delay);
        };
    }
}
class Animation {
    /**
     * Slide up aims to close an element. To do that, we take the height of the element, and set it to 0 to
     * force an animation
     */
    slideUp(element) {
        delete element.dataset.animation;
        element.style.height = `${element.scrollHeight}px`; // Force previous height to allow CSS transition
        element.offsetHeight; // Force redraw
        element.style.height = "0";
    }
    /**
     * Slide down aims to open an element. To do that, you must make sure that the element you are trying to open
     * is set with height: 0; overflow: hidden in the CSS, and does not contain any padding nor margin.
     */
    static slideDown(element) {
        if (element.style.height === 'auto' || !element.firstElementChild)
            return;
        element.dataset.animation = 'slide-down';
        // To do the animation we temporarily hide it, check the height, and transition to it
        element.style.height = `${element.firstElementChild.scrollHeight}px`;
        const transitionEnded = (event) => {
            if (event.propertyName === 'height' && element.dataset.animation === 'slide-down') {
                delete element.dataset.animation;
                element.style.height = 'auto';
                element.removeEventListener('transitionend', transitionEnded);
            }
        };
        element.addEventListener('transitionend', transitionEnded);
    }
}
class Dom {
    /**
     * Get all the previous and next siblings, optionally filtered by a selector
     */
    static getSiblings(element, filter, includeSelf = false) {
        const parent = element.parentNode;
        if (!parent)
            return [];
        const children = Array.from(parent.children);
        return children.filter(child => {
            if (child === element)
                return includeSelf;
            if (filter) {
                return child.matches(filter);
            }
            return true;
        });
    }
    /**
     * By default, NodeList object are only iterable with forEach on newest browsers. To support it cross-browser,
     * we need to normalize it
     */
    static nodeListToArray(collection, filter) {
        const array = Array.from(collection);
        if (filter) {
            return array.filter(node => node.matches(filter));
        }
        return array;
    }
    /**
     * Calculate an element width with its margin
     */
    static outerWidthWithMargin(element) {
        const style = window.getComputedStyle(element);
        return element.offsetWidth + parseFloat(style.marginLeft) + parseFloat(style.marginRight);
    }
    /**
     * Calculate an element height with its margin
     */
    static outerHeightWithMargin(element) {
        const style = window.getComputedStyle(element);
        return element.offsetHeight + parseFloat(style.marginTop) + parseFloat(style.marginBottom);
    }
    /**
     * Disable scrolling on the document
     */
    static disableScroll(disable, element) {
        if (disable) {
            if (this.noScrollActivators.includes(element))
                return;
            this.noScrollActivators.push(element);
            document.documentElement.classList.add('no-scroll');
            window.dispatchEvent(new Event('resize'));
            return;
        }
        if (!this.noScrollActivators.includes(element))
            return;
        this.noScrollActivators = this.noScrollActivators.filter(item => item !== element);
        if (this.noScrollActivators.length === 0)
            document.documentElement.classList.remove('no-scroll');
        window.dispatchEvent(new Event('resize'));
    }
    /**
     * Eval scripts in a container, useful for ajax loaded content
     */
    static evalScripts(container) {
        const scripts = container.getElementsByTagName("script");
        for (let i = 0; i < scripts.length; i++) {
            if (scripts[i].src) {
                const newScript = document.createElement("script");
                for (const attr of scripts[i].attributes) {
                    newScript.setAttribute(attr.name, attr.value);
                }
                if (scripts[i].innerHTML)
                    newScript.innerHTML = scripts[i].innerHTML;
                if (scripts[i].parentNode)
                    scripts[i].parentNode.replaceChild(newScript, scripts[i]);
            }
            else {
                if (scripts[i].type && scripts[i].type !== "text/javascript")
                    continue;
                const scriptContent = scripts[i].text || scripts[i].textContent || scripts[i].innerHTML;
                if (!scriptContent)
                    continue;
                eval(scriptContent);
            }
        }
    }
}
Dom.noScrollActivators = [];
class Collapsible {
    static init(domDelegateInstance) {
        domDelegateInstance.on('click', '[data-action="toggle-collapsible"]', (e, target) => {
            const collapsible = new Collapsible(e.target);
            collapsible.toggle(e, target);
        });
        // Keep aria state in sync with the visually auto-expanded state at each breakpoint.
        // Without this, screen readers see the content as hidden while it's visually shown.
        Collapsible.AUTO_EXPAND_VARIANTS.forEach(({ className, mediaQuery }) => {
            const query = window.matchMedia(mediaQuery);
            const sync = () => {
                document.querySelectorAll(`.${className}`).forEach(collapsible => {
                    Collapsible.syncAutoExpandAria(collapsible, query.matches);
                });
            };
            sync();
            query.addEventListener('change', sync);
        });
    }
    static syncAutoExpandAria(collapsible, isAutoExpanded) {
        const button = collapsible.querySelector('[data-action="toggle-collapsible"]');
        const inner = collapsible.querySelector('.Collapsible__Inner');
        if (!button || !inner)
            return;
        button.setAttribute('aria-expanded', isAutoExpanded ? 'true' : 'false');
        inner.setAttribute('aria-hidden', isAutoExpanded ? 'false' : 'true');
    }
    static isAutoExpandedNow(collapsible) {
        if (!collapsible)
            return false;
        return Collapsible.AUTO_EXPAND_VARIANTS.some(({ className, mediaQuery }) => collapsible.classList.contains(className) && window.matchMedia(mediaQuery).matches);
    }
    constructor(element) {
        element.addEventListener('keydown', this.onKeyPress.bind(this));
    }
    toggle(event, target) {
        event.preventDefault();
        const parentCollapsible = target.closest('.Collapsible');
        if (Collapsible.isAutoExpandedNow(parentCollapsible)) {
            return;
        }
        const toggleButton = target.getAttribute('data-action') === 'toggle-collapsible' ? target : target.closest('[data-action="toggle-collapsible"]'), collapsibleInner = parentCollapsible.querySelector(`#${toggleButton.getAttribute('aria-controls')}`);
        const isOpen = toggleButton.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
            this.close(toggleButton, collapsibleInner);
        }
        else {
            this.open(toggleButton, collapsibleInner);
        }
    }
    setCollapsibleHeightVariable(inner) {
        if (!inner || !inner.firstElementChild)
            return;
        const onTransitionEnd = (e) => {
            if (e.propertyName !== 'height' || !e.target)
                return;
            e.target.style.removeProperty('--collapsible-height');
            e.target.removeEventListener('transitionend', onTransitionEnd);
        };
        inner.style.setProperty('--collapsible-height', inner.firstElementChild.scrollHeight + 'px');
        inner.offsetHeight; // Force reflow
        inner.addEventListener('transitionend', onTransitionEnd);
    }
    open(button, collapsible) {
        //close all others
        const parent = button.parentElement;
        Dom.getSiblings(parent).forEach(sibling => {
            const [button, collapsible] = sibling.children;
            if (!button || !collapsible)
                return;
            this.close(button, collapsible);
        });
        this.setCollapsibleHeightVariable(collapsible);
        button.setAttribute('aria-expanded', 'true');
        collapsible.setAttribute('aria-hidden', 'false');
    }
    close(button, collapsible) {
        this.setCollapsibleHeightVariable(collapsible);
        button.setAttribute('aria-expanded', 'false');
        collapsible.style.removeProperty('--collapsible-height');
        collapsible.setAttribute('aria-hidden', 'true');
    }
    onKeyPress(event) {
        if (event.key !== 'Escape')
            return;
        let parentCollapsible = event.target.closest('.Collapsible');
        while (parentCollapsible) {
            const toggleButton = parentCollapsible.querySelector('[data-action="toggle-collapsible"]');
            if (toggleButton.getAttribute('aria-expanded') === 'true') {
                toggleButton.focus();
                this.toggle(event, toggleButton);
                event.stopPropagation();
                break;
            }
            parentCollapsible = parentCollapsible.parentElement.closest('.Collapsible');
        }
    }
}
Collapsible.AUTO_EXPAND_VARIANTS = [
    { className: 'Collapsible--autoExpand', mediaQuery: '(min-width: 768px)' },
    { className: 'Collapsible--autoExpandLg', mediaQuery: '(min-width: 1024px)' },
];
class LoginArea {
    static init(domDelegateInstance) {
        const customerLoginForm = document.getElementById('customer_login');
        const recoverPasswordForm = document.getElementById('recover_customer_password');
        if (!customerLoginForm || !recoverPasswordForm) {
            return;
        }
        const searchParams = new URLSearchParams(window.location.search);
        const recoverParam = searchParams.get('recover');
        const shouldShowRecoverForm = searchParams.has('recover')
            && (recoverParam === null || ['true', '1', ''].includes(recoverParam))
            || ['recover', 'forgot'].includes(searchParams.get('view') || '')
            || ['recover', 'forgot'].includes(searchParams.get('mode') || '');
        if (shouldShowRecoverForm) {
            customerLoginForm.style.display = 'none';
            recoverPasswordForm.style.display = 'block';
        }
        domDelegateInstance.on('click', '[data-action="toggle-recover-form"]', () => {
            const isLoginActive = customerLoginForm.style.display === 'block';
            customerLoginForm.style.display = isLoginActive ? 'none' : 'block';
            recoverPasswordForm.style.display = isLoginActive ? 'block' : 'none';
        });
    }
}
class Accessibility {
    /**
     * Traps the focus in a particular container
     */
    static trapFocus(container, key) {
        if (this.traps[key]) {
            this.removeFocus(key);
        }
        const focusableElements = Dom.nodeListToArray(container.querySelectorAll(this.FOCUSABLE_SELECTORS)).filter(el => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length));
        const trapHandler = (event) => {
            if (event.key !== 'Tab')
                return;
            if (!focusableElements.length)
                return;
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            if (document.activeElement === lastElement && !event.shiftKey) {
                firstElement.focus();
                event.preventDefault();
            }
            else if (document.activeElement === firstElement && event.shiftKey) {
                lastElement.focus();
                event.preventDefault();
            }
        };
        focusableElements[0]?.focus();
        container.addEventListener('keydown', trapHandler);
        this.traps[key] = { container, handler: trapHandler };
    }
    /**
     * Removes the trap of focus in a particular container
     */
    static removeFocus(key) {
        const trap = this.traps[key];
        if (!trap)
            return;
        trap.container.removeEventListener('keydown', trap.handler);
        delete this.traps[key];
    }
    /**
     * Reset any previous trap focus
     */
    static clearTrapFocus() {
        Object.keys(this.traps).forEach(key => this.removeFocus(key));
    }
    constructor() {
        window.addEventListener('keydown', this.onKeyDown);
    }
    onMouseDown() {
        document.body.classList.remove('is-tabbing');
        document.removeEventListener('mousedown', this.onMouseDown);
        window.addEventListener('keydown', this.onKeyDown);
    }
    onKeyDown(event) {
        if (event.key !== 'Tab')
            return;
        document.body.classList.add('is-tabbing');
        window.removeEventListener('keydown', this.onKeyDown);
        document.addEventListener('mousedown', this.onMouseDown);
    }
}
Accessibility.FOCUSABLE_SELECTORS = 'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled])';
Accessibility.traps = {};
class Localization {
    static async init() {
        const switzerlandPopoverId = 'switzerland-modal';
        const switzerlandPopover = document.getElementById(switzerlandPopoverId);
        if (switzerlandPopover) {
            await new Localization().showSwitzerlandLocalizationModal(switzerlandPopoverId);
        }
        const italyPopoverId = 'italy-modal';
        const italyPopover = document.getElementById(italyPopoverId);
        if (italyPopover) {
            await new Localization().showItalyLocalizationModal(italyPopoverId);
        }
    }
    constructor() {
        const forms = Dom.nodeListToArray(document.querySelectorAll('form[data-form="localization"]'));
        forms.forEach((form) => {
            const countrySelector = form.querySelector('select[name="country_code"]');
            if (countrySelector) {
                countrySelector.addEventListener('change', () => {
                    form.requestSubmit();
                });
            }
            form.addEventListener('submit', () => {
                const countryInput = form.querySelector('[name="country_code"]');
                if (!countryInput)
                    return;
                localStorage.setItem('themeSelectedLocation', countryInput.value);
            });
        });
    }
    async showSwitzerlandLocalizationModal(modalId) {
        const shopCountry = window.Shopify.country;
        const selectedCountry = localStorage.getItem('themeSelectedLocation');
        const locationCountry = await Localization.getIPLocation();
        const relevantCountry = selectedCountry || locationCountry;
        const shouldTriggerSwissLogic = relevantCountry === 'CH' && shopCountry !== 'CH';
        if (!shouldTriggerSwissLogic)
            return;
        localStorage.setItem('themeSelectedLocation', 'DE');
        new Modal(document.getElementById(modalId)).open();
    }
    async showItalyLocalizationModal(modalId) {
        const shopCountry = window.Shopify.country;
        const selectedCountry = localStorage.getItem('themeSelectedLocation');
        if (shopCountry !== 'IT' || selectedCountry === 'IT' || !navigator.language.includes('de'))
            return;
        localStorage.setItem('themeSelectedLocation', 'IT');
        new Modal(document.getElementById(modalId)).open();
    }
    static async getIPLocation() {
        const storageCountry = localStorage.getItem('themeLocation');
        if (storageCountry)
            return storageCountry;
        return new Promise((resolve) => {
            if (window.Shopify.customerPrivacy) {
                const countryCode = window.Shopify.customerPrivacy.getRegion().substring(0, 2);
                localStorage.setItem('themeLocation', countryCode);
                return countryCode;
            }
            window.addEventListener('theme:consentapi:ready', () => {
                resolve("DE"); //Fallback
            });
        });
    }
}
class Drawer {
    static init(domDelegateInstance) {
        document.querySelectorAll('[data-sidebar]').forEach(drawer => {
            if (!Drawer.drawers[drawer.id])
                new Drawer(drawer);
        });
        domDelegateInstance.on('click', '[data-action="open-drawer"]', (e) => {
            e.preventDefault();
            const activator = e.target.getAttribute('data-action') === 'open-drawer' ? e.target : e.target.closest('[data-action="open-drawer"]');
            const drawerId = activator.getAttribute('data-drawer-id');
            const drawerElement = document.getElementById(drawerId);
            if (!drawerId || !drawerElement)
                return;
            let drawer = Drawer.drawers[drawerId];
            if (!drawer)
                drawer = new Drawer(drawerElement);
            drawer.open();
        });
        domDelegateInstance.on('click', '[data-action="close-drawer"]', (e) => {
            e.preventDefault();
            const activator = e.target.getAttribute('data-action') === 'close-drawer' ? e.target : e.target.closest('[data-action="close-drawer"]');
            const drawerId = activator.getAttribute('data-drawer-id');
            const drawerElement = document.getElementById(drawerId);
            if (!drawerId || !drawerElement)
                return;
            let drawer = Drawer.drawers[drawerId];
            if (!drawer)
                drawer = new Drawer(drawerElement);
            drawer.close();
        });
    }
    constructor(drawer, options) {
        this.isOpen = false;
        this.stackOptions = {
            increasedSize: false,
            decreasedSize: false
        };
        this.overlay = null;
        this.onHandleResize = null;
        this.drawer = drawer;
        this.id = drawer?.id?.length ? drawer.id : `drawer-${Math.random().toString(36).substring(2, 15)}`;
        this.onOpen = options?.onOpen || function () {
        };
        this.onClose = options?.onClose || function () {
        };
        this.onTransitionEnd = this.transitionEnd.bind(this);
        this.onHandleKeydown = this.handleKeydown.bind(this);
        this.drawer.addEventListener('keydown', this.onHandleKeydown);
        Drawer.drawers[this.id] = this;
    }
    open() {
        if (this.isOpen)
            return;
        new CustomEvent('theme:sidebarMenu:close');
        this.isOpen = true;
        this.initAjaxSidebar().then();
        // Make sure that the search is closed whenever a drawer is opened
        this.drawer.dispatchEvent(new CustomEvent('search:close', {
            bubbles: true
        }));
        this.drawer.dispatchEvent(new CustomEvent('navigation:close', {
            bubbles: true
        }));
        this.drawer.addEventListener('transitionend', this.onTransitionEnd);
        this.drawer.setAttribute('aria-hidden', 'false');
        if (Drawer.stack.length === 0) {
            Dom.disableScroll(true, this.drawer);
            window.pageOverlay.open(this.id, Drawer.closeAll);
        }
        else {
            this.drawer.style.zIndex = (Drawer.stack.length * 10 + 20) + '';
            this.drawer.querySelectorAll('.Drawer__Close, .Drawer__BottomCloseButton').forEach(closeButton => {
                closeButton.classList.toggle('none', !closeButton.classList.contains('Drawer__Close--stacked'));
            });
            const lastDrawer = Drawer.stack[Drawer.stack.length - 1];
            if (lastDrawer.drawer.classList.contains('Drawer--large') && !this.drawer.classList.contains('Drawer--large')) {
                this.stackOptions.increasedSize = true;
                this.drawer.classList.add('Drawer--large');
            }
            else if (!lastDrawer.drawer.classList.contains('Drawer--large') && this.drawer.classList.contains('Drawer--large')) {
                this.stackOptions.decreasedSize = true;
                this.drawer.classList.remove('Drawer--large');
            }
            lastDrawer.drawer.classList.add('Drawer--stackBottom');
        }
        Drawer.stack.push(this);
        Accessibility.trapFocus(this.drawer, this.id);
        this.calculateMaxHeight();
        this.onHandleResize = this.calculateMaxHeight.bind(this);
        window.addEventListener('resize', this.onHandleResize);
        this.onOpen(this);
        this.drawer.dispatchEvent(new CustomEvent('theme:drawer:opened', { detail: { id: this.id }, bubbles: true }));
    }
    close() {
        if (!this.isOpen)
            return;
        this.isOpen = false;
        this.drawer.addEventListener('transitionend', this.onTransitionEnd);
        this.drawer.setAttribute('aria-hidden', 'true');
        Accessibility.removeFocus(this.id);
        if (Drawer.stack.length > 1) {
            const lastDrawer = Drawer.stack[Drawer.stack.length - 2];
            lastDrawer.drawer.classList.remove('Drawer--stackBottom');
            window.pageOverlay.open(lastDrawer.id, lastDrawer.close.bind(lastDrawer));
        }
        else {
            window.pageOverlay.close(this.id);
            Dom.disableScroll(false, this.drawer);
        }
        Drawer.stack = Drawer.stack.filter(drawer => drawer !== this);
        if (this.onHandleResize) {
            window.removeEventListener('resize', this.onHandleResize);
            this.onHandleResize = null;
        }
        this.onClose(this);
        this.drawer.dispatchEvent(new CustomEvent('theme:drawer:closed', { detail: { id: this.id }, bubbles: true }));
    }
    static closeAll() {
        for (let i = Drawer.stack.length - 1; i >= 0; i--) {
            Drawer.stack[i].close();
        }
    }
    destroy() {
        if (this.onHandleResize) {
            window.removeEventListener('resize', this.onHandleResize);
            this.onHandleResize = null;
        }
        this.drawer.removeEventListener('keydown', this.onHandleKeydown);
    }
    async initAjaxSidebar() {
        if (!this.drawer.hasAttribute('data-ajax-sections'))
            return;
        if (this.drawer.hasAttribute('data-ajax-initialized'))
            return;
        const contentContainer = this.drawer.querySelector('[data-ajax-container]');
        if (!contentContainer)
            return;
        contentContainer.classList.add('loading');
        const ajaxContext = this.drawer.getAttribute('data-ajax-context') || window.location.pathname, ajaxSections = this.drawer.getAttribute('data-ajax-sections');
        if (!ajaxSections)
            return;
        // Keep current search params because otherwise it won't work for draft product previews
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.append('sections', ajaxSections);
        const ajaxUrl = `${ajaxContext}?${searchParams.toString()}`;
        try {
            const response = fetch(ajaxUrl, {
                credentials: 'same-origin',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest' // This is needed as currently there is a bug in Shopify that assumes this header
                }
            });
            const sectionsJson = await (await response).json();
            contentContainer.innerHTML = Array.from(Object.values(sectionsJson)).join('');
            // Always remove page header from main-page section
            if (sectionsJson['main-page']) {
                contentContainer.querySelectorAll('[data-section-type="main-page"] header.PageHeader').forEach(element => {
                    element.remove();
                });
            }
            this.drawer.setAttribute('data-ajax-initialized', 'true');
            Dom.evalScripts(contentContainer);
            contentContainer.classList.remove('loading');
            initFunctions(contentContainer);
        }
        catch (e) {
            console.error('Error loading popup ajax content: ', e);
        }
    }
    calculateMaxHeight() {
        this.drawer.style.maxHeight = window.innerHeight + 'px';
    }
    transitionEnd(event) {
        if (event.propertyName !== 'visibility' || event.target !== this.drawer)
            return;
        if (this.isOpen) {
            Accessibility.trapFocus(this.drawer, this.id);
        }
        else {
            if (this.stackOptions.increasedSize || this.stackOptions.decreasedSize) {
                this.drawer.classList.toggle('Drawer--large', this.stackOptions.decreasedSize);
                this.stackOptions.increasedSize = false;
                this.stackOptions.decreasedSize = false;
            }
            this.drawer.querySelectorAll('.Drawer__Close, .Drawer__BottomCloseButton').forEach(closeButton => {
                closeButton.classList.toggle('none', closeButton.classList.contains('Drawer__Close--stacked'));
            });
        }
        this.drawer.removeEventListener('transitionend', this.onTransitionEnd);
    }
    handleKeydown(event) {
        if (event.key !== 'Escape')
            return;
        this.close();
    }
}
Drawer.drawers = {};
Drawer.stack = [];
class TabSwitch {
    static init(element) {
        element.querySelectorAll('.tab-switch:not(.tab-switch-initialized)').forEach((tabSwitch) => new TabSwitch(tabSwitch));
    }
    constructor(tabButtonContainer) {
        tabButtonContainer.classList.add('tab-switch-initialized');
        this.container = tabButtonContainer.closest('section, .Collapsible__Content');
        tabButtonContainer.querySelectorAll('[data-action="toggle-tab"]').forEach((button) => {
            button.addEventListener('click', (e) => {
                this.switchTab(button);
                const container = e.target
                    .closest('.container')
                    ?.querySelector('.tab-panel[aria-hidden="false"]');
                if (!container)
                    return;
                const sliderElement = container.querySelector('.Slider');
                if (!sliderElement)
                    return;
                const slider = new Slider(sliderElement);
                slider.enable();
            });
        });
    }
    switchTab(button) {
        if (button.classList.contains('is-active'))
            return;
        button.classList.add('is-active');
        Dom.getSiblings(button).forEach(button => {
            button.classList.remove('is-active');
        });
        const tabPanelToShow = this.container.querySelector("#".concat(button.getAttribute('aria-controls')));
        if (!tabPanelToShow)
            return;
        tabPanelToShow.setAttribute('aria-hidden', 'false');
        Dom.getSiblings(tabPanelToShow).forEach(panel => {
            panel.setAttribute('aria-hidden', 'true');
        });
    }
}
export class FloatingWidget {
    static init(selectors) {
        if (!FloatingWidget.enabled)
            return;
        const timeout = 15000;
        const checkPresence = (selector, time) => {
            if (!FloatingWidget.enabled)
                return;
            const widget = document.querySelector(selector);
            if (widget) {
                FloatingWidget.widgets.push(new FloatingWidget(widget));
            }
            else {
                if (time <= 0)
                    return;
                setTimeout(() => {
                    checkPresence(selector, time - 1000);
                }, 1000);
            }
        };
        for (const selector of selectors) {
            checkPresence(selector, timeout);
        }
        window.FloatingWidget = FloatingWidget;
    }
    constructor(widget) {
        this.widget = widget;
        this.lastScrollY = window.scrollY;
        this.visible = true;
        this.debouncedOnScroll = Responsive.debounce(this.onScroll.bind(this), 50);
        this.widget.style.setProperty('transition', 'transform 0.4s ease-in-out', 'important');
        this.widget.style.setProperty('transform', 'translateX(0)', 'important');
        window.addEventListener('scroll', this.debouncedOnScroll, { passive: true });
    }
    show() {
        if (this.visible)
            return;
        this.visible = true;
        this.widget.style.setProperty('transform', 'translateX(0)', 'important');
    }
    hide() {
        if (!this.visible)
            return;
        this.visible = false;
        this.widget.style.setProperty('transform', 'translateX(30vw)', 'important');
    }
    onScroll() {
        if (!this.widget.isConnected) {
            window.removeEventListener('scroll', this.debouncedOnScroll);
            return;
        }
        if (this.visible && window.scrollY > this.lastScrollY) {
            this.hide();
        }
        else if (!this.visible && window.scrollY < this.lastScrollY) {
            this.show();
        }
        this.lastScrollY = window.scrollY;
    }
    destroy() {
        window.removeEventListener('scroll', this.debouncedOnScroll);
        this.show();
        FloatingWidget.widgets = FloatingWidget.widgets.filter(widget => widget !== this);
    }
}
FloatingWidget.widgets = [];
FloatingWidget.enabled = true;
class Metafields {
    constructor() {
        document.addEventListener('customer:metafield:save', async () => {
            await this.processQueue();
        });
        this.processQueue().then();
    }
    async save(path, id, value, callback) {
        const url = `${Metafields.API_URL}${path}`;
        try {
            await fetch(url, {
                method: 'POST',
                body: JSON.stringify({
                    id: id,
                    value: value
                }),
                headers: {
                    'Content-Type': 'application/json',
                    'X-Vm-Token': window.theme.customerToken || '',
                    'X-Vm-Id': window.theme.customerId || ''
                }
            });
            if (callback)
                callback(true);
        }
        catch (e) {
            console.warn(e);
            if (callback)
                callback(false);
        }
    }
    async processQueue() {
        let cmqItem = window.cmq?.shift();
        while (cmqItem !== undefined) {
            if (!cmqItem.path || !cmqItem.id || !cmqItem.value) {
                continue;
            }
            await this.save(cmqItem.path, cmqItem.id, cmqItem.value, cmqItem.callback);
            cmqItem = window.cmq?.shift();
        }
    }
}
Metafields.API_URL = 'https://api.vitamoment.de/';
class LoadingBar {
    constructor() {
        this.element = document.querySelector('.LoadingBar');
        this.onLoadingStartHandler = this.onLoadingStart.bind(this);
        this.onLoadingEndHandler = this.onLoadingEnd.bind(this);
        this.onTransitionEndHandler = this.onTransitionEnd.bind(this);
        document.addEventListener('theme:loading:start', this.onLoadingStartHandler);
        document.addEventListener('theme:loading:end', this.onLoadingEndHandler);
        this.element?.addEventListener('transitionend', this.onTransitionEndHandler);
    }
    onLoadingStart() {
        if (!this.element)
            return;
        this.element.classList.add('is-visible');
        this.element.style.width = '40%';
    }
    onLoadingEnd() {
        if (!this.element)
            return;
        this.element.style.width = '100%';
        this.element.classList.add('is-finished');
    }
    onTransitionEnd(event) {
        if (!this.element)
            return;
        if (event.propertyName === 'width' && this.element.classList.contains('is-finished')) {
            this.element.classList.remove('is-visible', 'is-finished');
            this.element.style.width = '0';
        }
    }
    destroy() {
        document.removeEventListener('theme:loading:start', this.onLoadingStartHandler);
        document.removeEventListener('theme:loading:end', this.onLoadingEndHandler);
        this.element?.removeEventListener('transitionend', this.onTransitionEndHandler);
        this.element = null;
    }
}
export class Modal {
    static init(domDelegateInstance) {
        domDelegateInstance.on('click', '[data-action="open-modal"]', (e) => {
            const activator = e.target?.closest('[data-action="open-modal"]');
            if (!activator)
                return;
            const modalId = activator.getAttribute('aria-controls');
            let modal = Modal.modals[modalId];
            if (!modal) {
                modal = new Modal(document.getElementById(modalId));
            }
            modal.open();
        });
    }
    constructor(modal) {
        this.modal = modal;
        modal.querySelectorAll('[data-action="close-modal"]').forEach(element => {
            element.onclick = this.close.bind(this);
        });
        this.id = modal.id;
        Modal.modals[this.id] = this;
    }
    open() {
        this.modal.setAttribute('aria-hidden', 'false');
        window.pageOverlay.open(this.id, this.close.bind(this));
        Accessibility.trapFocus(this.modal, this.id);
        this.modal.addEventListener('keydown', this.handleKeydown.bind(this));
        Dom.disableScroll(true, this.modal);
        if (this.modal.hasAttribute('data-ajax-sections') && !this.modal.hasAttribute('data-ajax-initialized')) {
            this.loadAjaxContent().then();
        }
    }
    close() {
        Dom.disableScroll(false, this.modal);
        this.modal.setAttribute('aria-hidden', 'true');
        window.pageOverlay.close(this.id);
        Accessibility.removeFocus(this.id);
        this.modal.removeEventListener('keydown', this.handleKeydown.bind(this));
    }
    destroy() {
        this.close();
        delete Modal.modals[this.id];
        this.modal.remove();
    }
    handleKeydown(event) {
        if (event.key !== 'Escape')
            return;
        this.close();
    }
    async loadAjaxContent() {
        this.modal.classList.add('loading');
        const ajaxContext = this.modal.getAttribute('data-ajax-context') || window.location.pathname, ajaxSections = this.modal.getAttribute('data-ajax-sections'), ajaxUrl = `${ajaxContext}?sections=${ajaxSections}`;
        try {
            const res = await fetch(ajaxUrl, {
                credentials: 'same-origin',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest' // This is needed as currently there is a bug in Shopify that assumes this header
                }
            });
            const sectionsJson = await res.json();
            const modalContentElement = this.modal.querySelector('.Modal__Content');
            modalContentElement.innerHTML = Array.from(Object.values(sectionsJson)).join('');
            Dom.evalScripts(modalContentElement);
            this.modal.setAttribute('data-ajax-initialized', 'true');
            document.dispatchEvent(new CustomEvent('theme:sidebarMenu:htmlLoaded'));
        }
        catch (e) {
            console.error('Error on ajax section loading', e);
        }
        finally {
            this.modal.classList.remove('loading');
        }
    }
}
Modal.modals = {};
class Popover {
    constructor(element, activator, options) {
        this.cartOverlayElements = document.querySelectorAll('.PageOverlay--cart');
        this.popover = element;
        // @ts-expect-error
        this.delegateElement = new domDelegate.Delegate(element);
        this.activator = activator;
        this.id = element.id;
        this.options = {
            ...{
                threshold: 20,
                isDrawer: false,
                onValueChanged: () => {
                },
            }, ...options
        };
        this.delegateElement.on('click', '[data-action="select-value"]', (event) => this.handleValueClicked(event));
        this.closeListener = this.close.bind(this);
        this.keydownListener = this.handleKeydown.bind(this);
        this.delegateElement.on('click', '[data-action="close-popover"]', this.closeListener);
        element.querySelector('.Popover__Close')?.addEventListener('click', this.closeListener);
        Popover.popovers[this.id] = this;
    }
    open(position = 'left', invisibleOverlay = false) {
        this.popover.setAttribute('aria-hidden', 'false');
        const isNotADrawer = Responsive.matchesBreakpoint('lap-and-up') && !this.options.isDrawer;
        window.pageOverlay.open(this.id, this.close.bind(this), isNotADrawer || invisibleOverlay);
        if (Cart.isOpen) {
            this.cartOverlayElements.forEach(e => {
                e.classList.add('is-visible');
                e.onclick = this.closeListener;
            });
        }
        this.popover.addEventListener('keydown', this.keydownListener);
        Dom.disableScroll(true, this.popover);
        if (isNotADrawer)
            this.updatePopoverPositioning(position);
        this.popover.addEventListener('transitionend', () => {
            Accessibility.trapFocus(this.popover, this.id);
        }, {
            once: true
        });
    }
    close() {
        this.popover.setAttribute('aria-hidden', 'true');
        window.pageOverlay.close(this.id);
        this.cartOverlayElements.forEach((pageOverlayElement) => {
            pageOverlayElement.classList.remove('is-visible');
            pageOverlayElement.removeEventListener('click', this.closeListener);
        });
        this.popover.removeEventListener('keydown', this.keydownListener);
        Dom.disableScroll(false, this.popover);
        this.popover.addEventListener('transitionend', () => {
            Accessibility.removeFocus(this.id);
            this.activator.focus();
        }, {
            once: true
        });
        delete Popover.popovers[this.id];
        this.delegateElement.off();
    }
    setLoading() {
        this.popover.classList.add('loading');
        document.addEventListener('theme:loading:end', () => {
            this.popover.classList.remove('loading');
        }, { once: true });
    }
    static closeAll() {
        Object.values(Popover.popovers).forEach(popover => popover.close());
    }
    handleValueClicked(event) {
        const target = event.target;
        const dataValue = target.getAttribute('data-value');
        if (!dataValue)
            return;
        this.setLoading();
        this.options.onValueChanged(dataValue, target, this.activator, this);
    }
    updatePopoverPositioning(position) {
        const activatorBoundingRect = this.activator.getBoundingClientRect();
        const popoverHeight = this.popover.clientHeight;
        const popoverWidth = this.popover.clientWidth;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const threshold = this.options.threshold;
        const activatorCenter = activatorBoundingRect.top + activatorBoundingRect.height / 2;
        let alignment;
        if (activatorCenter < windowHeight / 3) {
            alignment = 'bottom';
        }
        else if (activatorCenter > (2 * windowHeight) / 3) {
            alignment = 'top';
        }
        else {
            alignment = 'center';
        }
        if (position === 'top') {
            alignment = 'right';
        }
        let topPosition;
        let rightPosition;
        if (position === 'top') {
            topPosition = activatorBoundingRect.top - popoverHeight - threshold;
            if (alignment === 'center') {
                rightPosition = windowWidth - activatorBoundingRect.right - popoverWidth / 2 + (activatorBoundingRect.width / 2);
            }
            else {
                rightPosition = windowWidth - activatorBoundingRect.right;
            }
        }
        else if (position === 'bottom') {
            topPosition = activatorBoundingRect.bottom + threshold;
            if (alignment === 'center') {
                rightPosition = windowWidth - activatorBoundingRect.right - popoverWidth / 2 + (activatorBoundingRect.width / 2);
            }
            else {
                rightPosition = windowWidth - activatorBoundingRect.right;
            }
        }
        else { // position === 'left'
            rightPosition = windowWidth - activatorBoundingRect.left + threshold;
            if (alignment === 'center') {
                topPosition = activatorBoundingRect.top - popoverHeight / 2 + activatorBoundingRect.height / 2;
            }
            else if (alignment === 'top') {
                topPosition = activatorBoundingRect.bottom - popoverHeight;
            }
            else { // alignment === 'bottom'
                topPosition = activatorBoundingRect.top;
            }
        }
        const className = `Popover--position${position.charAt(0).toUpperCase() + position.slice(1)} Popover--align${alignment.charAt(0).toUpperCase() + alignment.slice(1)}`;
        this.popover.style.top = `${topPosition}px`;
        this.popover.style.right = `${rightPosition}px`;
        this.popover.classList.remove('Popover--positionBottom', 'Popover--positionTop', 'Popover--positionCenter', 'Popover--positionLeft', 'Popover--alignTop', 'Popover--alignCenter', 'Popover--alignBottom');
        this.popover.classList.add(...className.split(" "));
    }
    handleKeydown(event) {
        if (event.key !== 'Escape')
            return;
        this.close();
    }
}
Popover.popovers = {};
class FeaturedProductsSection {
    static init() {
        document.addEventListener(ThemeEditor.SHOPIFY_BLOCK_SELECT, (event) => {
            const button = event.target;
            const parent = button.closest('[data-section-type="featured-products"]');
            if (!parent)
                return;
            parent.querySelector("[aria-controls=\"".concat(button.id, "\"]"))?.click();
        });
    }
}
class InstagramPostsSection {
    constructor(element) {
        this.sliderElement = null;
        this.slider = null;
        this.resizeObserver = null;
        this.intersectionObserver = null;
        this.element = element;
        this.sliderElement = this.element.querySelector('.Slider');
        if (!this.sliderElement)
            return;
        this.slider = new Slider(this.sliderElement);
        this.resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(() => {
                this.slider.onResize(true);
            });
        });
        this.resizeObserver.observe(this.sliderElement.querySelector('.Slider__Slide'));
        this.intersectionObserver = new IntersectionObserver((entries) => {
            if (!entries[0].isIntersecting)
                return;
            this.loadEmbedScript();
            this.intersectionObserver.disconnect();
            this.intersectionObserver = null;
        }, {
            rootMargin: '200px 0px'
        });
        this.intersectionObserver.observe(this.element);
    }
    loadEmbedScript() {
        if (InstagramPostsSection.scriptLoaded)
            return;
        const script = document.createElement('script');
        script.src = 'https://www.instagram.com/embed.js';
        script.async = true;
        document.head.appendChild(script);
        InstagramPostsSection.scriptLoaded = true;
    }
    onUnload() {
        if (this.slider)
            this.slider.disable();
        if (this.resizeObserver)
            this.resizeObserver.disconnect();
        if (this.intersectionObserver)
            this.intersectionObserver.disconnect();
    }
}
InstagramPostsSection.scriptLoaded = false;
class ImageViewer {
    static init(domDelegateInstance) {
        const imageViewer = new ImageViewer("#zoom-dialog");
        domDelegateInstance.on('click', '[data-image-zoom="true"]', (e) => {
            const target = e.target.tagName === 'IMG' ? e.target : e.target.querySelector('img');
            if (!target)
                return;
            const originalImageUrl = target.getAttribute("data-original-src");
            if (!originalImageUrl) {
                console.warn('ImageViewer: Zoom not possible, data-original-src not found on image.');
                return;
            }
            imageViewer.show(originalImageUrl);
        });
    }
    constructor(containerSelector, options = {}) {
        const defaults = {
            maxZoomLevel: 500,
            maxZoomLevelButton: 500,
            minZoomLevel: 100,
            zoomStep: 100,
            wheelZoomStep: 1,
        };
        this.options = { ...defaults, ...options };
        this.dialog = document.querySelector(containerSelector);
        if (!this.dialog) {
            console.error(`ImageViewer Error: Container "${containerSelector}" not found.`);
            this.zoomImage = null;
            this.imageWrapper = null;
            this.minusButton = null;
            this.plusButton = null;
            this.closeButton = null;
            this.zoomPercentageDisplay = null;
            this.reset();
            return;
        }
        this.zoomImage = this.dialog.querySelector("#zoom-image");
        this.imageWrapper = this.dialog.querySelector("#image-wrapper");
        this.minusButton = this.dialog.querySelector("#minus-button");
        this.plusButton = this.dialog.querySelector("#plus-button");
        this.closeButton = this.dialog.querySelector("#zoom-close-button");
        this.zoomPercentageDisplay = this.dialog.querySelector("#zoom-percentage");
        this.bindEvents();
    }
    reset() {
        this.currentZoomLevel = this.options.minZoomLevel;
        this.isDragging = false;
        this.startPosX = 0;
        this.startPosY = 0;
        this.currentTranslateX = 0;
        this.currentTranslateY = 0;
        this.initialTranslateX = 0;
        this.initialTranslateY = 0;
    }
    bindEvents() {
        if (!this.closeButton || !this.plusButton || !this.minusButton || !this.imageWrapper) {
            return;
        }
        this.closeButton.onclick = () => this.hide();
        this.plusButton.onclick = () => this.adjustZoom(1);
        this.minusButton.onclick = () => this.adjustZoom(-1);
        this.imageWrapper.addEventListener("mousedown", (e) => this.dragStart(e));
        this.imageWrapper.addEventListener("touchstart", (e) => this.dragStart(e), { passive: true });
        this.imageWrapper.addEventListener("mousemove", (e) => this.dragMove(e));
        this.imageWrapper.addEventListener("touchmove", (e) => this.dragMove(e), { passive: true });
        this.imageWrapper.addEventListener("mouseup", () => this.dragEnd());
        this.imageWrapper.addEventListener("mouseleave", () => this.dragEnd());
        this.imageWrapper.addEventListener("touchend", () => this.dragEnd());
        this.imageWrapper.addEventListener("wheel", (e) => this.handleWheelZoom(e));
    }
    dragStart(event) {
        if (this.currentZoomLevel <= this.options.minZoomLevel || !this.zoomImage)
            return;
        event.preventDefault();
        this.isDragging = true;
        this.zoomImage.classList.add("is-dragging");
        this.zoomImage.style.cursor = "grabbing";
        const { x, y } = this.getEventPosition(event);
        this.startPosX = x;
        this.startPosY = y;
        this.initialTranslateX = this.currentTranslateX;
        this.initialTranslateY = this.currentTranslateY;
    }
    dragMove(event) {
        if (!this.isDragging)
            return;
        event.preventDefault();
        const { x, y } = this.getEventPosition(event);
        const deltaX = x - this.startPosX;
        const deltaY = y - this.startPosY;
        const scale = this.currentZoomLevel / 100;
        this.currentTranslateX = this.initialTranslateX + deltaX / scale;
        this.currentTranslateY = this.initialTranslateY + deltaY / scale;
        this.updateTransform();
    }
    dragEnd() {
        if (!this.isDragging || !this.zoomImage || !this.imageWrapper)
            return;
        this.isDragging = false;
        this.zoomImage.classList.remove("is-dragging");
        this.zoomImage.style.cursor = "grab";
        const scale = this.currentZoomLevel / 100;
        const wrapperRect = this.imageWrapper.getBoundingClientRect();
        const imageRect = this.zoomImage.getBoundingClientRect();
        const imgNaturalWidth = this.zoomImage.naturalWidth;
        const imgNaturalHeight = this.zoomImage.naturalHeight;
        const wrapperWidth = this.imageWrapper.clientWidth;
        const wrapperHeight = this.imageWrapper.clientHeight;
        const imgAspectRatio = imgNaturalWidth / imgNaturalHeight;
        const wrapperAspectRatio = wrapperWidth / wrapperHeight;
        let visualWidth, visualHeight;
        if (imgAspectRatio > wrapperAspectRatio) {
            visualWidth = wrapperWidth;
            visualHeight = visualWidth / imgAspectRatio;
        }
        else {
            visualHeight = wrapperHeight;
            visualWidth = visualHeight * imgAspectRatio;
        }
        const scaledVisualWidth = visualWidth * scale;
        const scaledVisualHeight = visualHeight * scale;
        let deltaX = 0;
        let deltaY = 0;
        if (scaledVisualWidth > wrapperRect.width) {
            const offsetX = (imageRect.width - scaledVisualWidth) / 2;
            const contentLeft = imageRect.left + offsetX;
            const contentRight = imageRect.right - offsetX;
            if (contentLeft > wrapperRect.left)
                deltaX = contentLeft - wrapperRect.left;
            else if (contentRight < wrapperRect.right)
                deltaX = contentRight - wrapperRect.right;
        }
        else {
            deltaX = imageRect.left + imageRect.width / 2 - (wrapperRect.left + wrapperRect.width / 2);
        }
        if (scaledVisualHeight > wrapperRect.height) {
            const offsetY = (imageRect.height - scaledVisualHeight) / 2;
            const contentTop = imageRect.top + offsetY;
            const contentBottom = imageRect.bottom - offsetY;
            if (contentTop > wrapperRect.top)
                deltaY = contentTop - wrapperRect.top;
            else if (contentBottom < wrapperRect.bottom)
                deltaY = contentBottom - wrapperRect.bottom;
        }
        else {
            deltaY = imageRect.top + imageRect.height / 2 - (wrapperRect.top + wrapperRect.height / 2);
        }
        if (deltaX !== 0)
            this.currentTranslateX -= deltaX / scale;
        if (deltaY !== 0)
            this.currentTranslateY -= deltaY / scale;
        this.updateTransform();
    }
    getEventPosition(event) {
        if (event instanceof TouchEvent) {
            return { x: event.touches[0].clientX, y: event.touches[0].clientY };
        }
        return { x: event.clientX, y: event.clientY };
    }
    handleWheelZoom(event) {
        event.preventDefault();
        const zoomDelta = event.deltaY * -this.options.wheelZoomStep;
        const newZoomLevel = this.currentZoomLevel + zoomDelta;
        this.setZoomLevel(newZoomLevel);
    }
    setZoomLevel(newLevel) {
        const oldZoomLevel = this.currentZoomLevel;
        this.currentZoomLevel = Math.max(this.options.minZoomLevel, Math.min(this.options.maxZoomLevel, newLevel));
        if (oldZoomLevel !== this.currentZoomLevel) {
            const scaleRatio = this.currentZoomLevel / oldZoomLevel;
            this.currentTranslateX *= scaleRatio;
            this.currentTranslateY *= scaleRatio;
        }
        if (this.zoomPercentageDisplay) {
            this.zoomPercentageDisplay.innerText = `${Math.round(this.currentZoomLevel)}%`;
        }
        const isMin = this.currentZoomLevel <= this.options.minZoomLevel;
        if (isMin) {
            this.currentTranslateX = 0;
            this.currentTranslateY = 0;
        }
        this.updateTransform();
        if (this.minusButton && this.plusButton) {
            this.minusButton.disabled = isMin;
            this.plusButton.disabled = this.currentZoomLevel >= this.options.maxZoomLevelButton;
            const plusAriaBase = this.plusButton.getAttribute("data-aria-label") || 'Zoom in';
            const minusAriaBase = this.minusButton.getAttribute("data-aria-label") || 'Zoom out';
            this.plusButton.ariaLabel = `${plusAriaBase} ${Math.round(this.currentZoomLevel)}%`;
            this.minusButton.ariaLabel = `${minusAriaBase} ${Math.round(this.currentZoomLevel)}%`;
        }
    }
    adjustZoom(direction) {
        let newZoom = this.currentZoomLevel + direction * this.options.zoomStep;
        newZoom = Math.min(newZoom, this.options.maxZoomLevelButton);
        newZoom = Math.max(newZoom, this.options.minZoomLevel);
        this.setZoomLevel(newZoom);
    }
    updateTransform() {
        if (!this.zoomImage)
            return;
        const scale = this.currentZoomLevel / 100;
        this.zoomImage.style.transform = `scale(${scale}) translate(${this.currentTranslateX}px, ${this.currentTranslateY}px)`;
    }
    show(imageUrl) {
        if (!this.zoomImage || !this.dialog)
            return;
        this.zoomImage.src = imageUrl;
        this.zoomImage.classList.add("img-loading");
        this.zoomImage.onload = () => {
            this.zoomImage?.classList.remove("img-loading");
        };
        this.setZoomLevel(this.options.minZoomLevel);
        this.dialog.showModal();
    }
    hide() {
        if (!this.zoomImage || !this.dialog)
            return;
        this.zoomImage.src = "";
        this.dialog.close();
    }
}
export class PageOverlay {
    constructor() {
        this.isOpen = false;
        this.initiators = new Map();
        this.overlay = document.querySelector('.PageOverlay--primary');
        this.closeButton = this.overlay.querySelector('.PageOverlay__Close');
    }
    open(id, onClick, overlayIsNotVisible = false) {
        if (this.isOpen)
            return;
        this.isOpen = true;
        this.overlay.classList.add('is-visible');
        if (overlayIsNotVisible) {
            this.overlay.style.opacity = "0";
        }
        if (onClick) {
            this.overlay.addEventListener('click', onClick);
            this.initiators.set(id, onClick);
        }
    }
    close(id) {
        if (id) {
            const onClick = this.initiators.get(id);
            if (!onClick)
                return;
            this.overlay.removeEventListener('click', onClick);
            this.overlay.style.opacity = "";
            this.initiators.delete(id);
        }
        else {
            this.initiators.forEach((onClick) => {
                this.overlay.removeEventListener('click', onClick);
                this.overlay.style.opacity = "";
            });
            this.initiators.clear();
        }
        if (!this.initiators.size) {
            this.isOpen = false;
            this.overlay.classList.remove('is-visible');
            this.closeButton.classList.add('none');
        }
    }
}
export class Currency {
    static formatMoney(cents, format = '{{amount}} €') {
        if (typeof cents === 'string') {
            cents = +cents.replace('.', '');
        }
        const formatWithDelimiters = (number, precision, thousands = ".", decimal = ',') => {
            if (isNaN(number) || number == null)
                return "0";
            const formattedNumberString = (number / 100.0).toFixed(precision);
            const parts = formattedNumberString.split('.');
            const dollarsAmount = parts[0];
            const centsAmount = parts[1];
            const dollarsWithThousands = dollarsAmount.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
            const centsString = centsAmount ? decimal + centsAmount : '';
            return dollarsWithThousands + centsString;
        };
        const regex = /\{\{\s*(\w+)\s*}}/;
        const formatString = format?.match(regex)[1];
        let result;
        let useCommaAsSeparator = false;
        switch (formatString) {
            case 'amount_no_decimals':
                result = formatWithDelimiters(cents, 0);
                break;
            case 'amount_with_space_separator':
                result = formatWithDelimiters(cents, 2, ' ', '.');
                break;
            case 'amount_no_decimals_with_comma_separator':
                useCommaAsSeparator = true;
                result = formatWithDelimiters(cents, 0, ',', '.');
                break;
            case 'amount_no_decimals_with_space_separator':
                result = formatWithDelimiters(cents, 0, ' ');
                break;
            case 'amount_with_comma_separator':
                useCommaAsSeparator = true;
                result = formatWithDelimiters(cents, 2, '.', ',');
                break;
            default: {
                result = formatWithDelimiters(cents, 2);
            }
        }
        return format.replace(regex, result).replace(useCommaAsSeparator ? ',00' : '.00', '');
    }
}
class ProductVariants {
    constructor(element, productData, settings) {
        this.element = element;
        this.domDelegate = new window.domDelegate.Delegate(this.element);
        this.globalDomDelegate = new window.domDelegate.Delegate(document);
        this.productData = productData;
        this.settings = settings;
        this.currentVariant = this.productData.product.variants.find((variant) => variant.id === this.productData['selected_variant_id']);
        this.defaultVariant = this.currentVariant;
        this.currentOptions = [null, null, null];
        for (let i = 0; i < this.currentVariant.options.length; i++) {
            this.currentOptions[i] = this.currentVariant.options[i];
        }
        this.updateVariantAvailability();
        productData['temporary_discounts']?.forEach(discount => {
            const variant = this.productData.product.variants.find((variant) => variant.id === +discount.id);
            if (!variant)
                return;
            variant.price = discount.price;
            variant.compare_at_price = discount.compare_at_price;
            variant.unit_price = discount.unit_price;
        });
        this.domDelegate.on('change', '.GeneralVariant__Radio', (e) => {
            this.onProductVariantChanged(e.target.value, e.target, e.target);
        });
        const onVariantSelectorClick = (e) => {
            const button = e.target?.closest('button');
            if (!button)
                return;
            const syncAdd = button.getAttribute('data-action') === 'sync_variant';
            const popoverId = syncAdd ? document.querySelector('.ProductForm__Item--Geschmack').getAttribute('aria-controls') : button.getAttribute('aria-controls');
            let popover = Popover.popovers[popoverId];
            if (!popover) {
                popover = new Popover(document.getElementById(popoverId), button, { onValueChanged: this.onProductVariantChanged.bind(this) });
            }
            else {
                popover.activator = button;
            }
            popover.open(syncAdd ? 'top' : undefined);
        };
        this.domDelegate.on('click', '.ProductForm__Item--tasteAmount', onVariantSelectorClick);
        this.globalDomDelegate.on('click', '[data-action="sync_variant"]', onVariantSelectorClick);
        const stickyBar = document.querySelector('.StickyAddToCart');
        if (stickyBar) {
            let stickyBarHidden = true;
            const observerVisibleElements = new Set();
            const stickyBarObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        observerVisibleElements.add(entry.target);
                    }
                    else {
                        observerVisibleElements.delete(entry.target);
                    }
                });
                const newHidden = observerVisibleElements.size !== 0;
                if (stickyBarHidden != newHidden) {
                    stickyBarHidden = newHidden;
                    stickyBar.setAttribute('aria-hidden', newHidden.toString());
                    document.documentElement.style.setProperty('--sticky-cta-height', newHidden ? '0' : `${stickyBar.offsetHeight}px`);
                }
            }, {
                threshold: 0.25
            });
            const observables = [
                document.querySelector('.ProductMeta'),
                document.querySelector('.ProductForm__AddToCart '),
                document.querySelector('[id$="__product-recommendations"]'),
                document.querySelector('[id$="__recently-viewed-products"]'),
                document.querySelector('#shopify-section-shop-footer')
            ];
            observables.forEach((observable) => {
                if (observable)
                    stickyBarObserver.observe(observable);
            });
        }
    }
    onProductVariantChanged(newValue, target, activator) {
        const productName = activator.querySelector('.ProductForm__SelectedValue');
        const productImage = activator.querySelector('.ProductForm__Image');
        if (productName)
            productName.innerHTML = newValue;
        if (productImage) {
            if (productImage.getAttribute('data-option-name')?.includes('geschmack')) {
                productImage.setAttribute('data-option-image', newValue);
                const stickyActivator = document.querySelector('.StickyAddToCart [data-action="sync_variant"]');
                if (stickyActivator) {
                    const stickyActivatorImage = stickyActivator.querySelector('.ProductForm__Image');
                    stickyActivator.querySelector('.ProductForm__SelectedValue').innerHTML = newValue;
                    stickyActivatorImage.setAttribute('data-option-image', newValue);
                }
            }
            else if (productImage.getAttribute('data-option-name')?.includes('menge')) {
                productImage.setAttribute('data-option-image', newValue);
            }
            else if (productImage.getAttribute('data-option-name')?.includes('groesse')) {
                productImage.setAttribute('data-option-image', newValue);
            }
        }
        const parent = target.closest('.button-wrapper') || target;
        Dom.getSiblings(parent).forEach(e => {
            const btn = e.matches('button') ? e : e.querySelector('button');
            btn?.classList.remove('is-selected');
        });
        target.classList.add('is-selected');
        const optionIndex = Number(target.getAttribute('data-option-position'));
        this.currentOptions[optionIndex - 1] = newValue;
        const select = document.querySelector('[id^="product-select-"]');
        const currentVariant = this.productData['product'].variants.find(variant => JSON.stringify(this.currentOptions.filter(option => option !== null)) === JSON.stringify(variant.options));
        const oldVariant = this.currentVariant;
        this.currentVariant = currentVariant || null;
        if (this.currentVariant) {
            select.querySelector('[selected]')?.removeAttribute('selected');
            select.querySelector(`[value="${this.currentVariant.id}"]`)?.setAttribute('selected', 'selected');
            const search = new URLSearchParams(window.location.search);
            search.set('variant', String(this.currentVariant.id));
            const newUrl = `${location.origin}${location.pathname}?${search}`;
            history.replaceState({ path: newUrl }, '', newUrl);
            ProductImageSlider.reloadSlider(this.currentVariant.title);
        }
        this.updatePrice();
        if (this.settings.showInventoryQuantity) {
            this.updateInventory();
        }
        if (this.productData['template_suffix'] === 'club') {
            this.updateAddToCartClubButton();
        }
        else {
            this.updateAddToCartButton();
        }
        this.updateInfoBoxesVisibility();
        this.updateAdvantagePackages();
        this.updateVariantAvailability();
        this.updateNutritionInformation();
        this.updateOutOfStock();
        Popover.closeAll();
        document.dispatchEvent(new CustomEvent('theme:variant:change', { detail: { oldVariant, newVariant: this.currentVariant, newMetafields: this.productData.variant_metafields[this.currentVariant?.id || ''] || null } }));
    }
    updatePrice(variant = this.currentVariant) {
        const productMetaPriceWrapper = document.querySelector('.ProductMeta__PriceWrapper');
        if (this.productData['template_suffix'] === 'club') {
            if (productMetaPriceWrapper)
                productMetaPriceWrapper.style.display = '';
            return;
        }
        this.updatePortionPrice(variant);
        this.updateUnitPrice(variant);
        this.updateDiscount(variant);
        if (productMetaPriceWrapper)
            productMetaPriceWrapper.style.display = '';
    }
    updateInventory() {
        const productFormInventory = document.querySelector('.ProductForm__Inventory');
        if (!productFormInventory)
            return;
        const variantInventory = this.currentVariant ? this.productData['inventories'][this.currentVariant.id] : null;
        const noInventory = !variantInventory?.inventory_management || variantInventory.inventory_quantity <= 0 || (this.settings.inventoryQuantityThreshold > 0 && variantInventory.inventory_quantity > this.settings.inventoryQuantityThreshold);
        if (noInventory) {
            productFormInventory.style.display = 'none';
        }
        else {
            productFormInventory.textContent = variantInventory.inventory_message;
            productFormInventory.style.display = '';
        }
    }
    updateAddToCartButton() {
        const addToCartButton = document.querySelector('.ProductForm__AddToCart'), addToCartButtonSticky = document.querySelector('.StickyAddToCart__Content button[data-action="sync_add"]'), availabilityNotifyButton = document.querySelector('.out-of-stock-trigger');
        if (!addToCartButton)
            return;
        if (this.currentVariant?.available) {
            addToCartButtonSticky?.removeAttribute('disabled');
            addToCartButton.removeAttribute('disabled');
            addToCartButton.setAttribute('data-action', 'add-to-cart');
            addToCartButton.innerText = window.languages.productFormAddToCart;
            if (availabilityNotifyButton)
                availabilityNotifyButton.style.display = 'none';
        }
        else {
            addToCartButton.setAttribute('disabled', 'disabled');
            addToCartButton.removeAttribute('data-action');
            addToCartButton.innerText = this.currentVariant ? window.languages.productFormSoldOut : window.languages.productFormUnavailable;
            addToCartButtonSticky?.setAttribute('disabled', 'disabled');
            if (availabilityNotifyButton)
                availabilityNotifyButton.style.display = '';
        }
    }
    updateInfoBoxesVisibility() {
        const infoBoxesContainer = document.querySelector('.Product__InfoBoxes');
        const infoBoxes = Dom.nodeListToArray(infoBoxesContainer.querySelectorAll('[data-variant-info-box]'));
        const anyVariantBoxIsVisible = infoBoxes.reduce((wasVisible, currentBox) => {
            const isMatch = !!this.currentVariant && currentBox.dataset.variantInfoBox === String(this.currentVariant.id);
            currentBox.classList.toggle('none', !isMatch);
            return wasVisible || isMatch;
        }, false);
        const hasGenericProductBoxes = infoBoxesContainer.querySelector('[data-product-info-box]') !== null;
        if (anyVariantBoxIsVisible || hasGenericProductBoxes) {
            infoBoxesContainer.classList.remove('none');
        }
        else {
            infoBoxesContainer.classList.add('none');
        }
    }
    updateAdvantagePackages() {
        const advantageContainer = document.querySelector('.AdvantagePackages[data-advantage-packages-active="true"]');
        if (!advantageContainer)
            return;
        const advantagesEnabled = this.currentVariant ? this.productData.variant_metafields[this.currentVariant.id].advantage_packages_enabled : false;
        advantageContainer.classList.toggle('none', !advantagesEnabled);
        const checkedRadio = advantageContainer.querySelector('input[name="advantage-package-pseudo-variant"]:checked');
        if (!advantagesEnabled) {
            // Reset the selection so a stale :checked radio doesn't multiply add-to-cart quantity
            if (checkedRadio && checkedRadio.value !== '1') {
                const defaultRadio = advantageContainer.querySelector('input[name="advantage-package-pseudo-variant"][value="1"]');
                if (defaultRadio) {
                    defaultRadio.checked = true;
                    defaultRadio.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }
        }
        else if (checkedRadio && checkedRadio.value !== '1') {
            // Variant changed while a package is selected — re-fire change so the pseudo
            // variant (price, unit price, portions) is recomputed against the new variant.
            checkedRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }
    updateNutritionInformation() {
        if (!this.currentVariant)
            return;
        const select = document.getElementsByClassName('Nutrition__HeaderBlockSelect').item(0);
        if (!select)
            return;
        const option = select.querySelector(`[value="${this.currentVariant.id}"]`);
        option.selected = true;
        select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    updateOutOfStock() {
        const outOfStockButton = document.querySelector('.ProductForm .out-of-stock-trigger');
        if (!this.currentVariant || this.currentVariant?.available) {
            outOfStockButton?.classList.add('none');
            return;
        }
        outOfStockButton?.classList.remove('none');
        const dialog = document.getElementById('out-of-stock-dialog');
        const variantSelect = dialog.querySelector('select[name="variant"]');
        if (variantSelect) {
            const option = variantSelect.querySelector(`option[value="${this.currentVariant.id}"]`);
            if (option)
                option.selected = true;
        }
    }
    updateAddToCartClubButton() {
        const activeButton = document.querySelector('[data-club-button="active"][data-lion-seamless-product-reward]');
        const inactiveButton = document.querySelector('[data-club-button="inactive"]');
        if (this.currentVariant && this.currentVariant.available && activeButton && window.loyaltylion) {
            activeButton.setAttribute('data-variant-id', `${this.currentVariant.id}`);
            activeButton.style.display = '';
            if (inactiveButton)
                inactiveButton.style.display = 'none';
            window.loyaltylion.ui.refresh();
            return;
        }
        if (activeButton)
            activeButton.style.display = 'none';
        if (inactiveButton)
            inactiveButton.style.display = '';
    }
    updatePortionPrice(variant = this.currentVariant) {
        const portionPriceLabel = document.querySelector('.PortionPriceLabel--extended');
        if (!portionPriceLabel)
            return;
        if (!variant) {
            portionPriceLabel.classList.add('none');
            return;
        }
        // Additional price elements for a/b tests
        const abMainPortionPrice = document.querySelector('.AB__ProductPrice--main');
        const abSliderPortionPrices = document.querySelectorAll('.AB__ProductPrice--slider');
        const currencyFormat = window.theme.currencyCodeEnabled ? window.theme.moneyWithCurrencyFormat : window.theme.moneyFormat;
        const price = variant.price;
        const portionPriceData = this.productData.variant_metafields[variant.id] || null;
        if (!portionPriceData || !portionPriceData.portions_name || !portionPriceData.portions_total || !portionPriceData.portions_total) {
            portionPriceLabel.classList.add('none');
            abMainPortionPrice?.classList.add('none');
            return;
        }
        portionPriceLabel.classList.remove('none');
        abMainPortionPrice?.classList.remove('none');
        const setText = (selector, text) => {
            portionPriceLabel.querySelectorAll(selector).forEach((el) => {
                el.textContent = String(text);
            });
        };
        const formattedPortionPrice = Currency.formatMoney(price / portionPriceData.portions_total, currencyFormat);
        setText('[data-portions-price]', formattedPortionPrice);
        setText('[data-portions-name]', portionPriceData.portions_name);
        setText('[data-portions-name-plural]', portionPriceData.portions_name === window.languages.day
            ? window.languages.days
            : window.languages.portions);
        setText('[data-portions-total]', portionPriceData.portions_total);
        if (abMainPortionPrice) {
            abMainPortionPrice.querySelector('[data-portions-price]').textContent = formattedPortionPrice;
            abMainPortionPrice.querySelector('[data-portions-name]').textContent = portionPriceData.portions_name;
        }
        // Only update slider portion prices when product has only one variant (advantage package selection)
        if (this.productData.product.variants.length === 1) {
            abSliderPortionPrices.forEach(el => {
                el.querySelector('[data-portions-price]').textContent = formattedPortionPrice;
            });
        }
    }
    updateUnitPrice(variant = this.currentVariant) {
        const unitPriceMeasurement = document.querySelector('.ProductMeta__PriceList .UnitPriceMeasurement');
        if (!unitPriceMeasurement)
            return;
        if (!variant?.unit_price) {
            unitPriceMeasurement.classList.add('none');
            return;
        }
        unitPriceMeasurement.classList.remove('none');
        unitPriceMeasurement.querySelector('.UnitPriceMeasurement__Price').innerHTML = Currency.formatMoney(variant.unit_price, window.theme.moneyFormat);
        unitPriceMeasurement.querySelector('.UnitPriceMeasurement__ReferenceUnit').textContent = variant.unit_price_measurement.reference_unit;
    }
    updateDiscount(variant = this.currentVariant) {
        const productMetaPrices = document.querySelector('.ProductMeta__PriceList'), unitPriceElement = productMetaPrices.querySelector('.UnitPriceMeasurement'), currencyFormat = window.theme.currencyCodeEnabled ? window.theme.moneyWithCurrencyFormat : window.theme.moneyFormat;
        productMetaPrices.innerHTML = '';
        if (!variant)
            return;
        const price = variant.price, compareAtPrice = variant.compare_at_price || 0;
        if (compareAtPrice > price) {
            productMetaPrices.innerHTML += `
        <span class="PriceList__Item Price Price--highlight u-h2" data-money-convertible>
          ${Currency.formatMoney(price, currencyFormat)}
        </span>
        <span class="PriceList__Item Price Price--compareAt u-h2" data-money-convertible>
          ${Currency.formatMoney(compareAtPrice, currencyFormat)}
        </span>
      `;
            // Discount badge
            const discount = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
            productMetaPrices.innerHTML += `
        <span class="PriceList__Item SaleLabel">
          ${window.languages.youSaveHtml.replace('[amount]', `<span class="SaleLabel__Value">${discount}%</span>`)}
        </span>
      `;
        }
        else {
            // No discount
            productMetaPrices.innerHTML += `
        <span class="PriceList__Item Price u-h2" data-money-convertible>
          ${Currency.formatMoney(price, currencyFormat)}
        </span>
      `;
        }
        if (unitPriceElement)
            productMetaPrices.appendChild(unitPriceElement);
    }
    updateVariantAvailability() {
        const container = document.querySelector('.MainProduct');
        const optionSelectors = container.querySelectorAll('.OptionSelector:not(.PopoverGlobal)');
        const selectedElements = [];
        const availabilityMap = new Map(this.productData.product.variants.map(variant => {
            const key = this.productData.product.options.map((_, idx) => variant[`option${idx + 1}`])
                .join(" / ");
            return [key, variant.available];
        }));
        // Initally remove availability attribute from second option selector
        if (optionSelectors.length === 2) {
            const optionButton = container.querySelector(`[aria-controls="${optionSelectors[1].id}"]`);
            optionButton?.removeAttribute("data-availability");
        }
        // Go through all option selectors to calculate availability for every option instance. If an option instance is not available in every
        // combination with other options it gets is 'sold-out'. If the option instance is the currently selected one in the first option selector,
        // the second option selector - if existent - gets 'other-option'.
        optionSelectors.forEach((selector, i) => {
            const options = selector.querySelectorAll('[data-action="select-value"]');
            options.forEach(option => {
                const variantOptions = [...this.currentOptions].filter(option => option != null);
                variantOptions[i] = option.dataset.value ?? "";
                const variantKey = variantOptions.join(" / ");
                const variantAvailability = availabilityMap.get(variantKey);
                // Default: all available
                option.toggleAttribute("disabled", false);
                if (variantAvailability === true) {
                    option.setAttribute("data-availability", "available");
                    return;
                }
                // Special case: second option does not exist
                if (i === 1 && variantAvailability === undefined) {
                    option.setAttribute("data-availability", "non-existent");
                    option.setAttribute("disabled", "true");
                    return;
                }
                // Check if there are other valid variants for the first option
                let hasOtherVariant = false;
                if (i === 0 && this.productData.product.options.length === 2) {
                    const possibleKeys = [...availabilityMap.keys()].filter(key => {
                        const [first, ...rest] = variantOptions;
                        // only keep keys that contain the first value but not the rest
                        return key.includes(first) && rest.every(v => !key.includes(v));
                    });
                    hasOtherVariant = possibleKeys.some(key => availabilityMap.get(key));
                }
                if (hasOtherVariant && option === selectedElements[i]) {
                    const optionButton = container.querySelector(`[aria-controls="${optionSelectors[1].id}"]`);
                    optionButton?.setAttribute("data-availability", "other-option");
                }
                else if (hasOtherVariant) {
                    option.setAttribute("data-availability", "available");
                }
                else {
                    option.setAttribute("data-availability", "sold-out");
                }
            });
        });
    }
    destroy() {
        this.domDelegate.off('click');
        this.globalDomDelegate.off('click');
    }
}
class SearchBar {
    constructor(searchElement) {
        this.isOpen = false;
        this.currentActivator = null;
        // @ts-expect-error
        this.domDelegateInstance = new domDelegate.Delegate(document.body);
        this.searchElement = searchElement;
        this.id = searchElement.id;
        this.domDelegateInstance.on('click', '[data-action="open-search"]', this.open.bind(this));
        this.domDelegateInstance.on('click', '[data-action="close-search"]', this.close.bind(this));
        this.searchElement.addEventListener('keydown', this.handleKeyPress.bind(this));
        this.domDelegateInstance.on('search:close', this.close.bind(this)); // Allow for third-party elements to close the bar
    }
    open(event) {
        event.preventDefault();
        // Resource loading on first open
        if (!window.themeSearch) {
            const searchScript = document.createElement('script');
            document.head.appendChild(searchScript);
            searchScript.src = window.routes.searchScriptUrl;
            const searchStyles = document.createElement('link');
            searchStyles.rel = 'stylesheet';
            document.head.appendChild(searchStyles);
            searchStyles.href = window.routes.searchStylesUrl;
        }
        this.isOpen = true;
        this.searchElement.setAttribute('aria-hidden', 'false');
        this.currentActivator = event.target;
        this.currentActivator.setAttribute('aria-expanded', 'true');
        Dom.disableScroll(true, this.searchElement);
        window.pageOverlay.open(this.id, this.close.bind(this));
        Accessibility.trapFocus(this.searchElement, 'search');
    }
    close() {
        this.isOpen = false;
        Accessibility.removeFocus(this.id);
        this.searchElement.setAttribute('aria-hidden', 'true');
        if (this.currentActivator) {
            this.currentActivator.setAttribute('aria-expanded', 'false');
            this.currentActivator.focus();
            this.currentActivator = null;
        }
        Dom.disableScroll(false, this.searchElement);
        window.pageOverlay.close(this.id);
        document.dispatchEvent(new CustomEvent('search:afterclose'));
    }
    handleKeyPress(event) {
        if (event.key === 'Escape' && this.isOpen) {
            this.close();
            event.preventDefault();
        }
    }
    destroy() {
        this.close();
        this.domDelegateInstance.off();
    }
}
class HeaderSection {
    static init() {
        const headerEl = document.querySelector('header.ShopHeader');
        if (headerEl)
            HeaderSection.headerInstance = new HeaderSection(headerEl);
        document.addEventListener('shopify:section:load', (event) => {
            const sectionEl = event.target;
            if (!sectionEl.matches('.shopify-section--shop-header'))
                return;
            const headerEl = sectionEl.querySelector('header.ShopHeader');
            if (headerEl)
                HeaderSection.headerInstance = new HeaderSection(headerEl);
        });
        document.addEventListener('shopify:section:unload', (event) => {
            const sectionEl = event.target;
            if (!sectionEl.matches('.shopify-section--shop-header'))
                return;
            HeaderSection.headerInstance?.destroy?.();
            HeaderSection.headerInstance = null;
        });
    }
    constructor(element) {
        this.id = "menu";
        this.isNavOpen = false;
        // @ts-expect-error
        this.domDelegateInstance = new domDelegate.Delegate(element);
        const searchBarElement = document.getElementById('search');
        this.searchBar = searchBarElement ? new SearchBar(searchBarElement) : null;
        this.header = element;
        this.mainLogo = this.header.querySelector('.ShopHeader__LogoImage--primary');
        if (!this.mainLogo.complete) {
            this.mainLogo.addEventListener('load', () => {
                document.documentElement.style.setProperty('--header-height', this.header.clientHeight + 'px');
            });
        }
        else {
            document.documentElement.style.setProperty('--header-height', this.header.clientHeight + 'px');
        }
        this.domDelegateInstance.on('mouseenter', '.ShopHeader__MainMenu [data-level="1"][aria-controls]', this.openMenu.bind(this), true);
        this.domDelegateInstance.on('mouseenter', '.ShopHeader__MainColumn [data-level="2"]:not([aria-controls])', this.openThirdLevel.bind(this), true);
        this.domDelegateInstance.on('focusin', '.ShopHeader__MainColumn [data-level="2"]:not([aria-controls])', (e, target) => {
            target.style.zIndex = '2';
        }, true);
        this.domDelegateInstance.on('focusout', '.ShopHeader__MainColumn [data-level="2"]:not([aria-controls])', (e, target) => {
            target.style.zIndex = '1';
        }, true);
        this.domDelegateInstance.on('click', '.ShopHeader__MainColumn [data-level="2"][aria-controls]', this.openThirdLevel.bind(this), true);
        this.domDelegateInstance.on('click', '[data-action="open-search"], [data-action="open-drawer"][data-drawer-id="sidebar-cart"]', this.closeNavigation.bind(this));
        this.domDelegateInstance.on('keydown', '.ShopHeader__MainMenu', this.onKeydown.bind(this));
        this.header.addEventListener('mouseleave', this.closeNavigation.bind(this));
        this.header.classList.remove('ShopHeader--initializing');
    }
    destroy() {
        this.searchBar?.destroy();
    }
    openMenu(event, target) {
        if (event.type === 'mouseenter' && target !== event.target)
            return;
        const controlledElement = document.getElementById(target.getAttribute('aria-controls'));
        if (target.getAttribute('aria-expanded') === 'true')
            return;
        this.header.parentNode.style.zIndex = '10';
        this.isNavOpen = true;
        window.pageOverlay.open(this.id);
        const openElement = this.header.querySelector('.ShopHeader__MainMenu [data-level="1"][aria-expanded="true"]');
        if (openElement)
            this.closeMenu(null, openElement);
        if (controlledElement) {
            target.setAttribute('aria-expanded', 'true');
            controlledElement.setAttribute('aria-hidden', 'false');
        }
    }
    closeMenu(event, target) {
        if (target.getAttribute('aria-expanded') === 'false')
            return;
        const controlledElement = document.getElementById(target.getAttribute('aria-controls'));
        const openElement = controlledElement.querySelector('.ShopHeader__MainColumn [data-level="2"][aria-expanded="true"]');
        if (openElement)
            this.closeThirdLevel(null, openElement);
        if (event && event.type === 'keydown')
            target.focus();
        controlledElement.setAttribute('aria-hidden', 'true');
        target.setAttribute('aria-expanded', 'false');
    }
    openThirdLevel(event, target) {
        if (event.type === 'mouseenter' && target !== event.target)
            return;
        const activator = target.hasAttribute('aria-controls') ? target : target.querySelector('[data-level="2"][aria-controls]');
        const controlledElement = document.getElementById(activator.getAttribute('aria-controls'));
        if (activator.getAttribute('aria-expanded') === 'true')
            return;
        const openElement = target.closest('.ShopHeader__MainColumn').querySelector('[data-level="2"][aria-expanded="true"]');
        if (openElement)
            this.closeThirdLevel(null, openElement);
        activator.setAttribute('aria-expanded', 'true');
        controlledElement.setAttribute('aria-hidden', 'false');
        if (event.type === 'mouseenter') {
            target.style.zIndex = '2';
            const mouseMoveHandler = (e) => this.calcHoverArea(target, controlledElement, e);
            const mouseLeaveHandler = () => {
                target.style.zIndex = '1';
                target.removeEventListener('mousemove', mouseMoveHandler);
                target.removeEventListener('mouseleave', mouseLeaveHandler);
                this.removeHoverArea(target);
            };
            target.addEventListener('mousemove', mouseMoveHandler, { passive: true });
            target.addEventListener('mouseleave', mouseLeaveHandler);
        }
    }
    closeThirdLevel(event, target) {
        if (target.getAttribute('aria-expanded') === 'false')
            return;
        const controlledElement = document.getElementById(target.getAttribute('aria-controls'));
        Accessibility.removeFocus("third-level");
        if (event && event.type === 'keydown')
            target.focus();
        controlledElement.setAttribute('aria-hidden', 'true');
        target.setAttribute('aria-expanded', 'false');
        this.removeHoverArea(target);
    }
    closeNavigation(event, target) {
        if (event && target && event.type === 'focusout')
            return;
        if (!this.isNavOpen)
            return;
        this.isNavOpen = false;
        const openElement = this.header.querySelector('.ShopHeader__MainMenu [data-level="1"][aria-expanded="true"]');
        if (openElement)
            this.closeMenu(event, openElement);
        if (event && target && event.type === 'click' && target.getAttribute('data-action') === 'open-search') {
            this.header.style.zIndex = '';
        }
        this.header.parentNode.style.zIndex = "";
        window.pageOverlay.close();
    }
    onKeydown(event) {
        const activator = event.target.hasAttribute('aria-controls') ? event.target : null;
        const controlledElement = activator ? document.getElementById(activator.getAttribute('aria-controls')) : null;
        switch (event.key) {
            case 'Escape': {
                const openThirdLevel = this.header.querySelector('.ShopHeader__MainColumn [data-level="2"][aria-expanded="true"]');
                if (openThirdLevel) {
                    this.closeThirdLevel(event, openThirdLevel);
                    break;
                }
                const openSecondLevel = this.header.querySelector('.ShopHeader__MainMenu [data-level="1"][aria-expanded="true"]');
                if (openSecondLevel)
                    this.closeNavigation(event, openSecondLevel);
                break;
            }
            case 'Tab': {
                if (!activator || !controlledElement || activator.getAttribute('aria-expanded') === 'false' || event.shiftKey)
                    return;
                const focusTrapElement = (controlledElement.querySelector('.ShopHeader__MainColumn') || controlledElement);
                Accessibility.trapFocus(focusTrapElement, activator.getAttribute('aria-controls'));
                controlledElement.querySelector('button,a[href]').focus();
                event.preventDefault();
                break;
            }
            case 'Enter':
            case ' ': {
                if (!activator || !controlledElement)
                    break;
                if (activator.getAttribute('data-level') === '2') {
                    this.openThirdLevel(event, activator);
                    event.preventDefault();
                }
                else if (activator.getAttribute('data-level') === '1') {
                    this.openMenu(event, activator);
                    event.preventDefault();
                }
                break;
            }
            default: {
                event.preventDefault();
            }
        }
    }
    calcHoverArea(activator, box, moveEvent) {
        const activatorRect = activator.getBoundingClientRect();
        const boxRect = box.getBoundingClientRect();
        const mouseX = moveEvent.clientX - activatorRect.left;
        const mouseY = moveEvent.clientY - activatorRect.top;
        // Wenn Maus außerhalb des Menüpunktes → keine Hover-Area neu berechnen
        if (mouseY < 0 || mouseY > activatorRect.height || mouseX > activatorRect.width)
            return;
        const boxRelative = {
            top: boxRect.top - activatorRect.top,
            left: boxRect.left - activatorRect.left,
            bottom: boxRect.bottom - activatorRect.top
        };
        const points = [
            { x: Math.max(0, mouseX), y: -boxRelative.top },
            { x: boxRelative.left, y: 0 },
            { x: boxRelative.left, y: Math.max(activatorRect.height, boxRelative.bottom) - boxRelative.top },
            { x: Math.max(0, mouseX), y: -boxRelative.top + activatorRect.height }
        ];
        this.setHoverArea(activator, boxRelative.top, boxRelative.left, points);
    }
    setHoverArea(element, topOffset, width, points) {
        const height = points[2].y - points[1].y;
        const pointsString = points.map(p => `${p.x},${p.y}`).join(' ');
        const oldSvg = element.querySelector('svg.ShopHeader__HoverArea');
        if (oldSvg) {
            oldSvg.setAttribute('viewBox', `0 0 ${width} ${height}`);
            oldSvg.querySelector('polygon').setAttribute('points', pointsString);
        }
        else {
            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.classList.add('ShopHeader__HoverArea');
            svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
            svg.style.cssText = `top:${topOffset}px; width:${width}px; height:${height}px; position:absolute; left:0; pointer-events:none; z-index: 4;`;
            const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
            polygon.setAttribute('points', pointsString);
            polygon.setAttribute('fill', 'none');
            svg.appendChild(polygon);
            element.appendChild(svg);
        }
    }
    removeHoverArea(element) {
        element.querySelector('svg.ShopHeader__HoverArea')?.remove();
    }
}
HeaderSection.headerInstance = null;
class Dialog {
    static init() {
        document.querySelectorAll('[data-action="open-dialog"]').forEach((el) => {
            el.onclick = (e) => {
                e.preventDefault();
                const dialogId = el.dataset.dialogId;
                if (!dialogId)
                    return;
                Dialog.open(document.querySelector(`#${dialogId}`));
            };
        });
        document.querySelectorAll('[data-action="close-dialog"]').forEach((el) => {
            el.onclick = (e) => {
                Dialog.close(e.target.closest('dialog'));
            };
        });
    }
    static open(dialog) {
        if (!dialog)
            return;
        dialog.showModal();
        dialog.addEventListener('click', (event) => {
            const rect = dialog.getBoundingClientRect();
            const isInDialog = event.clientX >= rect.left &&
                event.clientX <= rect.right &&
                event.clientY >= rect.top &&
                event.clientY <= rect.bottom;
            if (!isInDialog) {
                this.close(dialog);
            }
        });
        this.dialogs[dialog.id] = dialog;
    }
    static close(dialog) {
        if (!dialog)
            return;
        delete this.dialogs[dialog.id];
        dialog.close();
    }
}
Dialog.dialogs = {};
class OutOfStockDialog {
    constructor() {
        this.dialogId = 'out-of-stock-dialog';
        this.klaviyoCompanyId = 'Jck3C4';
        const form = document.querySelector(`#${this.dialogId} form`);
        if (!form)
            return;
        form.addEventListener('submit', this.handleSubmit.bind(this));
    }
    async handleSubmit(event) {
        event.preventDefault();
        const form = event.target;
        const submitButton = form.querySelector('button[type="submit"]');
        const successMessage = form.querySelector('[data-status-action="success"]');
        const failedMessage = form.querySelector('[data-status-action="error"]');
        submitButton.classList.add('loading');
        if (successMessage)
            successMessage.classList.add('none');
        if (failedMessage)
            failedMessage.classList.add('none');
        const formData = new FormData(form);
        const { email, variant } = Object.fromEntries(formData.entries());
        const payload = {
            "data": {
                "type": "back-in-stock-subscription",
                "attributes": {
                    "profile": {
                        "data": {
                            "type": "profile",
                            "attributes": {
                                email
                            }
                        }
                    },
                    "channels": ["EMAIL"],
                },
                "relationships": {
                    "variant": {
                        "data": {
                            "type": "catalog-variant",
                            "id": `$shopify:::$default:::${variant}`
                        }
                    }
                }
            }
        };
        const requestOptions = {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
                "revision": "2024-06-15"
            },
            body: JSON.stringify(payload),
        };
        try {
            const res = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.klaviyoCompanyId}`, requestOptions);
            if (res.status === 202) {
                if (successMessage)
                    successMessage.classList.remove('none');
            }
            else {
                if (failedMessage)
                    failedMessage.classList.remove('none');
            }
        }
        catch (e) {
            console.error(e);
            if (failedMessage)
                failedMessage.classList.remove('none');
        }
        finally {
            submitButton.classList.remove('loading');
        }
    }
}
class NutritionIngredients {
    static init() {
        document.querySelectorAll('.Nutrition').forEach(nutritionElement => {
            new NutritionIngredients(nutritionElement);
        });
    }
    constructor(element) {
        this.element = element;
        const nutritionSelectors = this.element.querySelectorAll('select[data-action="set-nutrition"]');
        if (!nutritionSelectors.length)
            return;
        nutritionSelectors.forEach(nutritionSelector => {
            nutritionSelector.onchange = (event) => {
                const target = event.target;
                const nutritionContainer = target.closest('.Nutrition__Tab');
                const nutritionInformation = Dom.nodeListToArray(nutritionContainer.querySelectorAll('[data-nutrition-variant]'));
                nutritionInformation.forEach(nutritionInformation => {
                    const isMatch = nutritionInformation.dataset.nutritionVariant === target.value;
                    nutritionInformation.classList.toggle('none', !isMatch);
                    if (isMatch)
                        nutritionInformation.closest('.Nutrition__ContentContainer')?.classList.toggle('none', !nutritionInformation.innerHTML.trim());
                });
            };
        });
    }
}
class ProductSection {
    constructor(element) {
        this.element = element;
        // @ts-expect-error
        this.domDelegateInstance = new domDelegate.Delegate(document.body);
        this.settings = JSON.parse(this.element.getAttribute('data-section-settings'));
        const jsonData = JSON.parse(this.element.querySelector('[data-product-json]').innerHTML);
        ProductImageSlider.init(this.element, jsonData.product.options, jsonData.product.variants);
        if (!this.settings.templateSuffix || !['not-for-sale', 'bundle-exclusive'].includes(this.settings.templateSuffix)) {
            this.productVariants = new ProductVariants(element, jsonData, this.settings);
        }
        this.handleUpdateQuantity();
        this.initStickAddToCartButton();
        this.initProductContentNavigation();
        this.initOutOfStock();
        const cartButtonSelector = '.ProductForm__AddToCart';
        const stickyCartButtonSelector = '[data-action="sync_add"]';
        this.addToCartButton = this.element.querySelector(cartButtonSelector);
        this.addToCartStickyButton = document.querySelector(stickyCartButtonSelector);
        this.domDelegateInstance.on('click', cartButtonSelector, this.addToCart.bind(this));
        this.domDelegateInstance.on('click', stickyCartButtonSelector, this.addToCart.bind(this));
        if (this.productVariants) {
            this.domDelegateInstance.on('change', 'input[name="advantage-package-pseudo-variant"]', (e) => {
                const target = e.target;
                const advantageValue = target.value;
                const [paidQuantity, freeQuantity] = advantageValue.split('+').map(value => parseInt(value));
                const totalQuantity = paidQuantity + freeQuantity;
                // Compute the pseudo variant against the currently selected variant, not the
                // initial page-load default. Matters when the enabled variant is not the default
                // one, or when the user changes variant while a package is selected.
                const currentVariant = this.productVariants.currentVariant;
                if (!currentVariant)
                    return;
                let pseudoVariant;
                if (!freeQuantity) {
                    pseudoVariant = currentVariant;
                }
                else {
                    const pseudoVariantTitle = target.getAttribute('data-pseudo-title');
                    pseudoVariant = {
                        id: pseudoVariantTitle,
                        price: Math.round(currentVariant.price * paidQuantity / totalQuantity) * totalQuantity, // Need to sum up the rounded discounted price for single items because that's how shopify functions do it
                        compare_at_price: (currentVariant.compare_at_price || currentVariant.price) * totalQuantity,
                        title: pseudoVariantTitle,
                        unit_price: currentVariant.unit_price,
                        unit_price_measurement: {
                            quantity_value: currentVariant.unit_price_measurement.quantity_value,
                            reference_unit: currentVariant.unit_price_measurement.reference_unit,
                            reference_value: currentVariant.unit_price_measurement.reference_value
                        }
                    };
                    if (currentVariant.unit_price) {
                        const measurement = currentVariant.unit_price_measurement;
                        const referenceFactor = measurement.quantity_unit === 'g' && measurement.reference_unit === 'kg' || measurement.quantity_unit === 'ml' && measurement.reference_unit === 'l' ? 1000 : 1;
                        // Calculate discounted price for pseudo variant for unit price to use the same unit price as Shopify does in cart items
                        const discountedSinglePrice = Math.round(currentVariant.price * (paidQuantity / totalQuantity));
                        pseudoVariant.unit_price = discountedSinglePrice * (measurement.reference_value * referenceFactor / (measurement.quantity_value));
                        pseudoVariant.unit_price_measurement = {
                            quantity_value: measurement.quantity_value * totalQuantity,
                            reference_unit: measurement.reference_unit,
                            reference_value: measurement.reference_value
                        };
                    }
                    // Add dynamic portion data for pseudo variant
                    jsonData.variant_metafields[pseudoVariant.id] = {
                        portions_total: jsonData.variant_metafields[currentVariant.id].portions_total * totalQuantity,
                        portions_name: jsonData.variant_metafields[currentVariant.id].portions_name,
                        advantage_packages_enabled: true
                    };
                }
                this.productVariants.updatePrice(pseudoVariant);
                document.dispatchEvent(new CustomEvent('theme:advantage:change', { detail: { currentVariant: this.productVariants.currentVariant, advantageVariant: pseudoVariant, currentMetafields: jsonData.variant_metafields[pseudoVariant.id] } }));
            });
            this.checkHashVariant();
        }
        this.initGifts();
    }
    checkHashVariant() {
        let rawHash = window.location.hash;
        if (!rawHash) { // Workaround to get hash from search because Instagram URI-encodes #
            const search = decodeURIComponent(window.location.search);
            if (search.includes('#')) {
                const searchArr = search.split('#');
                rawHash = searchArr[searchArr.length - 1];
            }
        }
        else {
            rawHash = decodeURIComponent(rawHash);
        }
        if (window.theme.pageType === 'product' && rawHash) {
            let hash = rawHash.replace('#', '').replaceAll('_', ' ');
            hash = hash.includes('?') ? hash.split('?')[0] : hash;
            const variants = hash.split('&');
            const variantPopovers = document.querySelectorAll('.Product__OffScreen .OptionSelector');
            for (let i = 0, shift = 0; i - shift < variants.length && i < variantPopovers.length; i++) {
                const popoverElement = variantPopovers[i], popoverActivator = document.querySelector(`button.ProductForm__Item--tasteAmount[aria-controls="${popoverElement.id}"]`), variantItems = popoverElement.querySelectorAll('button.Popover__Value'), variantHandle = window.handleize(variants[i - shift]);
                let found = false;
                for (let k = 0; k < variantItems.length; k++) {
                    const itemValue = variantItems[k].getAttribute('data-value');
                    if (!itemValue)
                        continue;
                    if (window.handleize(itemValue) === variantHandle) {
                        let popoverInstance = Popover.popovers[popoverElement.id];
                        if (!popoverInstance) {
                            popoverInstance = new Popover(document.getElementById(popoverElement.id), popoverActivator, { onValueChanged: this.productVariants.onProductVariantChanged.bind(this.productVariants) });
                        }
                        else {
                            popoverInstance.activator = popoverActivator;
                        }
                        variantItems[k].click();
                        found = true;
                        break;
                    }
                }
                if (!found) {
                    shift++;
                }
            }
        }
    }
    unload() {
        this.domDelegateInstance.off();
        ProductImageSlider.productSlideshow.disable();
        const stickAddToCartButton = document.querySelector('.StickyAddToCart');
        if (stickAddToCartButton)
            stickAddToCartButton.setAttribute('aria-hidden', 'true');
        this.productVariants?.destroy();
    }
    initGifts() {
        this.domDelegateInstance.on('click', '.inner-gift-container', (e) => {
            const target = e.target;
            if (target instanceof HTMLInputElement && target.type === 'checkbox') {
                return;
            }
            e.preventDefault();
            const container = target.closest('.inner-gift-container');
            if (!container)
                return;
            const checkbox = container.querySelector('[name="gift"]');
            if (!checkbox || checkbox.disabled)
                return;
            checkbox.checked = !checkbox.checked;
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        });
        document.getElementsByName('gift').forEach((el) => {
            el.addEventListener('change', (e) => {
                const selectContainer = el.closest('.gift-container')?.querySelector('.Collapsible__Inner');
                if (selectContainer) {
                    const target = e.target;
                    selectContainer.style.setProperty('--collapsible-height', selectContainer.firstElementChild.clientHeight + 'px');
                    selectContainer.ariaHidden = `${!target.checked}`;
                }
            });
        });
        document.getElementsByName('free-product-variant-id').forEach((el) => {
            el.addEventListener('change', () => {
                const checkbox = el.closest('.gift-container')?.querySelector('input[type="checkbox"]');
                if (checkbox) {
                    const select = el;
                    checkbox.value = select.value;
                }
            });
        });
    }
    static updateGifts(productsInCart) {
        document.querySelectorAll('[name="gift"]').forEach((input) => {
            const inCart = productsInCart.includes(+input.value);
            const container = input.closest('.gift-container');
            const alreadyInCart = container?.querySelector('.already-in-cart');
            const freeProductTitle = container?.querySelector('.free-product-title');
            const priceTag = container?.querySelector('.price-tag');
            const collapsibleInner = container?.querySelector('.Collapsible__Inner');
            if (inCart) {
                input.disabled = true;
                input.parentNode.classList.add('Checkbox--disabled');
                alreadyInCart?.classList.remove('hidden');
                freeProductTitle?.classList.add('text-text-disabled');
                freeProductTitle?.classList.remove('text-text-primary');
                priceTag?.classList.add('hidden');
                if (collapsibleInner)
                    collapsibleInner.ariaHidden = "true";
            }
            else {
                input.disabled = false;
                input.checked = false;
                input.parentNode.classList.remove('Checkbox--disabled');
                alreadyInCart?.classList.add('hidden');
                freeProductTitle?.classList.remove('text-text-disabled');
                freeProductTitle?.classList.add('text-text-primary');
                priceTag?.classList.remove('hidden');
            }
        });
    }
    handleUpdateQuantity() {
        const quantityInput = this.element.querySelector('.QuantitySelector__Input');
        const decreaseButtons = this.element.querySelector('[data-action="decrease-quantity"]');
        const increaseButtons = this.element.querySelector('[data-action="increase-quantity"]');
        if (!quantityInput || !decreaseButtons || !increaseButtons)
            return;
        increaseButtons.onclick = () => {
            decreaseButtons.ariaDisabled = "false";
            quantityInput.value = `${parseInt(quantityInput.value) + 1}`;
        };
        decreaseButtons.onclick = () => {
            if (parseInt(quantityInput.value) <= 1) {
                decreaseButtons.ariaDisabled = "true";
                return;
            }
            quantityInput.value = `${parseInt(quantityInput.value) - 1}`;
        };
        quantityInput.onchange = (event) => {
            const value = parseInt(event.target.value);
            if (isNaN(value) || value < 1) {
                quantityInput.value = '1';
                decreaseButtons.ariaDisabled = "true";
            }
            else {
                quantityInput.value = `${value}`;
                decreaseButtons.ariaDisabled = value === 1 ? "true" : "false";
            }
        };
    }
    initStickAddToCartButton() {
        const addToCartButton = this.element.querySelector('.ProductForm__AddToCart');
        const stickAddToCartButton = document.querySelector('.StickyAddToCart');
        if (!addToCartButton || !stickAddToCartButton)
            return;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                stickAddToCartButton.setAttribute('aria-hidden', entry.isIntersecting.toString());
            });
        });
        observer.observe(addToCartButton);
    }
    initProductContentNavigation() {
        const navigationBar = document.querySelector('.NavigationBar');
        if (!navigationBar)
            return;
        const scrollSlider = navigationBar.querySelector('.NavigationList');
        // Initialize table of contents
        const scrollListener = (_, activeLinks) => {
            activeLinks.forEach((link) => {
                if (!link.closest('.NavigationList'))
                    return;
                const menuScrollLeft = link.offsetLeft + link.clientWidth / 2 - scrollSlider.clientWidth / 2;
                scrollSlider.scroll({
                    left: menuScrollLeft,
                    behavior: "smooth"
                });
            });
        };
        // Save the height of the navigation bar in a CSS variable to offset the anchor links
        const setNavigationHeightVariable = () => {
            const navigationHeight = navigationBar.offsetHeight;
            document.documentElement.style.setProperty('--navigation-height', navigationHeight + 'px');
        };
        window.addEventListener('load', () => {
            setNavigationHeightVariable();
            new TableOfContents([navigationBar], document, {
                additionalOnScroll: scrollListener,
                activeSelector: '.NavigationList__Item'
            });
        });
        window.addEventListener('resize', setNavigationHeightVariable, { passive: true });
        if (!navigationBar)
            return;
        const horizontalMenu = document.querySelector('.NavigationList');
        const itemTemplate = horizontalMenu.querySelector('.NavigationList__Item--template');
        const titledSections = document.querySelectorAll('[data-section-title]');
        if (titledSections.length === 0) {
            navigationBar.style.visibility = "hidden;";
        }
        else {
            titledSections.forEach((section, index) => {
                const anchor = document.createElement('DIV');
                anchor.classList.add('Anchor');
                const sectionTitle = section.dataset.sectionTitle;
                anchor.id = sectionTitle.replaceAll('ä', 'ae').replaceAll('ö', 'oe').replaceAll('ü', 'ue').replaceAll('ß', 'ss').replaceAll(' ', '_');
                section.parentElement.insertBefore(anchor, section);
                if (!itemTemplate)
                    return;
                const menuItemWrapper = document.createElement('DIV');
                menuItemWrapper.innerHTML = itemTemplate.outerHTML.replace(' NavigationList__Item--template', `${index === 0 ? ' is-active' : ''}`).replaceAll('%anchor%', anchor.id).replaceAll('%title%', sectionTitle);
                horizontalMenu.appendChild(menuItemWrapper.firstChild);
            });
            // Add intersection observer to hide navigation bar if user scrolled below product content
            let navigationBarHidden = false;
            let lastTitledSectionVisible = false;
            const navigationObserverVisibleElements = new Set();
            const navigationBarObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.target.hasAttribute('data-section-title')) {
                        lastTitledSectionVisible = entry.isIntersecting;
                    }
                    else {
                        if (entry.isIntersecting) {
                            navigationObserverVisibleElements.add(entry.target);
                        }
                        else {
                            navigationObserverVisibleElements.delete(entry.target);
                        }
                    }
                });
                const newHidden = !(navigationObserverVisibleElements.size == 0 || lastTitledSectionVisible);
                if (navigationBarHidden !== newHidden) {
                    navigationBarHidden = newHidden;
                    navigationBar.setAttribute('aria-hidden', newHidden.toString());
                }
            }, {
                threshold: 0.2
            });
            const observables = [
                titledSections[titledSections.length - 1],
                document.querySelector('[id$="__product-recommendations"]'),
                document.querySelector('[id$="__recently-viewed-products"]'),
                document.querySelector('#shopify-section-shop-footer')
            ];
            observables.forEach((observable) => {
                if (observable)
                    navigationBarObserver.observe(observable);
            });
        }
        itemTemplate?.remove();
    }
    initOutOfStock() {
        if (this.element.querySelector('.ProductForm__AddToCart')?.disabled) {
            this.element.querySelector('.out-of-stock-trigger').style.display = '';
        }
        const dialog = document.getElementById('out-of-stock-dialog');
        if (!dialog)
            return;
        const variantSelect = dialog.querySelector('select[name="variant"]');
        if (!variantSelect)
            return;
        if (!this.productVariants || this.productVariants.productData.product.variants.length < 2) {
            const label = variantSelect.closest('label');
            if (label && this.productVariants?.defaultVariant) {
                const hiddenInput = document.createElement('input');
                hiddenInput.type = 'hidden';
                hiddenInput.name = 'variant';
                hiddenInput.value = `${this.productVariants.defaultVariant.id}`;
                label.parentElement?.insertBefore(hiddenInput, label);
                label.remove();
            }
        }
        else {
            this.productVariants.productData.product.variants
                .filter(v => !v.available)
                .forEach(variant => {
                const option = document.createElement('option');
                const isDefaultVariante = variant.id === this.productVariants?.defaultVariant.id;
                if (isDefaultVariante)
                    option.selected = true;
                option.value = `${variant.id}`;
                option.textContent = variant.title;
                variantSelect.appendChild(option);
            });
        }
    }
    async addToCart(e) {
        e.preventDefault();
        document.dispatchEvent(new CustomEvent('theme:loading:start'));
        this.addToCartButton.classList.add('loading');
        this.addToCartStickyButton.classList.add('loading');
        const formElement = this.element.querySelector('form[action*="/cart/add"]');
        const form = new Form(formElement);
        let serializedForm = form.serializedFormData;
        const advantagePackageMultiplicity = document.querySelector('input[name="advantage-package-pseudo-variant"]:checked')?.value?.match(/\d+/g)
            ?.map(Number)
            .reduce((a, b) => a + b, 0) ?? 1;
        serializedForm.quantity = advantagePackageMultiplicity * serializedForm.quantity;
        if (serializedForm.gift) {
            const { id, quantity, gift } = serializedForm;
            const items = [{ id, quantity }];
            const gifts = Array.isArray(gift) ? gift : [gift];
            gifts.forEach(giftId => {
                items.push({ id: giftId, quantity: 1 });
            });
            serializedForm = {
                ...serializedForm,
                items,
                id: undefined,
                quantity: undefined,
                gift: undefined,
            };
        }
        try {
            const response = await fetch(`${window.routes.cartAddUrl}.js`, {
                body: JSON.stringify(serializedForm),
                credentials: 'same-origin',
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest' // This is needed as currently there is a bug in Shopify that assumes this header
                }
            });
            if (response.ok) {
                const productsInCart = await Cart.rerenderCart();
                this.addToCartButton.classList.remove('loading');
                this.addToCartStickyButton.classList.remove('loading');
                let drawer = Drawer.drawers['sidebar-cart'];
                if (!drawer) {
                    drawer = new Drawer(document.getElementById('sidebar-cart'));
                }
                drawer.open();
                ProductSection.updateGifts(productsInCart);
            }
            else {
                const content = await response.json();
                const errorMessageElement = document.createElement('span');
                errorMessageElement.className = 'ProductForm__Error Alert Alert--error';
                errorMessageElement.innerHTML = content['description'];
                this.addToCartButton.insertAdjacentElement('afterend', errorMessageElement);
                setTimeout(function () {
                    errorMessageElement.remove();
                }, 2500);
            }
        }
        catch (e) {
            console.error(e);
        }
    }
}
class Loyalty {
    static init() {
        document.addEventListener('theme:product_carousel:loaded', e => {
            const event = e;
            if (!window.loyaltylion || !window.loyaltylion.ui)
                return;
            window.loyaltylion.ui.refresh();
            this.refreshLoyaltyButtons(event.detail);
        });
        document.addEventListener('theme:search:changed', e => {
            const event = e;
            if (!window.loyaltylion || !window.loyaltylion.ui)
                return;
            window.loyaltylion.ui.refresh();
            this.refreshLoyaltyButtons(event.detail);
        });
        this.refreshLoyaltyButtons(document);
        this.initClubProgress();
    }
    static refreshLoyaltyButtons(container) {
        const variantButtons = container.querySelectorAll('a[data-type="loyaltyslider"]');
        if (!variantButtons)
            return;
        // Since loyaltylion refreshes UI asynchronously, we need to wait for the refresh to finish to update variant buttons
        const timeout = 10000;
        const start = Date.now();
        variantButtons.forEach((button) => {
            const productItem = button.closest('.ClubProduct');
            const updateVariantButton = () => {
                if (!productItem)
                    return;
                const lionButton = productItem.querySelector('.lion-in-cart-reward__button');
                if (!lionButton) {
                    if (Date.now() - start < timeout) {
                        setTimeout(updateVariantButton, 1000);
                    }
                    return;
                }
                const lionButtonActive = lionButton.hasAttribute('data-lion-active');
                const lionButtonInactive = /^.*\d+.*$/.test(lionButton.innerText);
                if (lionButtonActive || lionButtonInactive) {
                    if (lionButtonActive) {
                        button.removeAttribute('disabled');
                    }
                    else {
                        button.setAttribute('disabled', 'disabled');
                    }
                    if (!button.classList.contains('Button--icon_only')) {
                        button.innerText = lionButton.innerText;
                    }
                }
                else if (Date.now() - start < timeout) {
                    setTimeout(updateVariantButton, 1000);
                }
            };
            updateVariantButton();
        });
    }
    ;
    static initClubProgress() {
        if (!window.loyaltylion)
            return;
        const hiddenPointsElement = document.createElement("div");
        hiddenPointsElement.classList.add("none");
        hiddenPointsElement.setAttribute("data-lion-points", "approved");
        document.body.appendChild(hiddenPointsElement);
        let currentPoints = null;
        const updateProgress = (container) => {
            const root = container || document;
            const progressElements = root.querySelectorAll("[data-club-progress-price]");
            progressElements.forEach((element) => {
                const redeemButton = element.parentNode?.querySelector('.RedeemButton');
                const priceAttr = element.getAttribute("data-club-progress-price");
                if (!priceAttr)
                    return;
                const priceForProduct = parseInt(priceAttr, 10);
                if (isNaN(priceForProduct))
                    return;
                if (currentPoints !== null && currentPoints >= priceForProduct) {
                    element.classList.add("none");
                    redeemButton?.classList.remove('none');
                }
                else {
                    redeemButton?.classList.add('none');
                    element.classList.remove("none");
                    const barEl = element.querySelector("[data-club-progress-bar]");
                    const missingEl = element.querySelector("[data-club-progress-missing-points]");
                    if (barEl && currentPoints !== null) {
                        const percent = Math.round((currentPoints / priceForProduct) * 100);
                        barEl.style.setProperty("--progress", `${percent}%`);
                    }
                    if (missingEl && currentPoints !== null) {
                        missingEl.innerText = String(Math.min(priceForProduct, priceForProduct - currentPoints));
                    }
                }
            });
        };
        const pointsObserver = new MutationObserver(() => {
            const text = hiddenPointsElement.innerText.replace(/[^\d]/g, "");
            const newPoints = text ? parseInt(text, 10) : 0;
            if ((!newPoints && newPoints !== 0) || newPoints === currentPoints)
                return;
            currentPoints = newPoints;
            updateProgress();
        });
        pointsObserver.observe(hiddenPointsElement, { childList: true });
        const loyaltylionObj = window.loyaltylion;
        if (loyaltylionObj?.ui?.refresh) {
            loyaltylionObj.ui.refresh();
        }
        const updateProgressOnLoad = (e) => {
            const detail = e.detail;
            if (detail instanceof HTMLElement) {
                updateProgress(detail);
            }
        };
        document.addEventListener("theme:search:changed", updateProgressOnLoad);
        document.addEventListener("theme:product_carousel:loaded", updateProgressOnLoad);
        document.addEventListener("theme:collection:changed", updateProgressOnLoad);
    }
}
export class Cart {
    static init(domDelegateInstance) {
        this.element = document.querySelector('[data-section-type="cart"]');
        if (!this.element)
            return;
        if (this.element.hasAttribute('data-section-settings'))
            this.settings = JSON.parse(this.element.getAttribute('data-section-settings'));
        domDelegateInstance.on('click', 'button.GlobalAddToCart', (e) => {
            const target = e.target;
            const variantId = target.dataset.variantId;
            const products = target.dataset.products;
            if (!variantId && !products)
                return;
            const actions = [];
            if (products) {
                const productsJson = JSON.parse(products);
                productsJson.items.forEach((item) => {
                    actions.push({ action: 'add', id: +item.id, quantity: item.quantity || 1, properties: item.properties || {} });
                });
            }
            else {
                actions.push({ action: 'add', id: +variantId, quantity: 1 });
            }
            target.classList.add('loading');
            target.disabled = true;
            this.changeCartItems(actions);
            document.addEventListener('theme:loading:end', () => {
                target.classList.remove('loading');
                target.disabled = false;
            }, { once: true });
        });
        window.loyaltylion?.on('cart.addedItem', async () => {
            await this.rerenderCart();
            this.open();
        });
        const delegateElement = new window.domDelegate.Delegate(this.element);
        const onQuantityChange = async (e) => {
            e.preventDefault();
            const quantity = e.target.tagName === 'INPUT' ? Math.max(parseInt(e.target.value) || 1, 1) : parseInt(e.target.dataset.quantity);
            const lineKey = e.target.dataset.lineKey;
            const elementsToAnimate = [];
            if (quantity === 0)
                elementsToAnimate.push(e.target.closest('.CartItem__Wrapper'));
            await this.changeCartItems([{ action: 'update', id: lineKey, quantity }]);
        };
        delegateElement.on('click', '[data-action="update-item-quantity"], [data-action="remove-item"]', onQuantityChange);
        delegateElement.on('change', '[name="updates[]"]', onQuantityChange);
        delegateElement.on('cart:loading:start', () => this.element.classList.add('loading'));
        delegateElement.on('cart:loading:end', () => this.element.classList.remove('loading'));
        delegateElement.on('click', '[data-action="apply-discount"]', this.applyDiscount.bind(this));
        delegateElement.on('keydown', 'input[name="discount"]', (e) => {
            if (e.key === 'Enter')
                this.applyDiscount(e);
        });
        delegateElement.on('click', 'button[name="checkout"]', (e) => {
            if (!e.target)
                return;
            e.target.classList.add('loading');
            window.addEventListener('pageshow', (pageshowEvent) => {
                if (!pageshowEvent.persisted)
                    return;
                e.target.classList.remove('loading');
            }, { once: true });
        });
        Localization.getIPLocation().then(countryCode => {
            switch (countryCode) {
                case "DE":
                case "AT":
                    this.shippingLocale = "de";
                    break;
                case "CH":
                    this.shippingLocale = "ch";
                    break;
                case "NL":
                case "BE":
                case "LU":
                    this.shippingLocale = "benelux";
                    break;
            }
            this.calculateFreeShipping();
        });
        this.handleCartAttributes().then();
    }
    static get isOpen() {
        return this.element.ariaHidden === 'false';
    }
    static async applyDiscount(e) {
        e.preventDefault();
        const discountInput = this.element.querySelector('input[name="cart-discount-code"]');
        const discountApplyButton = this.element.querySelector('[data-action="apply-discount"]');
        if (!discountInput || !discountApplyButton)
            return;
        const setErrorMessage = (message) => {
            const labelElement = discountInput.closest('label');
            const errorMessageElement = labelElement?.querySelector('.Form__InputError .Form__InputErrorText');
            if (!labelElement || !errorMessageElement)
                return;
            if (!message) {
                labelElement.classList.remove('error');
                return;
            }
            errorMessageElement.innerHTML = message;
            labelElement.classList.add('error');
        };
        const discountCode = discountInput.value.trim();
        const currentDiscountCodes = discountInput.dataset.currentDiscountCodes ? discountInput.dataset.currentDiscountCodes.split(',') : [];
        const allDiscountCodes = currentDiscountCodes ? [...currentDiscountCodes, discountCode] : [discountCode];
        if (!discountCode) {
            setErrorMessage(window.languages.cart.errorEmptyDiscount);
            return;
        }
        if (/^.*-sp187$/.test(discountCode)) {
            // Block any sp discount
            setErrorMessage(window.languages.cart.errorInvalidDiscount);
            return;
        }
        try {
            discountApplyButton.classList.add('loading');
            const response = await fetch(`${window.routes.cartUpdateUrl}.js`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ discount: allDiscountCodes.join(',') })
            });
            if (!response.ok)
                throw new Error('Error applying discount');
            const responseData = await response.json();
            const appliedDiscount = responseData.discount_codes.find((d) => d.code === discountCode);
            if (!appliedDiscount)
                throw new Error('Discount not found in server response');
            if (!appliedDiscount.applicable) {
                setErrorMessage(window.languages.cart.errorInvalidDiscount);
                discountApplyButton.classList.remove('loading');
                return;
            }
            setErrorMessage('');
            document.dispatchEvent(new CustomEvent('theme:loading:start'));
            this.element.dispatchEvent(new CustomEvent('cart:loading:start'));
            await this.rerenderCart();
        }
        catch (error) {
            setErrorMessage(window.languages.cart.errorDiscountGeneral);
            console.error(error);
            document.dispatchEvent(new CustomEvent('theme:loading:end'));
            Cart.element.dispatchEvent(new CustomEvent('cart:loading:end'));
            discountApplyButton.classList.remove('loading');
        }
    }
    static async changeCartItems(items) {
        document.dispatchEvent(new CustomEvent('theme:loading:start'));
        this.element.dispatchEvent(new CustomEvent('cart:loading:start'));
        const cartItemList = document.querySelector('.CartItems__List');
        const elementsToAnimate = [];
        if (cartItemList) {
            items.forEach(item => {
                if (item.quantity !== 0)
                    return;
                const normalizedId = `${item.id}`.includes(':') ? `${item.id}`.split(':')[0] : `${item.id}`;
                const itemElement = cartItemList.querySelector(`[data-variant-id="${normalizedId}"]`);
                if (!itemElement)
                    return;
                const wrapper = itemElement.closest('.CartItem__Wrapper');
                elementsToAnimate.push(wrapper);
            });
        }
        try {
            // Sort items into those to add and those to update, then first update existing items because their keys might change during adding new products
            const itemsToUpdate = {};
            const itemsToAdd = [];
            for (let item of items) {
                if (!item.id)
                    throw new Error(`[Cart.changeCartItems] Missing id in item: ${JSON.stringify(item)}`);
                if (item.action === 'add') {
                    itemsToAdd.push({ id: item.id, quantity: item.quantity || 1, properties: item.properties });
                }
                else if (item.action === 'update') {
                    if (typeof item.quantity !== 'number')
                        throw new Error(`[Cart.changeCartItems] Missing quantity in item to update: ${JSON.stringify(item)}`);
                    itemsToUpdate[item.id] = item.quantity;
                }
            }
            if (Object.keys(itemsToUpdate).length) {
                await fetch(`${window.routes.cartUpdateUrl}.js`, {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ updates: itemsToUpdate })
                });
            }
            if (itemsToAdd.length) {
                await fetch(`${window.routes.cartAddUrl}.js`, {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ items: itemsToAdd })
                });
            }
        }
        catch (error) {
            console.error(error);
        }
        finally {
            const productsInCart = await this.rerenderCart(elementsToAnimate);
            ProductSection.updateGifts(productsInCart);
            SidebarMenu.close();
            this.open();
        }
    }
    static open() {
        if (this.settings.type !== 'drawer' || this.isOpen)
            return;
        const drawer = Drawer.drawers[this.element.id] ?? new Drawer(this.element);
        drawer?.open();
    }
    static async add(products = [], callback) {
        if (!products || products.length === 0) {
            if (callback)
                callback();
            return;
        }
        await this.changeCartItems(products.map(product => ({ action: 'add', id: product.id, quantity: product.quantity })));
        if (callback)
            callback();
    }
    static async rerenderCart(elementsToAnimate) {
        const type = this.settings.type === 'drawer' ? 'drawer' : 'page';
        if (elementsToAnimate) {
            elementsToAnimate.forEach(el => {
                el.style.setProperty('--item-height', `${el.offsetHeight}px`);
                el.offsetHeight; // Force reflow
                el.classList.add('removed');
            });
        }
        const variantIds = [];
        try {
            const fetchPromise = fetch(`${window.location.pathname}?section_id=${type === 'drawer' ? 'cart-drawer' : 'main-cart'}&timestamp="${Date.now()}"`, {
                credentials: 'same-origin',
                method: 'GET'
            }).then(res => res.text());
            const promises = [fetchPromise];
            if (elementsToAnimate) {
                const transitionPromise = Promise.all(elementsToAnimate.map((el) => new Promise((resolve) => {
                    const handler = (e) => {
                        if (e.propertyName === 'height') {
                            el.removeEventListener('transitionend', handler);
                            resolve();
                        }
                    };
                    el.addEventListener('transitionend', handler);
                })));
                promises.push(transitionPromise);
            }
            const [html] = await Promise.all(promises);
            const tempElement = document.createElement('div');
            tempElement.innerHTML = html;
            const tempCartSection = tempElement.querySelector('[data-section-type="cart"]');
            const variantIdsInCart = JSON.parse(tempCartSection.getAttribute('data-cart-variant-ids') || '[]');
            const cartItemCount = parseInt(tempCartSection.getAttribute('data-cart-item-count') || '0');
            variantIds.push(...variantIdsInCart);
            const cartDot = document.querySelector('.ShopHeader__CartDot');
            if (cartDot) {
                if (cartItemCount === 0) {
                    cartDot.classList.remove('is-visible');
                }
                else {
                    cartDot.innerHTML = cartItemCount.toString();
                    cartDot.classList.add('is-visible');
                }
            }
            this.element.innerHTML = '';
            for (const node of tempCartSection.children) {
                this.element.appendChild(node);
            }
            for (const attr of tempCartSection.attributes) {
                if (/^data-cart-.*$/.test(attr.name))
                    this.element.setAttribute(attr.name, attr.value);
            }
        }
        catch (e) {
            console.error(e);
        }
        finally {
            window?.loyaltylion?.ui?.fetchAndUpdateCart(); // Undocumented API method but necessary to update loyaltylion cart state after manual cart changes. Maybe use lion.setCartState instead in the future
            document.dispatchEvent(new CustomEvent('theme:loading:end'));
            Cart.element.dispatchEvent(new CustomEvent('cart:loading:end'));
            Popover.closeAll();
            Dom.evalScripts(this.element);
            document.dispatchEvent(new CustomEvent('theme:cart:refreshed'));
        }
        this.calculateFreeShipping();
        return variantIds;
    }
    static calculateFreeShipping() {
        if (!this.shippingLocale)
            return;
        const thresholdElements = document.querySelectorAll('[data-progress-thresholds]');
        thresholdElements.forEach((thresholdElement) => {
            const thresholdData = JSON.parse(thresholdElement.dataset.progressThresholds)[this.shippingLocale], thresholdProgressElement = thresholdElement.querySelector('[data-progress-element="bar"]'), thresholdTextElement = thresholdElement.querySelector('[data-progress-element="text"]');
            thresholdProgressElement.style.setProperty('--shipping-progress-width', `${thresholdData.percentage}%`);
            thresholdTextElement.innerHTML = `${thresholdData.text}`;
            if (thresholdData.percentage >= 100) {
                thresholdElement.querySelector('[data-progress-element="unreached-icon"]').classList.add('none');
                thresholdElement.querySelector('[data-progress-element="reached-icon"]').classList.remove('none');
            }
        });
    }
    static async handleCartAttributes() {
        const attributes = {};
        // Check from parameter
        const search = new URLSearchParams(window.location.search);
        const from = search.get('_from');
        if (from)
            attributes['_from'] = from;
        const salesEventTargeting = search.get('_sales-event-targeting');
        if (salesEventTargeting)
            attributes['_sales-event-targeting'] = salesEventTargeting;
        if (!Object.keys(attributes).length)
            return;
        const updateResult = await fetch(`${window.routes.cartUpdateUrl}.js`, {
            body: JSON.stringify({
                attributes
            }),
            credentials: 'same-origin',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        });
        if (!updateResult.ok) {
            console.error('Error updating cart with parameters', attributes);
            return;
        }
        await this.rerenderCart();
        Object.keys(attributes).forEach(key => {
            search.delete(key);
        });
        const newUrl = new URL(window.location.href);
        newUrl.search = search.toString();
        window.history.replaceState({
            path: newUrl.toString()
        }, '', newUrl);
    }
}
Cart.shippingLocale = 'general';
class Form {
    constructor(form) {
        this.form = form;
        this.formData = new FormData(form);
        this.serializedFormData = this.serialize(this.formData);
    }
    serialize(formData) {
        const hash = {};
        for (const [key, value] of formData.entries()) {
            this.setNestedValue(hash, key, value);
        }
        return hash;
    }
    setNestedValue(obj, path, value) {
        const keys = path.split(/[\[\]]/).filter(Boolean);
        let current = obj;
        keys.forEach((key, i) => {
            if (i === keys.length - 1) {
                if (current[key] !== undefined) {
                    if (Array.isArray(current[key])) {
                        current[key].push(value);
                    }
                    else {
                        current[key] = [current[key], value];
                    }
                }
                else {
                    current[key] = value;
                }
            }
            else {
                if (!current[key] || typeof current[key] !== "object") {
                    current[key] = {};
                }
                current = current[key];
            }
        });
    }
}
class ScrollList {
    constructor(element) {
        this.items = [];
        this.scrollableLeft = false;
        this.scrollableRight = false;
        this.dragging = false;
        this.element = element;
        try {
            this.options = this.element.hasAttribute('data-options')
                ? JSON.parse(this.element.getAttribute('data-options'))
                : {};
        }
        catch {
            this.options = {};
        }
        this.scrollBox = element.querySelector('.scroll-list__content');
        this.leftButton = element.querySelector('.scroll-list__nav--left');
        this.rightButton = element.querySelector('.scroll-list__nav--right');
        if (this.options.wheelScroll !== false) {
            this.scrollBox.addEventListener('wheel', this.onScroll.bind(this));
        }
        this.leftButton?.addEventListener('click', () => {
            const minOffset = this.scrollBox.scrollLeft * -1;
            this.scroll(Math.max(minOffset, -250), true);
        });
        this.rightButton?.addEventListener('click', () => {
            const maxOffset = this.scrollBox.scrollWidth - this.scrollBox.clientWidth;
            this.scroll(Math.min(maxOffset, 250), true);
        });
        this.element.addEventListener('theme:hsl:refresh', this.refresh.bind(this));
        this.leftObserver = this.createObserver(this.leftButton);
        this.rightObserver = this.createObserver(this.rightButton);
        this.dragEndListener = this.onDragEnd.bind(this);
        this.scrollBox.addEventListener('mousedown', this.onDragStart.bind(this));
        document.documentElement.addEventListener('mouseup', this.dragEndListener);
        document.documentElement.addEventListener('mouseleave', this.dragEndListener);
        document.documentElement.addEventListener('mousemove', this.onDrag.bind(this));
        // Prevent link default dragging behavior
        this.element.addEventListener('dragstart', (e) => {
            const target = e.target;
            if (target.tagName === 'A' || target.tagName === 'IMG')
                e.preventDefault();
        });
        this.refresh();
    }
    destroy() {
        this.leftObserver.disconnect();
        this.rightObserver.disconnect();
        document.documentElement.removeEventListener('mouseup', this.dragEndListener);
        document.documentElement.removeEventListener('mouseleave', this.dragEndListener);
    }
    onScroll(e) {
        if (!this.scrollableLeft && !this.scrollableRight)
            return;
        e.preventDefault();
        const position = e.deltaY > 0 ? 50 : -50;
        this.scroll(position, false);
    }
    onDragStart() {
        this.dragging = 1;
    }
    onDragEnd() {
        this.dragging = false;
        this.element.classList.remove('dragging');
    }
    onDrag(e) {
        if (!this.dragging || !this.scrollableLeft && !this.scrollableRight)
            return;
        if (+this.dragging < 5) {
            this.dragging = +this.dragging + 1;
        }
        else if (this.dragging === 5) {
            this.element.classList.add('dragging');
            this.dragging = 6;
        }
        if (this.dragging === 6)
            e.preventDefault();
        this.scroll(e.movementX * -1, false);
    }
    refresh() {
        this.leftObserver.disconnect();
        this.rightObserver.disconnect();
        this.items = this.element.querySelectorAll('.scroll-list__item');
        if (this.items.length <= 1)
            return;
        this.items.forEach(item => {
            item.removeAttribute('data-position');
        });
        this.items[0].setAttribute('data-position', 'left');
        this.leftObserver.observe(this.items[0]);
        this.items[this.items.length - 1].setAttribute('data-position', 'right');
        this.rightObserver.observe(this.items[this.items.length - 1]);
    }
    createObserver(target) {
        return new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (target)
                    target.classList.toggle('is-visible', !entry.isIntersecting);
                if (entry.target.getAttribute('data-position') === 'left') {
                    this.element.classList.toggle('scrollable--left', !entry.isIntersecting);
                    this.scrollableLeft = !entry.isIntersecting;
                }
                if (entry.target.getAttribute('data-position') === 'right') {
                    this.element.classList.toggle('scrollable--right', !entry.isIntersecting);
                    this.scrollableRight = !entry.isIntersecting;
                }
            });
        }, {
            root: this.element,
            rootMargin: '100% 0px 100% 0px',
            threshold: 0.99
        });
    }
    scroll(offset, smooth = false) {
        this.scrollBox.scrollBy({
            left: offset,
            behavior: smooth ? 'smooth' : 'auto'
        });
    }
}
export class TableOfContents {
    constructor(summaryContainers, contentContainer, options = {}) {
        this.anchors = [];
        this.links = [];
        this.anchorMap = new Map();
        this.summaries = summaryContainers;
        this.content = contentContainer;
        this.onScroll = options.additionalOnScroll;
        this.activeSelector = options.activeSelector;
        this.scrollProperties = {
            windowY: window.scrollY,
            currentAnchor: null,
            scrollingTo: null,
        };
        this.init();
        document.addEventListener("theme:navigation:change", this.init.bind(this));
        this.scrollListener = this.onScrollHandle.bind(this);
        window.addEventListener("scroll", this.scrollListener, { passive: true });
        this.onScrollHandle();
    }
    destroy() {
        window.removeEventListener("scroll", this.scrollListener);
    }
    init() {
        this.anchorMap.clear();
        this.anchors = [];
        this.links = [];
        const anchors = Array.from(this.content.querySelectorAll(".Anchor"));
        anchors.forEach(anchor => {
            this.anchorMap.set(anchor, []);
            this.anchors.push(anchor);
        });
        this.summaries.forEach(summary => {
            const links = Array.from(summary.querySelectorAll(".toc-link:not([href='#%anchor%'])"));
            links.forEach(link => {
                this.links.push(link);
                const hash = new URL(link.href).hash;
                const anchor = this.content.querySelector(hash);
                if (anchor) {
                    this.anchorMap.get(anchor)?.push(link);
                }
                link.addEventListener("click", e => {
                    const target = e.target.closest("a");
                    if (target) {
                        this.scrollProperties.scrollingTo = this.content.querySelector(new URL(target.href).hash);
                        // If toc is in popover, close it
                        const closestPopover = target.closest('.Popover');
                        const popoverInstance = closestPopover ? Popover.popovers[closestPopover.id] : null;
                        if (popoverInstance)
                            popoverInstance.close();
                    }
                });
            });
        });
        this.anchorMap.forEach((value, key) => {
            if (value.length > 0)
                return;
            this.anchorMap.delete(key);
            this.anchors.splice(this.anchors.indexOf(key), 1);
        });
    }
    onScrollHandle() {
        const props = this.scrollProperties;
        props.windowY = window.scrollY;
        for (let i = this.anchors.length - 1; i >= 0; i--) {
            const anchor = this.anchors[i];
            const offsetTop = anchor.getBoundingClientRect().top;
            if (offsetTop > 10)
                continue;
            if (props.currentAnchor !== props.scrollingTo &&
                (anchor === props.currentAnchor ||
                    (props.scrollingTo !== null && props.scrollingTo !== anchor)))
                break;
            props.scrollingTo = null;
            props.currentAnchor = anchor;
            this.links.forEach(link => {
                const activeParent = this.activeSelector
                    ? link.closest(this.activeSelector)
                    : null;
                activeParent?.classList.remove("is-active");
                link.classList.remove("is-active");
            });
            const activeLinks = this.anchorMap.get(anchor) ?? [];
            activeLinks.forEach((link) => {
                const collapsible = link.closest('.Collapsible');
                let collapsibleButton = null, collapsibleInner = null;
                if (collapsible) {
                    collapsibleButton = collapsible.querySelector('[data-action="toggle-collapsible"]');
                    if (collapsibleButton?.getAttribute('aria-expanded') === 'false') {
                        collapsibleButton.click();
                    }
                    // If link is in collapsible inner, additionally add active class to it
                    collapsibleInner = link.closest('.Collapsible__Inner');
                    if (this.activeSelector) {
                        link.closest(`${this.activeSelector}[data-toc-level="1"]`)?.classList.add('is-active');
                        if (collapsibleInner)
                            link.closest(this.activeSelector)?.classList.add('is-active');
                    }
                    else {
                        link.classList.add('is-active');
                    }
                }
                else {
                    if (this.activeSelector) {
                        link.closest(this.activeSelector)?.classList.add('is-active');
                    }
                    else {
                        link.classList.add('is-active');
                    }
                }
                const closestToc = link.closest('.TableOfContents');
                if (closestToc) {
                    // Scroll to current link, quite complex due to collapsible functionality and different locations of toc in sidebar and popover
                    const topLevelLink = ((collapsibleInner && this.activeSelector) ? collapsibleInner.closest(this.activeSelector) : this.activeSelector ? link.closest(this.activeSelector) : link), scrollContainer = closestToc.closest('.Popover__ValueList') || closestToc, menuScrollTop = topLevelLink.offsetTop - scrollContainer.clientHeight / 4;
                    scrollContainer.scroll({
                        top: menuScrollTop,
                        behavior: "instant"
                    });
                    // Close any remaining open collapsibles
                    const openCollapsibleButtons = closestToc.querySelectorAll('[data-action="toggle-collapsible"][aria-expanded="true"]');
                    openCollapsibleButtons.forEach(openCollapsibleButton => {
                        if (!collapsibleButton || openCollapsibleButton !== collapsibleButton)
                            openCollapsibleButton?.click();
                    });
                }
            });
            this.onScroll?.(anchor, activeLinks);
            break;
        }
    }
}
class MultiStepForm {
    constructor(element) {
        this.element = element;
        const formOptions = this.element.getAttribute('data-form-options');
        this.options = formOptions ? JSON.parse(formOptions.replaceAll("'", '"')) : {};
        if (this.options.loading)
            this.element.classList.add('LoadingContainer');
        this.initListeners();
    }
    initListeners() {
        this.element.querySelectorAll('[data-form-step-action="continue"], [data-form-step-action="return"]').forEach(btn => {
            btn.addEventListener('click', e => {
                const target = e.target;
                const currentStepContainer = target.closest('[data-form-step]'), action = target.getAttribute('data-form-step-action') || '';
                if (action === 'continue' && !this.validateStep(currentStepContainer))
                    return;
                let step = parseInt(currentStepContainer.getAttribute('data-form-step') || '0');
                if (action === 'continue' && this.options.submit === 'manual') {
                    if (this.options.loading)
                        this.element.classList.add('loading');
                    this.element.dispatchEvent(new CustomEvent('theme:form:step', { detail: this.toJson() }));
                    return;
                }
                action === 'continue' ? step++ : step--;
                this.setStep(step);
            });
        });
        this.element.addEventListener('submit', e => {
            e.preventDefault();
        });
        this.element.querySelectorAll('button[type="submit"]').forEach(btn => {
            btn.addEventListener('click', e => {
                const currentStepContainer = e.target.closest('[data-form-step]');
                if (!this.validateStep(currentStepContainer))
                    return;
                if (this.options.submit === 'manual') {
                    if (this.options.loading)
                        this.element.classList.add('loading');
                    this.element.dispatchEvent(new CustomEvent('theme:form:submit', { detail: this.toJson() }));
                    return;
                }
                this.element.submit();
                this.element.dispatchEvent(new CustomEvent('theme:form:done'));
            });
        });
        // Hook to allow external logic to set active step
        this.element.addEventListener('theme:form:setstep', e => {
            const step = e.detail;
            this.setStep(step);
        });
        // Hook to allow external logic to go to next step
        this.element.addEventListener('theme:form:continue', e => {
            this.element.classList.remove('loading');
            const currentStepContainer = this.element.querySelector('[data-form-step][aria-hidden="false"]');
            if (!currentStepContainer)
                return;
            const nextStep = parseInt(currentStepContainer.getAttribute('data-form-step') || '0') + 1;
            this.setStep(nextStep);
            if (e.detail.successAlert)
                this.showAlert(e.detail.successAlert, true);
        });
        // Hook to allow external logic to signal the form submit is done
        this.element.addEventListener('theme:form:done', () => {
            this.element.classList.remove('loading');
            this.showAlert('done', true);
        });
        // Hook to allow external logic to signal the form submit is done
        this.element.addEventListener('theme:form:alert', e => {
            this.element.classList.remove('loading');
            this.hideAlerts();
            this.showAlert(e.detail.name, e.detail.success);
        });
    }
    validateStep(stepElement) {
        this.hideAlerts(stepElement);
        let valid = true;
        stepElement.querySelectorAll('input, textarea, select').forEach(input => {
            if (!input.validity.valid) {
                valid = false;
                input.reportValidity();
            }
        });
        if (!valid)
            this.showAlert();
        return valid;
    }
    setStep(step) {
        const currentStepContainer = this.element.querySelector('[data-form-step][aria-hidden="false"]'), currentStep = parseInt(currentStepContainer.getAttribute('data-form-step') || '0');
        if (step !== currentStep) {
            const newStepContainer = this.element.querySelector(`[data-form-step="${step}"]`);
            if (!newStepContainer)
                return;
            currentStepContainer.setAttribute('aria-hidden', 'true');
            newStepContainer.setAttribute('aria-hidden', 'false');
            this.hideAlerts(newStepContainer);
        }
        const firstInput = this.element.querySelector('[data-form-step][aria-hidden="false"] input');
        if (firstInput)
            firstInput.focus();
    }
    toJson() {
        const formData = new FormData(this.element);
        const json = {};
        formData.forEach((value, key) => {
            if (value !== '')
                json[key] = value;
        });
        return json;
    }
    showAlert(name = 'validation', success = false) {
        const alertElement = this.element.querySelector(`[data-form-step][aria-hidden="false"] [data-form-${success ? 'success' : 'error'}="${name}"]`);
        if (!alertElement)
            return;
        alertElement.setAttribute('aria-hidden', 'false');
        alertElement.closest('.Form__Alert').setAttribute('aria-hidden', 'false');
    }
    hideAlerts(container = this.element) {
        const alertElements = container.querySelectorAll('.Alert__Message[aria-hidden="false"]');
        if (alertElements.length === 0)
            return;
        alertElements.forEach(element => {
            element.setAttribute('aria-hidden', 'true');
            element.closest('.Form__Alert').setAttribute('aria-hidden', 'true');
        });
    }
}
export class WistiaLoader {
    static loadVideo(element) {
        if (element.complete) {
            this.runLazyHandler(element);
        }
        else {
            element.addEventListener('load', (e) => this.runLazyHandler(e.target), { once: true });
        }
    }
    static runLazyHandler(element) {
        const wistiaId = element.dataset.wistiaLazyloadMediaId;
        if (!wistiaId)
            return;
        const videoWrapper = element.closest('.VideoWrapper, .VideoWrapper--blank');
        if (!videoWrapper) {
            console.error('[Wistia lazy loading] No video wrapper found for video element', element);
            return;
        }
        const embedScript = document.createElement('script');
        embedScript.type = 'module';
        embedScript.src = `https://fast.wistia.com/embed/${wistiaId}.js`;
        embedScript.async = true;
        videoWrapper.appendChild(embedScript);
        if (typeof window.Wistia === 'undefined' && !document.head.querySelector('script[src*="wistia.com/player.js"]')) {
            const wistiaScript = document.createElement('SCRIPT');
            wistiaScript.src = 'https://fast.wistia.com/player.js';
            wistiaScript.async = true;
            document.head.appendChild(wistiaScript);
        }
        if (element.getAttribute('data-wistia-after-lazyload') === 'remove-element') {
            const parent = element.parentElement;
            parent.insertAdjacentHTML('afterbegin', this.createEmbedHtml(element));
            element.remove();
        }
        else if (element.getAttribute('data-wistia-after-lazyload') === 'wrap-parent') {
            const parent = element.parentElement;
            parent.insertAdjacentHTML('beforebegin', this.createEmbedHtml(element));
            parent.style.position = 'relative';
            parent.style.width = '100%';
            parent.style.height = '100%';
            const styleTag = document.createElement('style');
            styleTag.innerHTML = '.wistia_click_to_play { width: 100%; }';
            parent.append(styleTag);
            parent.parentElement.querySelector('wistia-player').appendChild(parent);
        }
        else {
            console.error('[Wistia lazy loading] Unknown data-wistia-after-lazyload value', element);
        }
        const playerElement = videoWrapper.querySelector('wistia-player');
        playerElement.addEventListener('api-ready', () => {
            videoWrapper.style.pointerEvents = '';
            videoWrapper.classList.remove('loading');
            playerElement.dispatchEvent(new CustomEvent('theme:wistiaplayer:inserted', { bubbles: true }));
            const playButton = videoWrapper.querySelector('.VideoWrapper__PlayButton');
            if (playButton)
                playButton.classList.remove('none');
        });
        document.dispatchEvent(new CustomEvent('theme:tracking:init', { detail: { element: videoWrapper } }));
    }
    static createEmbedHtml(element) {
        const attributes = Array.from(element.attributes).reduce((acc, attr) => {
            if (!attr.name.startsWith('data-wistia-lazyload-'))
                return acc;
            const name = attr.name.replace('data-wistia-lazyload-', '');
            const value = attr.value ? `="${attr.value}"` : '';
            return `${acc} ${name}${value}`;
        }, '');
        return `<wistia-player${attributes}></wistia-player>`;
    }
}
//TODO: Use in future: https://kulturbanause.de/blog/responsive-masonry-layout-mit-css/
class Masonry {
    static initGrids(grids) {
        if (!grids.length)
            return;
        const gridMap = new Map();
        const watchCssGrids = [];
        const isCssEnabled = (grid) => {
            const pseudoStyle = window.getComputedStyle(grid, ':after');
            return pseudoStyle?.content.includes('masonry') ?? false;
        };
        const enableGrid = (grid) => {
            if (gridMap.get(grid) !== null)
                return;
            gridMap.set(grid, new MiniMasonry({
                container: '[data-masonry-grid="true"]',
                baseWidth: 400,
                gutter: 24,
                ultimateGutter: 16,
                minify: false,
                surroundingGutter: false,
            }));
        };
        const disableGrid = (grid) => {
            const instance = gridMap.get(grid);
            if (instance === null)
                return;
            instance?.destroy();
            gridMap.set(grid, null);
        };
        grids.forEach((grid) => {
            if (grid.hasAttribute('data-masonry-initialized'))
                return;
            grid.setAttribute('data-masonry-initialized', 'true');
            gridMap.set(grid, null);
            grid.addEventListener('theme:masonry:change', () => {
                const instance = gridMap.get(grid);
                instance?.layout();
            });
            let enable = true;
            if (grid.getAttribute('data-masonry-watch-css') === 'true') {
                watchCssGrids.push(grid);
                if (!isCssEnabled(grid))
                    enable = false;
            }
            if (enable)
                enableGrid(grid);
        });
        if (!watchCssGrids.length)
            return;
        const onResize = () => {
            watchCssGrids.forEach((grid) => {
                const instance = gridMap.get(grid);
                const enable = isCssEnabled(grid);
                if (instance === null && enable) {
                    enableGrid(grid);
                }
                else if (instance !== null && !enable) {
                    disableGrid(grid);
                }
            });
        };
        let masonryResizeTimer = null;
        window.addEventListener('resize', () => {
            if (masonryResizeTimer)
                clearTimeout(masonryResizeTimer);
            masonryResizeTimer = window.setTimeout(onResize, 100);
        });
    }
}
class ProductRecommendations {
    constructor(element) {
        this.element = element;
        this.options = JSON.parse(this.element.dataset.sectionSettings);
        if (this.options.useRecommendations) {
            this.loadRecommendations().then(this.createSlideshow.bind(this));
        }
        else {
            this.createSlideshow();
        }
    }
    onUnload() {
        this.carousel.disable();
    }
    async loadRecommendations() {
        const { sectionId } = this.element.dataset;
        const { productId, recommendationsCount } = this.options;
        const url = `${window.routes.productRecommendationsUrl}?section_id=${sectionId}&product_id=${productId}&limit=${recommendationsCount}`;
        try {
            const response = await fetch(url);
            const html = await response.text();
            const tempContainer = document.createElement('div');
            tempContainer.innerHTML = html;
            const liveContainer = this.element.querySelector('.ProductRecommendations');
            const newContent = tempContainer.querySelector('.ProductRecommendations');
            if (liveContainer && newContent) {
                liveContainer.innerHTML = newContent.innerHTML;
            }
            document.dispatchEvent(new CustomEvent('theme:product_carousel:loaded', { detail: this.element }));
            document.dispatchEvent(new CustomEvent('theme:tracking:init', { detail: { element: liveContainer } }));
        }
        catch (error) {
            console.error('Error loading product recommendations:', error);
        }
    }
    createSlideshow() {
        const carouselElement = this.element.querySelector('.Slider');
        if (!carouselElement) {
            return;
        }
        this.carousel = new Slider(carouselElement);
    }
}
class RecentlyViewedProducts {
    constructor(element) {
        this.element = element;
        this.options = JSON.parse(this.element.dataset.sectionSettings);
        if (this.options.productId) {
            this.saveProduct(this.options.productId);
        }
        this.fetchProducts().then();
    }
    onUnload() {
        this.carousel.disable();
    }
    saveProduct(productId) {
        try {
            const items = JSON.parse(localStorage.getItem('recentlyViewedProducts') || '[]');
            const updatedItems = items.filter(id => id !== productId);
            updatedItems.unshift(productId);
            localStorage.setItem('recentlyViewedProducts', JSON.stringify(updatedItems.slice(0, 8)));
        }
        catch (error) {
            // Do nothing, this may happen in Safari in incognito mode
        }
    }
    async fetchProducts() {
        const queryString = this.getSearchQueryString();
        if (!queryString)
            return;
        const url = `${window.routes.searchUrl}?view=recently-viewed-products&type=product&q=${encodeURIComponent(queryString)}`;
        try {
            const response = await fetch(url, {
                credentials: 'same-origin',
                method: 'GET',
            });
            if (!response.ok) {
                console.error(`Fetch failed with status ${response.status}`);
                return;
            }
            const content = await response.text();
            const tempElement = document.createElement('div');
            tempElement.innerHTML = content;
            const section = tempElement.querySelector('.Section');
            if (!section)
                return;
            this.element.innerHTML = section.innerHTML;
            this.element.parentNode.style.display = 'block';
            this.carousel = new Slider(this.element.querySelector('.Slider'));
            document.dispatchEvent(new CustomEvent('theme:product_carousel:loaded', { detail: this.element }));
            document.dispatchEvent(new CustomEvent('theme:tracking:init', { detail: { element: this.element } }));
        }
        catch (error) {
            console.error('Error fetching recently viewed products:', error);
        }
    }
    getSearchQueryString() {
        const items = JSON.parse(localStorage.getItem('recentlyViewedProducts') || '[]');
        if (!Array.isArray(items) || items.length === 0)
            return '';
        const productId = this.options?.productId;
        const filteredItems = productId ? items.filter(id => id !== productId) : items;
        return filteredItems.map(id => `id:${id}`).join(' OR ');
    }
}
class SidebarMenu {
    static init(domDelegateInstance) {
        if (this.initialized)
            return;
        this.initialized = true;
        const element = document.getElementById("sidebar-menu");
        if (!element)
            return;
        this.element = element;
        this.drawer = new Drawer(this.element);
        this.menuOverlay = document.querySelector(".PageOverlay--menu");
        // Overlay klick schließt Sidebar & Suche
        this.menuOverlay.addEventListener("click", () => {
            this.element.dispatchEvent(new CustomEvent("search:close", { bubbles: true }));
        });
        // Suchfeld innerhalb des Drawers
        this.element.querySelector('[data-action="open-search"]')?.addEventListener("click", () => {
            this.menuOverlay.classList.add("is-visible");
        });
        domDelegateInstance.on('click', '[data-action="open-drawer"][data-drawer-id="sidebar-menu"]', () => SidebarMenu.open());
        document.addEventListener("theme:sidebarMenu:close", () => this.close());
        document.addEventListener("search:afteropen", () => this.menuOverlay.classList.add("is-visible"));
        document.addEventListener("search:afterclose", () => this.menuOverlay.classList.remove("is-visible"));
    }
    static async open() {
        this.drawer.open();
    }
    static close() {
        this.element.dispatchEvent(new CustomEvent("search:close", { bubbles: true }));
        this.drawer.close();
    }
}
SidebarMenu.initialized = false;
class Intercom {
    static init(domDelegateInstance) {
        domDelegateInstance.on('click', '[data-action="start-intercom-chat"]', async (e) => {
            const target = e.target;
            if (!window.__intercomReady && window.getCookie('intercomConversationStarted') !== '1')
                target.classList.add('loading');
            if (window.__intercomReady || window.getCookie('intercomConversationStarted') === '1') {
                window.Intercom('show');
                return;
            }
            await Intercom.inject();
            window.Intercom('onShow', () => {
                window.__intercomReady = true;
                window.setCookie('intercomConversationStarted', '1', '7d');
                target.classList.remove('loading');
            });
            window.Intercom('showNewMessage');
        });
        const handleResetIntercom = () => {
            window.setCookie(this.COOKIE_NAME, '', '-1d');
            if (window.getCookie('intercomConversationStarted') === '1') {
                this.inject(true).then();
            }
        };
        document.addEventListener('customer:login', handleResetIntercom);
        document.addEventListener('customer:logout', handleResetIntercom);
        // Load Intercom if it has been used before
        if (window.getCookie('intercomConversationStarted') === '1') {
            this.inject().then();
        }
    }
    static async inject(force = false) {
        if (window.__intercomScriptInjected && !force)
            return;
        window.__intercomScriptInjected = true;
        const APP_ID = this.APP_ID;
        //@ts-expect-error
        (function () { var w = window; var ic = w.Intercom; if (typeof ic === "function") {
            ic('update', w.intercomSettings);
        }
        else {
            var d = document;
            var i = function () { i.c(arguments); };
            i.q = [];
            i.c = function (args) { i.q.push(args); };
            w.Intercom = i;
            var l = function () { var s = d.createElement('script'); s.type = 'text/javascript'; s.async = true; s.src = 'https://widget.intercom.io/widget/' + APP_ID; var x = d.getElementsByTagName('script')[0]; x.parentNode.insertBefore(s, x); };
            if (document.readyState === 'complete') {
                l();
            }
            else if (w.attachEvent) {
                w.attachEvent('onload', l);
            }
            else {
                w.addEventListener('load', l, false);
            }
        } })();
        let intercom_user_jwt = window.getCookie(this.COOKIE_NAME);
        let session_duration = 86400000; // 24 hours
        if (intercom_user_jwt) {
            session_duration = JSON.parse(intercom_user_jwt).duration;
            intercom_user_jwt = JSON.parse(intercom_user_jwt).token;
        }
        else {
            try {
                const name = ((window.theme.customerFirstName || '') + ' ' + (window.theme.customerLastName || '')).trim();
                const res = await fetch(`${this.TOKEN_AUTH_URL}?name=${name}`, {
                    method: 'GET',
                });
                const json = await res.json();
                intercom_user_jwt = json.token;
                session_duration = json.duration;
                const cookie_duration = `${session_duration / 1000 / 60 / 24}d`;
                window.setCookie(this.COOKIE_NAME, JSON.stringify({
                    token: intercom_user_jwt,
                    duration: cookie_duration
                }), cookie_duration);
            }
            catch (e) {
                console.error(e);
            }
        }
        const intercomSettings = {
            api_base: "https://api-iam.intercom.io",
            app_id: this.APP_ID,
            intercom_user_jwt,
            session_duration
        };
        if (window.Shopify?.locale === 'it')
            intercomSettings.language_override = 'it';
        window.Intercom("boot", intercomSettings);
    }
}
Intercom.APP_ID = 'v4jaad07';
Intercom.COOKIE_NAME = 'intercom_user_jwt';
Intercom.TOKEN_AUTH_URL = '/apps/intercom-token';
class KlaviyoForm {
    static init(domDelegateInstance) {
        domDelegateInstance.on('submit', '.KlaviyoForm', async (e, formElement) => {
            e.preventDefault();
            const displaySubmitResult = (success) => {
                const errors = formElement.querySelectorAll('.Form__Alert.Alert--error');
                const successes = formElement.querySelectorAll('.Form__Alert.Alert--success');
                const inputContainer = formElement.querySelector('.signup_form_input');
                if (success) {
                    errors.forEach(el => el.classList.add('none'));
                    successes.forEach(el => el.classList.remove('none'));
                    inputContainer?.classList.add('none');
                }
                else {
                    errors.forEach(el => el.classList.remove('none'));
                }
            };
            const inputs = formElement.querySelectorAll('input[required]');
            let legal = true;
            inputs.forEach(input => {
                input.setCustomValidity('');
                if (!input.validity.valid) {
                    legal = false;
                    switch (input.name) {
                        case 'first_name':
                            input.setCustomValidity('Bitte gib deinen Vornamen ein');
                            break;
                        case 'email':
                            input.setCustomValidity(input.value === ''
                                ? 'Bitte gib deine E-Mail-Adresse ein'
                                : 'Bitte gib eine gültige E-Mail-Adresse ein');
                            break;
                        case 'privacycheck':
                            input.setCustomValidity('Bitte akzeptiere unsere Datenschutzerklärung');
                            break;
                    }
                }
            });
            if (!legal)
                return;
            const search = new URLSearchParams(location.search);
            const formData = new FormData(formElement);
            const actionType = formElement.dataset.submitAction;
            const coachingHandle = formData.get('e:coaching_handle');
            const profileProperties = {};
            const eventProperties = {};
            formData.forEach((value, key) => {
                if (key.startsWith('e:'))
                    eventProperties[key.slice(2)] = value;
                else if (key.startsWith('p:'))
                    profileProperties[key.slice(2)] = value;
            });
            if (coachingHandle) {
                profileProperties.utm_source = search.get('utm_source') || 'None';
                profileProperties.utm_medium = search.get('utm_medium') || 'None';
                profileProperties.utm_campaign = search.get('utm_campaign') || 'None';
            }
            let actionUrl;
            let bodyData;
            let onSuccess;
            if (actionType === 'event') {
                actionUrl = 'https://a.klaviyo.com/client/events/';
                bodyData = {
                    type: 'event',
                    attributes: {
                        properties: eventProperties,
                        metric: { data: { type: 'metric', attributes: { name: formElement.name } } },
                        profile: {
                            data: {
                                type: 'profile',
                                attributes: {
                                    email: formData.get('email'),
                                    first_name: formData.get('first_name'),
                                    properties: profileProperties
                                }
                            }
                        }
                    }
                };
                onSuccess = () => {
                    displaySubmitResult(true);
                    if (coachingHandle) {
                        const btn = formElement.querySelector('[data-form-button="proceed"]');
                        if (btn)
                            btn.href = `${window.Shopify.routes.root}blogs/${coachingHandle}`;
                    }
                };
            }
            else {
                actionUrl = 'https://a.klaviyo.com/client/subscriptions/';
                bodyData = {
                    type: 'subscription',
                    attributes: {
                        custom_source: formData.get('source') || location.pathname,
                        profile: {
                            data: {
                                type: 'profile',
                                attributes: {
                                    email: formData.get('email'),
                                    first_name: formData.get('first_name'),
                                    properties: profileProperties
                                }
                            }
                        }
                    },
                    relationships: {
                        list: { data: { type: 'list', id: formData.get('list_id') } }
                    }
                };
                onSuccess = () => {
                    displaySubmitResult(true);
                    const result = formElement.querySelector('.signup_result_message');
                    if (result)
                        result.textContent = 'Schaue in deine Emails und bestätige die Anmeldung.';
                };
            }
            formElement.classList.add('loading');
            try {
                const response = await fetch(`${actionUrl}?company_id=${window.theme.tracking.klaviyoKey}`, {
                    method: 'POST',
                    headers: {
                        revision: '2024-02-15',
                        Accept: 'application/json',
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ data: bodyData })
                });
                if (response.ok) {
                    onSuccess();
                }
                else {
                    displaySubmitResult(false);
                }
            }
            catch (err) {
                console.error(err);
                displaySubmitResult(false);
            }
            finally {
                formElement.classList.remove('loading');
            }
        });
    }
}
class EmbedManager {
    static init(domDelegateInstance) {
        domDelegateInstance.on('click', '[data-action="show-embed"]', (e) => {
            const placeholder = e.target.closest('.EmbedPlaceholder');
            if (placeholder)
                this.initEmbed(placeholder);
        });
    }
    static initEmbed(placeholder) {
        const temp = document.createElement('DIV');
        temp.innerHTML = placeholder.querySelector('.EmbedPlaceholder__Embed').innerHTML;
        const scripts = temp.querySelectorAll('script');
        for (let script of scripts) {
            const clone = this.cloneScriptNode(script);
            script.parentNode.replaceChild(clone, script);
        }
        for (let node of temp.childNodes) {
            placeholder.parentNode.insertBefore(node, placeholder);
        }
        placeholder.remove();
    }
    constructor() {
        const onConsentChange = (event) => {
            const e = event;
            if (!e.detail)
                return;
            this.initAllEmbeds();
            window.removeEventListener('theme:consent:change', onConsentChange);
        };
        if (this.hasConsent) {
            this.initAllEmbeds();
            return;
        }
        window.addEventListener('theme:consent:change', onConsentChange);
    }
    get hasConsent() {
        return Boolean(window.theme?.consent?.getTrackingConsent?.());
    }
    initAllEmbeds() {
        document.querySelectorAll('[data-action="show-embed"]').forEach((embedButton) => {
            const placeholder = embedButton.closest('.EmbedPlaceholder');
            if (placeholder)
                EmbedManager.initEmbed(placeholder);
        });
    }
}
EmbedManager.cloneScriptNode = (node) => {
    const script = document.createElement("script");
    script.innerText = node.innerHTML;
    for (let attr of node.attributes) {
        script.setAttribute(attr.name, attr.value);
    }
    return script;
};
class Collection {
    static init(domDelegateInstance) {
        domDelegateInstance.on('click', '.CollectionToolbar__Item', (e) => {
            const button = e.target.closest('button');
            if (!button)
                return;
            const popoverId = button.getAttribute('aria-controls');
            if (!popoverId)
                return;
            let popover = Popover.popovers[popoverId];
            if (!popover) {
                const sortProducts = async (sortBy, target) => {
                    const url = new URL(window.location.href);
                    url.searchParams.set('sort_by', sortBy);
                    window.history.replaceState(null, '', url.href);
                    await Collection.reloadProducts(sortBy);
                    Dom.getSiblings(target).forEach((el) => el.classList.remove('is-selected'));
                    target.classList.add('is-selected');
                    popover.close();
                };
                popover = new Popover(document.getElementById(popoverId), button, { onValueChanged: sortProducts });
            }
            popover.open('bottom');
        });
    }
    static async reloadProducts(sortBy) {
        document.dispatchEvent(new CustomEvent('theme:loading:start'));
        const collection = document.getElementById('all-products');
        const collectionInnerElement = collection.querySelector('.CollectionInner__Products');
        const toolbarElement = collection.querySelector('.CollectionToolbar');
        try {
            const url = `${location.pathname}?view=ajax${sortBy ? `&sort_by=${sortBy}` : ''}`;
            const response = await fetch(url, {
                credentials: 'same-origin',
                method: 'GET',
            });
            if (!response.ok) {
                console.error(`Request failed: ${response.status}`);
                return;
            }
            const content = await response.text();
            const temp = document.createElement('div');
            temp.innerHTML = content;
            const newSection = temp.querySelector('.shopify-section');
            if (!newSection) {
                console.error('No .shopify-section found in response');
                return;
            }
            collectionInnerElement.innerHTML = newSection.innerHTML;
            document.dispatchEvent(new CustomEvent('theme:collection:changed', { detail: collection }));
            const main = collection.querySelector('.CollectionMain');
            if (main) {
                const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
                let offset = main.getBoundingClientRect().top - headerHeight;
                if (Responsive.matchesBreakpoint('lap-and-up') &&
                    toolbarElement?.clientHeight === 0) {
                    offset -= 50;
                }
                if (offset < 0) {
                    window.scrollBy({ top: offset, behavior: 'smooth' });
                }
            }
        }
        catch (error) {
            console.error('Error updating collection:', error);
        }
        finally {
            document.dispatchEvent(new CustomEvent('theme:loading:end'));
        }
    }
    constructor() {
        this.filterAmount = 0;
        this.url = '';
        this.filterContainer = document.getElementById('collection-filter-drawer');
        if (!this.filterContainer)
            return;
        const settings = JSON.parse(this.filterContainer.parentElement.dataset.sectionSettings);
        this.url = settings.collectionUrl;
        // @ts-expect-error
        const domDelegateElement = new domDelegate.Delegate(this.filterContainer);
        domDelegateElement.on('click', '[data-action="toggle-tag"]', this.handleFilterValueClicked.bind(this));
        domDelegateElement.on('click', '[data-action="apply-tags"]', this.handleApplyFilter.bind(this));
        domDelegateElement.on('click', '[data-action="reset-tags"]', this.handleResetFilter.bind(this));
        document.addEventListener('shopify:section:unload', (event) => {
            const sectionEl = event.target;
            if (!sectionEl.classList.contains('shopify-section'))
                return;
            this.destroy();
        });
    }
    destroy() {
        delete Popover.popovers['table-of-contents-popover'];
    }
    handleFilterValueClicked(e) {
        const tag = e.target;
        Dom.getSiblings(tag.parentElement).forEach(tagContainer => {
            tagContainer.classList.remove('is-selected');
            const tag = tagContainer.firstChild;
            tag.classList.remove('is-active');
        });
        tag.classList.toggle('is-active');
        tag.parentElement.classList.toggle('is-selected');
    }
    async handleApplyFilter() {
        const currentTags = Array.from(this.filterContainer.querySelectorAll('.Link--inline.is-active')).map(button => button.dataset.tag);
        this.filterAmount = currentTags.length;
        const tags = currentTags.length > 0 ? currentTags.join('+') : '';
        const newUrl = window.location.protocol + '//' + window.location.host + this.url + `${tags ? '/' + tags : ''}`;
        window.history.replaceState(null, '', newUrl);
        await Collection.reloadProducts();
        this.updateFilterCount();
        Drawer.drawers['collection-filter-drawer'].close();
        if (this.filterAmount) {
            const resetButton = this.filterContainer.querySelector('[data-action="reset-tags"]');
            resetButton.style.display = 'block';
            resetButton.classList.remove('none');
        }
    }
    async handleResetFilter(e) {
        this.filterContainer.querySelectorAll('li.Linklist__Item.is-selected').forEach(li => {
            li.classList.remove('is-selected');
            li.firstChild.classList.remove('is-active');
        });
        await this.handleApplyFilter();
        e.target.classList.add('none');
        //TODO: remove button focus
    }
    updateFilterCount() {
        const filterButton = document.querySelector('.CollectionToolbar__Item--filter');
        if (!filterButton)
            return;
        filterButton.innerText = filterButton.dataset.text;
        if (this.filterAmount) {
            const span = document.createElement('span');
            span.classList.add('Text--subdued');
            span.innerText = ` (${this.filterAmount})`;
            filterButton.appendChild(span);
        }
    }
}
class CustomerStories {
    constructor(element) {
        this.element = element;
        this.grid = this.element.querySelector('.CustomerStoryGrid__Container--quotes');
        if (this.grid)
            this.initVideoListeners();
    }
    initVideoListeners() {
        this.grid.addEventListener('theme:wistiaplayer:inserted', e => {
            const player = e.target;
            if (!player)
                return;
            const gridItem = player.closest('.CustomerStoryGrid__Item');
            if (!gridItem)
                return;
            const quote = gridItem.querySelector('.CustomerStoryGrid__ItemQuote');
            const name = gridItem.querySelector('.NameLabel');
            const playButton = gridItem.querySelector('.CustomerStoryGrid__ItemPlayButton');
            if (playButton)
                playButton.classList.remove('none');
            const onPlay = () => {
                if (quote)
                    quote.style.setProperty('display', 'none', 'important');
                if (name)
                    name.style.setProperty('display', 'none', 'important');
                if (playButton)
                    playButton.classList.add('none');
            };
            player.addEventListener('play', onPlay);
            const onPause = () => {
                if (quote)
                    quote.style.removeProperty('display');
                if (name)
                    name.style.removeProperty('display');
            };
            player.addEventListener('pause', onPause);
        });
    }
}
class Recipe {
    constructor() {
        this.initialPortionSize = 1;
        this.portionSize = 1;
        this.ingredientsContainer = document.querySelector('.Recipe__Ingredients');
        if (!this.ingredientsContainer)
            return;
        this.input = this.ingredientsContainer.querySelector('.QuantitySelector__Input');
        this.minusButton = this.ingredientsContainer.querySelector('[data-action="decrease-quantity"]');
        this.plusButton = this.ingredientsContainer.querySelector('[data-action="increase-quantity"]');
        this.initialPortionSize = +this.input.value;
        this.portionSize = this.initialPortionSize;
        this.plusButton.addEventListener('click', this.increasePortionSize.bind(this));
        this.minusButton.addEventListener('click', this.decreasePortionSize.bind(this));
        this.input.addEventListener('input', this.changePortionSize.bind(this));
    }
    decreasePortionSize() {
        if (this.portionSize <= 1)
            return;
        this.portionSize--;
        this.updatePortionSize();
    }
    increasePortionSize() {
        this.portionSize++;
        this.updatePortionSize();
    }
    changePortionSize(e) {
        const newPortionSize = +e.target.value;
        if (!isNaN(newPortionSize) || newPortionSize > 0) {
            this.portionSize = newPortionSize;
        }
        this.updatePortionSize();
    }
    updatePortionSize() {
        this.input.value = `${this.portionSize}`;
        this.minusButton.disabled = this.portionSize <= 1;
        this.ingredientsContainer.querySelectorAll('[data-ingr-amount]').forEach((elem) => {
            elem.innerText = (+(elem.dataset.ingrAmount) * this.portionSize / this.initialPortionSize).toLocaleString();
        });
    }
}
class DynamicPopover {
    static init(domDelegateInstance) {
        domDelegateInstance.on('click', '.DynamicPopoverActivator', async (e) => {
            const popoverId = 'dynamic-popover';
            const dynamicPopover = document.getElementById(popoverId);
            if (!dynamicPopover)
                return;
            const target = e.target;
            if (target.hasAttribute('disabled'))
                return;
            const selectionHandler = (value, button, activator, popoverInstance) => {
                const handleOnCartAdded = () => {
                    popoverInstance.close();
                };
                const variantId = button.getAttribute('data-variant-id');
                if (activator.hasAttribute('data-type') && activator.getAttribute('data-type') === 'loyaltyslider') {
                    const loyaltyButton = activator.closest('.ClubProduct').querySelector('[data-lion-seamless-product-reward]');
                    if (loyaltyButton && window.loyaltylion) {
                        loyaltyButton.setAttribute('data-variant-id', variantId);
                        window.loyaltylion.ui.refresh();
                        // LL re-renders the inner button asynchronously — poll until it's active before clicking
                        const timeout = 5000;
                        const start = Date.now();
                        const clickWhenReady = () => {
                            const btn = loyaltyButton.querySelector('.lion-in-cart-reward__button[data-lion-active]');
                            if (btn) {
                                btn.click();
                                return;
                            }
                            if (Date.now() - start < timeout) {
                                setTimeout(clickWhenReady, 100);
                            }
                            else {
                                console.warn('Loyaltyslider: Refreshed LL button never became active within timeout');
                            }
                        };
                        clickWhenReady();
                    }
                    else {
                        console.warn('Loyaltyslider: Variant selected but no loyalty button found to trigger add to cart process.');
                    }
                }
                else {
                    window.Cart.add([{ id: +variantId, quantity: 1 }], handleOnCartAdded);
                }
            };
            const popoverInstance = new Popover(dynamicPopover, target, { isDrawer: true, onValueChanged: selectionHandler });
            popoverInstance.open();
            popoverInstance.setLoading();
            const handle = target.getAttribute('data-handle');
            if (!handle) {
                console.warn('No data-handle attribute found on target');
                return;
            }
            const collectionHandle = target.getAttribute('data-collection-handle');
            const selectionURL = `${window.Shopify.routes.root}${collectionHandle ? `collections/${collectionHandle}/` : ''}products/${handle}?view=variantselection`;
            try {
                const response = await fetch(selectionURL, {
                    credentials: 'same-origin',
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                });
                if (!response.ok) {
                    const content = await response.json().catch(() => ({}));
                    console.warn('Problem loading popover ajax content', content.description);
                    return;
                }
                const dynamicPopoverContainer = dynamicPopover.querySelector('.Popover__Content');
                dynamicPopoverContainer.innerHTML = await response.text();
                // @ts-expect-error
                const dynamicPopoverContainerDelegator = new domDelegate.Delegate(dynamicPopoverContainer.parentNode);
                dynamicPopoverContainerDelegator.on('click', 'button.Popover__Value', async (e) => {
                    const button = e.target.closest('button');
                    if (!button || button.dataset.availability && button.dataset.availability !== 'available')
                        return;
                    popoverInstance.popover.classList.add('loading');
                });
                document.dispatchEvent(new CustomEvent('theme:tracking:init', { detail: { element: dynamicPopoverContainer } }));
                Animation.slideDown(dynamicPopoverContainer);
                document.dispatchEvent(new CustomEvent('theme:loading:end'));
            }
            catch (error) {
                console.error('There was a problem retrieving ajax popover content', error);
            }
        });
    }
}
class ModalLoading {
    static init(domDelegateInstance) {
        domDelegateInstance.on('click', '[data-modal-loading]', (e, target) => {
            e.preventDefault();
            const modal = document.getElementById(target.getAttribute('data-modal-loading'));
            if (modal)
                ModalLoading.showModalLoading(modal, target);
        });
    }
}
ModalLoading.showModalLoading = (modal, activator) => {
    if (!modal || !modal.hasAttribute('data-modal-loading-config')) {
        console.warn('[ModalLoading] Missing configuration data attribute on element', modal);
        return;
    }
    const data = JSON.parse(modal.getAttribute('data-modal-loading-config'));
    const pageOverlay = document.querySelector('.PageOverlay--primary');
    const hideLoading = () => {
        if (activator && activator.hasAttribute('data-href')) {
            const url = activator.getAttribute('data-href');
            if (activator.tagName === 'A' && activator.target === '_blank') {
                window.pageOverlay.close();
                modal.setAttribute('aria-hidden', 'true');
                window.open(url, '_blank');
            }
            else {
                window.addEventListener('pageshow', (e) => {
                    if (e.persisted) {
                        window.pageOverlay.close();
                        modal.setAttribute('aria-hidden', 'true');
                    }
                }, { once: true });
                window.location.href = url;
            }
        }
        else {
            window.pageOverlay.close();
            if (data.initial && pageOverlay) {
                pageOverlay.style.opacity = '';
                pageOverlay.style.transition = '';
                pageOverlay.classList.remove('PageOverlay--survey');
            }
            modal.setAttribute('aria-hidden', 'true');
        }
    };
    if (!data.loadingTime || !data.successTime) {
        console.warn(`[ModalLoading] Missing metafield data for loading time (${data.loadingTime}) or success time (${data.successTime})`);
        hideLoading();
        return;
    }
    window.pageOverlay.open(modal.id);
    modal.setAttribute('aria-hidden', 'false');
    const loadingContainer = modal.querySelector('.LoadingContainer');
    const textLoading = modal.querySelector('.LoadingText--loading');
    const textSuccess = modal.querySelector('.LoadingText--success');
    loadingContainer?.classList.remove('none');
    textLoading?.classList.remove('none');
    textSuccess?.classList.add('none');
    setTimeout(() => {
        loadingContainer?.classList.add('none');
        textLoading?.classList.add('none');
        textSuccess?.classList.remove('none');
        setTimeout(hideLoading, data.successTime);
    }, data.loadingTime);
    if (data.initial) {
        window.setCookie(`theme_hide_${location.pathname.replace(/.*\//, '')}_${modal.id}`, 'true');
    }
};
class ThemeTracking {
    constructor() {
        this.domDelegateInstance = new window.domDelegate.Delegate(document);
        this.observedElements = [];
        this.seenElements = new Set();
        this.intervalId = null;
        this.processEvent = (data) => {
            if (!data || !data.event)
                return;
            data.document = this.generateDocumentData();
            data.template = this.generateTemplateData();
            const events = this.processSecondLayer(data);
            events.forEach((evt) => {
                const name = evt.event;
                const payload = { ...evt };
                delete payload.event;
                this.processThirdParties(name, payload);
                if (!['page_viewed', 'form_submitted'].includes(name)) {
                    window.Shopify?.analytics?.publish?.(name, payload);
                }
            });
        };
        const initialQueue = Array.isArray(window.themeLayer)
            ? window.themeLayer
            : [];
        window.themeLayer = [];
        Object.defineProperty(window.themeLayer, 'push', {
            configurable: true,
            enumerable: false,
            writable: true,
            value: (...items) => {
                items.forEach((i) => this.processEvent(i));
                return items.length;
            },
        });
        initialQueue.forEach((entry) => this.processEvent(entry));
        this.intersectionObserver = new IntersectionObserver((entries) => this.onIntersections(entries), { rootMargin: '-20% 0px -20% 0px', threshold: 0 });
        // Allow initializing elements after dom changes
        document.addEventListener('theme:tracking:init', this.initElement.bind(this));
        this.initPageTracking();
        this.initCartTracking();
        this.initConsentTracking();
        this.initClickTracking();
        this.initFormTracking();
        this.initElement();
    }
    initElement(e) {
        this.observedElements = this.observedElements.filter((el) => {
            if (el && el.isConnected)
                return true;
            this.intersectionObserver.unobserve(el);
            return false;
        });
        const root = e?.detail?.element ?? document;
        this.initSectionTracking(root);
        this.initVideoTracking(root);
    }
    initCartTracking() {
        if (window.theme.template.name === 'cart') {
            window.themeLayer.push({
                event: 'cart_visited'
            });
        }
        else {
            document.addEventListener('theme:drawer:opened', e => {
                if (e.detail?.id !== 'sidebar-cart')
                    return;
                window.themeLayer.push({
                    event: 'cart_visited'
                });
            });
        }
    }
    initPageTracking() {
        window.themeLayer.push({ event: 'page_viewed' });
        const minScrollPercentage = 75;
        const minEngagementTime = 15; // seconds
        const minContentViewedTime = 60; // seconds
        let scrollPercentageReached = false;
        let activeTime = 0;
        let engagedFired = false;
        let contentViewedFired = false;
        const fireIfNeeded = () => {
            if (!contentViewedFired && scrollPercentageReached && activeTime >= minContentViewedTime) {
                window.themeLayer.push({ event: 'content_viewed' });
                contentViewedFired = true;
            }
            if (!engagedFired && activeTime >= minEngagementTime) {
                window.themeLayer.push({ event: 'engaged' });
                engagedFired = true;
            }
        };
        const onScroll = () => {
            const scrollable = document.documentElement.scrollHeight - window.innerHeight;
            const progress = scrollable > 0
                ? Math.min(100, (window.scrollY / scrollable) * 100)
                : 100;
            if (progress >= minScrollPercentage) {
                scrollPercentageReached = true;
                window.removeEventListener('scroll', onScroll);
                fireIfNeeded();
            }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        const tick = () => {
            if (document.visibilityState === 'visible') {
                activeTime++;
                fireIfNeeded();
                if (activeTime >= Math.max(minEngagementTime, minContentViewedTime)) {
                    if (this.intervalId !== null) {
                        window.clearInterval(this.intervalId);
                        this.intervalId = null;
                    }
                }
            }
        };
        this.intervalId = window.setInterval(tick, 1000);
    }
    initConsentTracking() {
        window.addEventListener('theme:consent:change', (e) => {
            const consent = !!e.detail;
            window.themeLayer.push({
                event: 'consent_updated',
                consent: {
                    marketingAllowed: consent,
                    saleOfDataAllowed: consent,
                    analyticsAllowed: consent,
                    preferencesAllowed: consent,
                },
            });
        });
    }
    initClickTracking() {
        this.domDelegateInstance.on('click', 'a, button', (_, target) => {
            window.themeLayer.push({
                event: 'element_clicked',
                section: this.generateSectionData(target.closest('[data-section-type]')),
                element: this.generateElementData(target),
            });
        });
    }
    initFormTracking() {
        this.domDelegateInstance.on('submit', 'form:not([data-form-tracking="none"])', (_, target) => {
            window.themeLayer.push({
                event: 'form_submitted',
                section: this.generateSectionData(target.closest('[data-section-type]')),
                form: this.generateFormData(target)
            });
        });
    }
    initSectionTracking(root) {
        const sections = (root instanceof Document ? root : root).querySelectorAll('[data-section-type]:not([data-tracking-initialized])');
        sections.forEach((section) => {
            if (!this.observedElements.includes(section)) {
                this.observedElements.push(section);
                this.intersectionObserver.observe(section);
                this.initBlockTracking(section);
            }
            section.dataset.trackingInitialized = 'true';
        });
    }
    initVideoTracking(root) {
        const players = (root instanceof Document
            ? root
            : root).querySelectorAll('wistia-player:not([data-tracking-initialized])');
        players.forEach((player) => {
            const onPlay = () => {
                window.themeLayer.push({
                    event: 'video_started',
                    section: this.generateSectionData(player.closest('[data-section-type]')),
                    video: this.generateVideoData(player),
                });
                player.removeEventListener('play', onPlay);
            };
            const onSecondChange = () => {
                window.themeLayer.push({
                    event: 'video_progressed',
                    section: this.generateSectionData(player.closest('[data-section-type]')),
                    video: this.generateVideoData(player),
                });
            };
            player.addEventListener('play', onPlay);
            player.addEventListener('second-change', onSecondChange);
            player.dataset.trackingInitialized = 'true';
        });
    }
    generateVideoData(player) {
        const closestVslWrapper = player.closest?.('[data-progress-based-content-time]');
        const duration = Number(player.duration) || 0;
        const currentTime = Number(player.currentTime) || 0;
        const currentPercent = duration > 0 ? currentTime / duration : 0;
        return {
            name: player.name || '',
            id: player.mediaId || '',
            duration,
            currentTime,
            currentPercent,
            percentWatched: player.percentWatched || 0,
            vsl: !!closestVslWrapper,
            vslProgressBasedContentReached: closestVslWrapper
                ? parseInt(closestVslWrapper.getAttribute('data-progress-based-content-time') || '0') <= currentTime
                : false,
        };
    }
    initBlockTracking(section) {
        const blocks = section.querySelectorAll('[data-block-type]:not([data-tracking-initialized])');
        blocks.forEach((block, index) => {
            if (this.observedElements.indexOf(block) === -1) {
                if (!block.dataset.blockPosition) {
                    block.dataset.blockPosition = (index + 1).toString();
                }
                this.observedElements.push(block);
                this.intersectionObserver.observe(block);
            }
            block.dataset.trackingInitialized = 'true';
        });
    }
    generateFormData(form) {
        if (!form)
            return null;
        const elements = Array.from(form.elements);
        return {
            id: form.id || '',
            action: form.action || '',
            name: form.name || '',
            elements: elements.map((element) => ({
                id: element.id || '',
                name: element.name || '',
                type: element.type || '',
                value: element instanceof HTMLInputElement && element.type === 'checkbox'
                    ? element.checked
                    : element.value || '',
                tagName: element.tagName
            }))
        };
    }
    generateDocumentData() {
        return {
            location: {
                hash: location.hash,
                host: location.host,
                hostname: location.hostname,
                href: location.href,
                origin: location.origin,
                pathname: location.pathname,
                search: location.search,
            },
            referrer: document.referrer,
            title: document.title,
        };
    }
    generateTemplateData() {
        const t = window.theme?.template ?? {};
        return {
            name: t.name || '',
            suffix: t.suffix || '',
        };
    }
    processSecondLayer(data) {
        const transforms = window.theme.tracking.secondLayerTransforms;
        if (!transforms)
            return [data];
        return [data].concat(transforms.reduce((acc, transform) => {
            try {
                if (transform.event !== data.event)
                    return acc;
                const clone = JSON.parse(JSON.stringify(data));
                if (!transform.condition(clone))
                    return acc;
                const subevent = typeof transform.subevent === 'function'
                    ? transform.subevent(clone)
                    : transform.subevent;
                acc.push({ event: `${clone.event}__${subevent}` });
            }
            catch (err) {
                console.error('[Theme Tracking] Error processing second layer transform', err);
            }
            return acc;
        }, []));
    }
    processThirdParties(eventName, data) {
        const thirdPartyEvents = window.theme?.tracking?.thirdPartyEvents ?? [];
        thirdPartyEvents.forEach((tp) => {
            try {
                if (tp.event !== eventName)
                    return;
                const clone = JSON.parse(JSON.stringify(data));
                if (tp.condition && !tp.condition(clone))
                    return;
                const serviceEventName = tp.serviceEvent || eventName;
                const serviceData = tp.data ? tp.data(clone) : undefined;
                switch (tp.service) {
                    case 'facebook':
                        if (typeof window.fbq === 'function') {
                            window.fbq(tp.serviceNative ? 'track' : 'trackCustom', serviceEventName, serviceData);
                        }
                        break;
                    case 'admetrics':
                        window.admDataLayer = window.admDataLayer || [];
                        window.admDataLayer.push({ name: serviceEventName, data: serviceData || {} });
                        break;
                    case 'kameleoon':
                        window.kameleoonQueue = window.kameleoonQueue || [];
                        window.kameleoonQueue.push([
                            'Kameleoon.API.Goals.processConversion',
                            serviceEventName,
                        ]);
                        break;
                }
            }
            catch (err) {
                console.error('[Theme Tracking] Third-party dispatch error', err);
            }
        });
    }
    onIntersections(entries) {
        entries.forEach((entry) => {
            if (!entry.isIntersecting)
                return;
            const el = entry.target;
            if (el.getAttribute('aria-hidden') === 'true')
                return;
            const sectionType = el.dataset.sectionType;
            const blockType = el.dataset.blockType;
            if (sectionType) {
                const key = `section:${sectionType}:${el.dataset.sectionPosition}`;
                if (!this.seenElements.has(key)) {
                    this.seenElements.add(key);
                    window.themeLayer.push({
                        event: 'section_viewed',
                        section: this.generateSectionData(el),
                    });
                }
            }
            else if (blockType) {
                const key = `block:${blockType}:${el.dataset.blockPosition}`;
                if (!this.seenElements.has(key)) {
                    this.seenElements.add(key);
                    window.themeLayer.push({
                        event: 'block_viewed',
                        section: this.generateSectionData(el.closest('[data-section-type]')),
                        block: this.generateBlockData(el),
                    });
                }
            }
            this.intersectionObserver.unobserve(el);
            const index = this.observedElements.indexOf(el);
            if (index !== -1) {
                this.observedElements.splice(index, 1);
            }
        });
    }
    generateSectionData(section) {
        if (!section)
            return null;
        return {
            type: section.dataset.sectionType || '',
            position: parseInt(section.dataset.sectionPosition || '-1'),
        };
    }
    generateBlockData(block) {
        return {
            type: block.dataset.blockType || '',
            position: parseInt(block.dataset.blockPosition || '-1')
        };
    }
    generateElementData(element) {
        if (!element)
            return null;
        const urlObject = element.href && !element.href.includes('javascript:void') ? new URL(element.href) : element.hasAttribute('data-href') ? new URL(element.getAttribute('data-href')) : null;
        return {
            id: element.id || '',
            text: element.innerText || '',
            name: element.getAttribute('aria-label') || element.innerText || '',
            type: element.href ? 'link' : 'button',
            url: urlObject ? {
                hash: urlObject.hash,
                host: urlObject.host,
                hostname: urlObject.hostname,
                href: urlObject.href,
                origin: urlObject.origin,
                pathname: urlObject.pathname,
                search: urlObject.search
            } : null,
            dataset: {
                action: element.getAttribute('data-action') || ''
            },
            outbound: urlObject ? urlObject.hostname !== location.hostname : false,
        };
    }
}
class SliderContainer {
    static initAllSliders() {
        const allSliders = new Map();
        document.querySelectorAll('.Slider').forEach((el) => {
            allSliders.set(el, new Slider(el));
        });
        document.addEventListener(ThemeEditor.SHOPIFY_SECTION_LOAD, (event) => {
            const sectionEl = event.target;
            if (!sectionEl.querySelector('.Slider'))
                return;
            sectionEl.querySelectorAll('.Slider').forEach((el) => {
                allSliders.set(el, new Slider(el));
            });
        });
        document.addEventListener(ThemeEditor.SHOPIFY_SECTION_UNLOAD, (event) => {
            const sectionEl = event.target;
            if (!sectionEl.querySelector('.Slider'))
                return;
            sectionEl.querySelectorAll('.Slider').forEach((el) => {
                const slider = allSliders.get(el);
                if (slider)
                    slider.disable();
                allSliders.delete(el);
            });
        });
    }
}
class ProductImageSlider {
    static init(container, options, variantData) {
        const htmlSlider = container.querySelector('.Product__Slideshow .Slider');
        if (!htmlSlider)
            return;
        const htmlSliderContent = htmlSlider.querySelectorAll('.Product__SlideItem');
        if (htmlSliderContent.length === 0)
            return;
        this.productSlideshow = new Slider(htmlSlider);
        this.allSlides = Array.from(htmlSlider.querySelectorAll('.Slider__Slide'));
        this.navList = container.querySelector('.Product__SlideshowNavList');
        if (this.navList) {
            this.allNavItems = Array.from(this.navList.querySelectorAll('.Product__SlideshowNavItem'));
            this.navDelegate = new window.domDelegate.Delegate(this.navList);
            this.navDelegate.on('click', '[data-action="select-product-media"]', this.onNavItemSelect.bind(this));
        }
        // Only display variant images
        const variantSelections = container.querySelectorAll('.ProductForm__Option .ProductForm__Item');
        if (variantSelections.length > 0) {
            const selectedValues = Array.from(variantSelections)
                .map(elem => {
                const popoverId = elem.getAttribute('aria-controls');
                const selectedButton = container.querySelector(`#${popoverId} .Popover__ValueList button.is-selected`);
                return selectedButton?.dataset.value || null;
            })
                .filter(Boolean);
            this.variantGroup = selectedValues.join(' / ');
        }
        else {
            const selectedRadio = document.querySelector('.ProductForm__Variants input[type="radio"][checked="checked"]:not([name="advantage-package-pseudo-variant"])');
            this.variantGroup = selectedRadio?.value || '';
        }
        if (!variantData || variantData.length <= 1) {
            const hiddenSlides = this.allSlides.filter((slide) => slide.getAttribute('data-media-type') === 'video');
            const hiddenNavItems = this.allNavItems.filter((navItem) => navItem.getAttribute('data-media-type') === 'video');
            if (hiddenSlides.length > 0) {
                hiddenSlides.forEach((slide) => this.productSlideshow.removeSlide(slide));
                if (hiddenNavItems.length > 0)
                    hiddenNavItems.forEach((navItem) => navItem.remove());
                this.reloadSlider();
            }
            else {
                const currentPosition = this.productSlideshow.slides[this.productSlideshow.currentIndex ?? 0].dataset.mediaPosition;
                this.selectNavItem(currentPosition);
            }
        }
        else {
            this.allSlides.forEach((slide) => this.productSlideshow.removeSlide(slide));
            this.allNavItems.forEach((navItem) => navItem.remove());
        }
        if (this.variantGroup === '' && variantData[0]) {
            this.variantGroup = variantData[0].title;
        }
        if (this.variantGroup !== 'Default Title') {
            this.reloadSlider();
        }
        if (this.navList)
            this.navList.classList.remove('is-initializing');
        this.productSlideshow.enable();
        this.productSlideshow.element.addEventListener('theme:slider:slidechanged', e => {
            const previousSlide = e.detail.previousSlide;
            const currentSlide = e.detail.currentSlide;
            if (previousSlide === currentSlide)
                return;
            this.onSlideLeave(previousSlide);
            this.onSlideEnter(currentSlide);
        });
        // Enable adding slides from 3rd party scripts
        this.productSlideshow.element.addEventListener('theme:slider:reload', (e) => {
            if (e.detail && e.detail.videoVisible !== undefined) {
                this.videoVisible = e.detail.videoVisible;
            }
            const sliderSlides = this.productSlideshow.element.querySelectorAll('.Slider__Slide');
            for (const slide of sliderSlides) {
                if (!this.allSlides.includes(slide)) {
                    this.allSlides.push(slide);
                }
            }
            if (this.navList) {
                const sliderNavItems = this.navList.querySelectorAll('.Product__SlideshowNavItem');
                for (const navItem of sliderNavItems) {
                    if (!this.allNavItems.includes(navItem)) {
                        this.allNavItems.push(navItem);
                    }
                }
            }
            this.reloadSlider();
        });
    }
    static reloadSlider(variantGroup) {
        if (variantGroup !== undefined)
            this.variantGroup = variantGroup;
        this.productSlideshow.disable();
        // Sort slides by image type and variant group
        const mediaSort = (a, b) => {
            const imageTypeA = a.dataset.mediaSubtype ?? null;
            const imageTypeB = b.dataset.mediaSubtype ?? null;
            if (imageTypeA === imageTypeB) {
                const groupIndexA = !a.dataset.mediaVariantGroup ? 4 : a.dataset.mediaVariantGroup === 'all' ? 3 : a.dataset.mediaVariantGroup.includes(':') ? 2 : 1;
                const groupIndexB = !b.dataset.mediaVariantGroup ? 4 : b.dataset.mediaVariantGroup === 'all' ? 3 : b.dataset.mediaVariantGroup.includes(':') ? 2 : 1;
                if (groupIndexA === groupIndexB) {
                    return parseInt(a.dataset.mediaPosition || '0') - parseInt(b.dataset.mediaPosition || '0');
                }
                return groupIndexA - groupIndexB;
            }
            return this.mediaSubtypeOrder.indexOf(imageTypeA) - this.mediaSubtypeOrder.indexOf(imageTypeB);
        };
        this.allSlides.sort(mediaSort);
        if (this.navList)
            this.allNavItems.sort(mediaSort);
        this.allSlides.forEach((slide, index) => {
            slide.dataset.mediaPosition = (index + 1).toString();
            if (this.navList)
                this.allNavItems[index].dataset.mediaPosition = (index + 1).toString();
        });
        this.allSlides.forEach((slide, index) => {
            this.onSlideLeave(slide);
            this.productSlideshow.removeSlide(slide);
            if (this.navList)
                this.allNavItems[index].remove();
        });
        this.allSlides.forEach((slide, index) => {
            if (!this.videoVisible && slide.getAttribute('data-media-type') === 'video')
                return;
            const slideVariantGroup = slide.dataset.mediaVariantGroup;
            if (slideVariantGroup === this.variantGroup) {
                this.productSlideshow.addSlide(slide);
                if (this.navList)
                    this.navList.appendChild(this.allNavItems[index]);
            }
            else if (slideVariantGroup?.includes(':')) {
                const slideVariantGroupName = slideVariantGroup.split(':')[1];
                if (this.variantGroup.split('/').find(groupPart => groupPart.trim() === slideVariantGroupName)) {
                    this.productSlideshow.addSlide(slide);
                    if (this.navList)
                        this.navList.appendChild(this.allNavItems[index]);
                }
            }
            else if (slideVariantGroup === this.variantGroup || (slideVariantGroup === 'all')) {
                this.productSlideshow.addSlide(slide);
                if (this.navList)
                    this.navList.appendChild(this.allNavItems[index]);
            }
        });
        this.productSlideshow.enable();
        this.productSlideshow.init();
        if (this.navList && this.productSlideshow.slides.length) {
            this.navList.closest('.Product__SlideshowNav')?.dispatchEvent(new CustomEvent('theme:hsl:refresh', { bubbles: true }));
            const currentPosition = this.productSlideshow.slides[0].dataset.mediaPosition;
            this.selectNavItem(currentPosition);
        }
    }
    static onSlideEnter(slide) {
        if (!this.navList)
            return;
        const slidePosition = slide.dataset.mediaPosition;
        this.selectNavItem(slidePosition);
    }
    static onSlideLeave(slide) {
        if (slide && slide.getAttribute('data-media-type') === 'video') {
            const player = slide.querySelector('wistia-player');
            player?.pause();
        }
    }
    static selectNavItem(position) {
        if (!this.navList)
            return;
        this.navList.querySelectorAll('.Product__SlideshowNavItem').forEach(navItem => {
            if (navItem.dataset.mediaPosition === position) {
                navItem.classList.add('is-selected');
            }
            else {
                navItem.classList.remove('is-selected');
            }
        });
    }
    static onNavItemSelect(e, target) {
        if (!this.navList)
            return;
        const position = target.dataset.mediaPosition;
        const slideIndex = this.productSlideshow.slides.findIndex(slide => slide.dataset.mediaPosition === position);
        if (slideIndex === -1)
            return;
        this.productSlideshow.select(slideIndex);
        this.selectNavItem(position);
    }
    static destroy() {
        this.productSlideshow.disable();
        if (this.navDelegate)
            this.navDelegate.off();
    }
}
ProductImageSlider.mediaSubtypeOrder = ['default', 'nutrients', 'presentation', 'detail', 'video', 'none', null];
ProductImageSlider.navList = null;
ProductImageSlider.navDelegate = null;
ProductImageSlider.videoVisible = true;
class Article {
    static initAllArticles() {
        const allTableOfContents = new Map();
        document.querySelectorAll('.ArticleBody').forEach((articleBody) => {
            allTableOfContents.set(articleBody, new TableOfContents(Dom.nodeListToArray(document.querySelectorAll('.TableOfContents')), articleBody, { activeSelector: '.Linklist__Item' }));
        });
        document.addEventListener(ThemeEditor.SHOPIFY_SECTION_LOAD, (event) => {
            const sectionEl = event.target;
            if (!sectionEl.querySelector('.ArticleBody'))
                return;
            sectionEl.querySelectorAll('.ArticleBody').forEach((articleBody) => {
                allTableOfContents.set(articleBody, new TableOfContents(Dom.nodeListToArray(document.querySelectorAll('.TableOfContents')), articleBody, { activeSelector: '.Linklist__Item' }));
            });
        });
        document.addEventListener(ThemeEditor.SHOPIFY_SECTION_UNLOAD, (event) => {
            const sectionEl = event.target;
            if (!sectionEl.querySelector('.ArticleBody'))
                return;
            sectionEl.querySelectorAll('.ArticleBody').forEach((el) => {
                const tableOfContents = allTableOfContents.get(el);
                if (tableOfContents)
                    tableOfContents.destroy();
                allTableOfContents.delete(el);
            });
        });
    }
}
class AnchorScrolling {
    static init(delegation) {
        delegation.on('click', '[href^="#"]', function (e, target) {
            e.preventDefault();
            const selector = target.hasAttribute('href')
                ? target.getAttribute('href')
                : target.getAttribute('data-href');
            if (!selector)
                return;
            // Determine path and handle invalid or external anchors
            let selectorPath = null;
            if (selector.includes('#'))
                selectorPath = selector.split('#')[0];
            if (selectorPath && selectorPath.includes('?'))
                selectorPath = selectorPath.split('?')[0];
            const isRoot = location.pathname === window.Shopify?.routes?.root;
            const isExternalPath = selectorPath &&
                (!selectorPath.includes(location.pathname) ||
                    (isRoot && selectorPath !== window.Shopify?.routes?.root));
            if (selector === '#' ||
                selector === '#main' ||
                isExternalPath) {
                return;
            }
            // Ensure selector starts with '#'
            let anchorSelector = selector;
            if (selector.includes('#') && !selector.startsWith('#')) {
                anchorSelector = `#${selector.split('#')[1]}`;
            }
            const element = document.querySelector(anchorSelector);
            if (!element)
                return;
            const offset = parseInt(target.getAttribute('data-offset') || '0', 10);
            // Optional: Handle focus
            if (target.hasAttribute('data-focus-on-click')) {
                const prevScrollY = window.pageYOffset;
                element.focus({ preventScroll: true });
                // Fix scroll jump after focusing
                if (window.pageYOffset !== prevScrollY) {
                    window.scrollTo(window.pageXOffset, prevScrollY);
                }
                element.focus();
            }
            // Calculate element offset top
            const getOffsetTop = (elem) => {
                let offsetTop = 0;
                let current = elem;
                while (current) {
                    offsetTop += current.offsetTop;
                    current = current.offsetParent;
                }
                return offsetTop;
            };
            window.scrollTo({
                behavior: 'smooth',
                top: getOffsetTop(element) - offset,
            });
            history.replaceState(null, '', anchorSelector);
            element.setAttribute('tabindex', '-1');
            element.focus({ preventScroll: true });
        });
    }
}
class ReferenceList {
    static init(domDelegateInstance) {
        const bibContainer = document.querySelector('.bibliography-global');
        if (!bibContainer)
            return;
        const entries = document.querySelectorAll('[data-bib-title]');
        const bibWrapper = bibContainer.closest('.ReferenceListWrapper');
        const bibToggle = bibWrapper?.querySelector('[data-action="toggle-collapsible"], [data-action="open-drawer"]');
        let html = '', index = 1;
        const liEl = bibContainer.querySelector('li');
        if (!liEl)
            return;
        const template = liEl.outerHTML;
        const referenceIndices = {};
        entries.forEach(entry => {
            const title = entry.dataset.bibTitle;
            let source = entry.dataset.bibSource, entryIndex;
            if (referenceIndices[title] !== undefined) {
                entryIndex = referenceIndices[title];
            }
            else {
                entryIndex = index;
                referenceIndices[title] = index;
                let templateInstance = template.replaceAll('$number', String(index)).replace('$title', title);
                if (source) {
                    source = source.replace(/#.*$/, ''); // Remove hashes because there is a bug where links break and only scroll to top
                    templateInstance = templateInstance.replace(/href="#"/, 'href="' + source + '"');
                }
                else {
                    templateInstance = templateInstance.replace('ReferenceList__Item ', 'ReferenceList__Item ReferenceList__Item--no-link ');
                }
                html += templateInstance;
                index++;
            }
            entry.href = '#';
            const sup = entry.querySelector('sup');
            if (sup)
                sup.innerText = String(entryIndex);
            if (bibToggle) {
                entry.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (bibToggle.getAttribute('aria-expanded') === 'false')
                        bibToggle.click();
                });
            }
        });
        const list = bibContainer.querySelector('ul');
        if (list)
            list.innerHTML = html;
        const wrapper = bibContainer.closest('.ReferenceListWrapper');
        if (wrapper && index > 1) {
            wrapper.classList.remove('none');
        }
        domDelegateInstance.on('click', '.ReferenceLink', (e) => {
            e.preventDefault();
            const bibliographyToggle = document.querySelector('.ReferenceList__Button');
            if (!bibliographyToggle)
                return;
            if (bibliographyToggle.getAttribute('aria-expanded') == 'false') {
                bibliographyToggle.click();
            }
        });
    }
}
class Timer {
    constructor(element) {
        const date = element.dataset.timerDate;
        const timeAttribute = element.dataset.timerTime;
        const time = timeAttribute && /^\d\d:\d\d$/.test(timeAttribute) ? timeAttribute : '23:59';
        this.targetDate = new Date(`${date} ${time}`);
        if (isNaN(this.targetDate.getTime())) {
            console.warn("[Timer] Invalid date/time:", date, time);
            return;
        }
        this.dayEl = element.querySelector("[data-timer-days]");
        this.hourEl = element.querySelector("[data-timer-hours]");
        this.minuteEl = element.querySelector("[data-timer-minutes]");
        this.secondEl = element.querySelector("[data-timer-seconds]");
        element.setAttribute('data-timer-initialized', 'true');
        this.start();
    }
    start() {
        this.update(); // initial render
        this.intervalId = window.setInterval(() => this.update(), 1000);
    }
    stop() {
        if (this.intervalId)
            window.clearInterval(this.intervalId);
    }
    update() {
        const now = Date.now();
        const distance = this.targetDate.getTime() - now;
        if (distance <= 0) {
            this.render(0, 0, 0, 0);
            this.stop();
            return;
        }
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((distance / (1000 * 60)) % 60);
        const seconds = Math.floor((distance / 1000) % 60);
        this.render(days, hours, minutes, seconds);
    }
    render(days, hours, minutes, seconds) {
        if (this.dayEl)
            this.dayEl.textContent = String(days);
        if (this.hourEl)
            this.hourEl.textContent = String(hours);
        if (this.minuteEl)
            this.minuteEl.textContent = String(minutes);
        if (this.secondEl)
            this.secondEl.textContent = String(seconds);
    }
    static init() {
        const initTimers = (element) => {
            if (element instanceof HTMLElement && element.classList.contains('Timer') && element.getAttribute('data-timer-initialized') !== 'true') {
                Timer.timers.set(element, new Timer(element));
            }
            else {
                element.querySelectorAll(".Timer:not([data-timer-initialized])").forEach((el) => {
                    Timer.timers.set(el, new Timer(el));
                });
            }
        };
        document.addEventListener(ThemeEditor.SHOPIFY_SECTION_LOAD, (event) => {
            const sectionEl = event.target;
            initTimers(sectionEl);
        });
        document.addEventListener(ThemeEditor.SHOPIFY_SECTION_UNLOAD, (event) => {
            const sectionEl = event.target;
            sectionEl.querySelectorAll('.Timer').forEach((el) => {
                const timer = Timer.timers.get(el);
                if (timer)
                    timer.stop();
                Timer.timers.delete(el);
            });
        });
        document.addEventListener('theme:timer:init', (e) => {
            if (e.target instanceof HTMLElement || e.target instanceof Document)
                initTimers(e.target);
        });
        initTimers(document);
    }
}
Timer.timers = new Map();
class ShowMore {
    static init(parent = document) {
        parent.querySelectorAll('[data-action="show-more"]:not([data-show-more-initialized])').forEach((toggleButton) => {
            toggleButton.setAttribute('data-show-more-initialized', 'true');
            const transitionEndListener = (e) => {
                const target = e.target;
                if (e.propertyName === 'grid-template-rows' ||
                    (target.classList.contains('ShowMore__Button') && e.propertyName === 'bottom')) {
                    // grid-template-rows event is not fired on iOS, so we handle both
                    let view = target;
                    if (target.classList.contains('ShowMore__Button')) {
                        const controlsId = target.getAttribute('aria-controls');
                        if (!controlsId)
                            return;
                        view = document.getElementById(controlsId);
                    }
                    if (!view)
                        return;
                    view.style.removeProperty(`--show-more-height-${view.id}`);
                    view.removeEventListener('transitionend', transitionEndListener);
                    if (view.getAttribute('data-show-more-state') === 'collapsed') {
                        const rect = view.getBoundingClientRect();
                        const scrollPosition = rect.bottom + window.scrollY - window.innerHeight / 2;
                        window.scrollTo({
                            top: scrollPosition,
                            behavior: 'smooth',
                        });
                    }
                }
            };
            const toggle = (e) => {
                const button = e.currentTarget;
                const controlsId = button.getAttribute('aria-controls');
                if (!controlsId)
                    return;
                const view = document.getElementById(controlsId);
                if (!view)
                    return;
                if (!view.classList.contains('ShowMore--breakText')) {
                    const firstChild = view.firstElementChild;
                    if (firstChild) {
                        view.style.setProperty(`--show-more-height-${view.id}`, `${firstChild.scrollHeight}px`);
                        void view.offsetHeight; // Force reflow
                        view.addEventListener('transitionend', transitionEndListener);
                    }
                }
                const isExpanded = button.getAttribute('aria-expanded') === 'true';
                button.setAttribute('aria-expanded', String(!isExpanded));
                view.setAttribute('data-show-more-state', isExpanded ? 'collapsed' : 'open');
                // If view is inside a masonry, trigger reload
                const closestMasonry = view.closest('[data-masonry-grid="true"]');
                if (closestMasonry) {
                    closestMasonry.dispatchEvent(new CustomEvent('theme:masonry:change'));
                }
            };
            toggleButton.addEventListener('click', toggle);
        });
    }
}
class ThemeEditor {
    static registerSection(type, constructor) {
        if (!type || !constructor)
            return;
        ThemeEditor.registeredSections[type] = { constructor, instances: {} };
        document.querySelectorAll(`[data-section-type="${type}"][data-section-id]`).forEach((element) => {
            const sectionId = element.dataset.sectionId;
            ThemeEditor.registeredSections[type].instances[sectionId] = new constructor(element);
        });
    }
    static init() {
        document.addEventListener(ThemeEditor.SHOPIFY_SECTION_LOAD, (event) => {
            initFunctions(event.target);
            const sectionElement = event.target.querySelector('section[data-section-type][data-section-id]');
            if (!sectionElement)
                return;
            const sectionType = sectionElement.dataset.sectionType;
            const sectionId = sectionElement.dataset.sectionId;
            const registeredSection = ThemeEditor.registeredSections[sectionType];
            if (!registeredSection)
                return;
            const sectionInstance = registeredSection.instances[sectionId];
            if (sectionInstance && typeof sectionInstance.onUnload === 'function')
                sectionInstance.onUnload();
            registeredSection.instances[sectionId] = new registeredSection.constructor(sectionElement);
        });
    }
}
ThemeEditor.SHOPIFY_SECTION_LOAD = 'shopify:section:load';
ThemeEditor.SHOPIFY_SECTION_UNLOAD = 'shopify:section:unload';
ThemeEditor.SHOPIFY_SECTION_SELECT = 'shopify:section:select';
ThemeEditor.SHOPIFY_SECTION_DESELECT = 'shopify:section:deselect';
ThemeEditor.SHOPIFY_BLOCK_SELECT = 'shopify:block:select';
ThemeEditor.SHOPIFY_BLOCK_DESELECT = 'shopify:block:deselect';
ThemeEditor.registeredSections = {};
function initFunctions(parent) {
    Masonry.initGrids(parent.querySelectorAll('[data-masonry-grid="true"]'));
    TabSwitch.init(parent);
    ShowMore.init(parent);
    parent.querySelectorAll('[data-section-type="customer-stories"]:not([data-customer-stories-initialized])').forEach(cStory => {
        cStory.setAttribute('data-customer-stories-initialized', 'true');
        new CustomerStories(cStory);
    });
    parent.querySelectorAll('[data-modal-loading-config]:not([data-modal-loading-config-initialized])').forEach(element => {
        element.setAttribute('data-modal-loading-config-initialized', 'true');
        const data = JSON.parse(element.getAttribute('data-modal-loading-config'));
        if (!data.initial)
            return;
        const search = new URLSearchParams(window.location.search);
        const cookieKey = `theme_hide_${location.pathname.replace(/.*\//, '')}_${element.id}`;
        if ((!data.source || data.source === search.get('referrer_template')) && !window.getCookie(cookieKey)) {
            ModalLoading.showModalLoading(element);
        }
    });
    parent.querySelectorAll('[data-modal-loading]:not([data-modal-loading-initialized])').forEach(link => {
        link.setAttribute('data-modal-loading-initialized', 'true');
        link.setAttribute('data-href', link.href);
        link.href = 'javascript:void(0);';
    });
    parent.querySelectorAll('.scroll-list:not([data-scroll-list-initialized])').forEach(element => {
        element.setAttribute('data-scroll-list-initialized', 'true');
        new ScrollList(element);
    });
    parent.querySelectorAll('form.Form--multistep:not([data-multi-step-form-initialized])').forEach(element => {
        element.setAttribute('data-multi-step-form-initialized', 'true');
        new MultiStepForm(element);
    });
    parent.querySelectorAll('[data-wistia-lazyload-media-id]:not([data-wistia-lazyload-initialized])').forEach(element => {
        element.setAttribute('data-wistia-lazyload-initialized', 'true');
        WistiaLoader.loadVideo(element);
    });
    parent.querySelectorAll('[data-section-type="product-recommendations"]:not([data-product-recommendations-initialized])').forEach((section) => {
        section.setAttribute('data-product-recommendations-initialized', 'true');
        new ProductRecommendations(section);
    });
    parent.querySelectorAll('[data-section-type="recently-viewed-products"]:not([data-recently-viewed-products-initialized])').forEach((section) => {
        section.setAttribute('data-recently-viewed-products-initialized', 'true');
        new RecentlyViewedProducts(section);
    });
    parent.querySelectorAll('.Rte iframe').forEach((iframe) => {
        if (iframe.closest('code') || iframe.closest('.VideoWrapper'))
            return;
        if (iframe.src.indexOf('youtube') !== -1 || iframe.src.indexOf('youtu.be') !== -1 || iframe.src.indexOf('vimeo') !== -1) {
            iframe.outerHTML = '<div class="VideoWrapper">' + iframe.outerHTML + '</div>'; // Re-set the src attribute on each iframe after page load for Chrome's "incorrect iFrame content on 'back'" bug.
            // https://code.google.com/p/chromium/issues/detail?id=395791. Need to specifically target video and admin bar
            iframe.src = iframe.src;
        }
    });
    parent.querySelectorAll('button.DefaultCloseButton:not([data-default-close-button-initialized])').forEach((closeButton) => {
        closeButton.setAttribute('data-default-close-button-initialized', 'true');
        const initialDelayAttr = closeButton.getAttribute('data-initial-delay');
        const initialDelay = initialDelayAttr ? parseInt(initialDelayAttr, 10) : 0;
        const closeContainer = closeButton.closest('.DefaultCloseContainer');
        const cookieId = closeButton.getAttribute('data-cookie-id');
        if (!closeContainer) {
            console.warn('You defined a close button but not closable element', closeButton);
            return;
        }
        const cookieValue = cookieId ? window.getCookie(`hide-${cookieId}`) : null;
        if (!cookieId || cookieValue === '') {
            if (!initialDelay || initialDelay === 0) {
                closeContainer.setAttribute('aria-hidden', 'false');
            }
            else if (initialDelay > 0) {
                setTimeout(() => {
                    closeContainer.setAttribute('aria-hidden', 'false');
                }, initialDelay * 1000);
            }
            closeButton.addEventListener('click', (event) => {
                closeContainer.setAttribute('aria-hidden', 'true');
                const target = event.currentTarget;
                const hideDuration = target.getAttribute('data-hide-duration');
                const targetCookieId = target.getAttribute('data-cookie-id');
                if (targetCookieId && hideDuration) {
                    window.setCookie(`hide-${targetCookieId}`, 'true', hideDuration);
                }
                else if (targetCookieId) {
                    window.setCookie(`hide-${targetCookieId}`, 'true');
                }
            });
        }
        else {
            const ariaHidden = closeContainer.getAttribute('aria-hidden');
            if (!ariaHidden || ariaHidden === 'false') {
                closeContainer.setAttribute('aria-hidden', 'true');
            }
        }
    });
}
(async () => {
    // @ts-expect-error
    const domDelegateInstance = new domDelegate.Delegate(document.body);
    new Responsive();
    new Accessibility();
    new Metafields();
    new LoadingBar();
    new SidebarMenu();
    new EmbedManager();
    new Recipe();
    new Collection();
    new ThemeTracking();
    new OutOfStockDialog();
    //maybe static?!
    window.pageOverlay = new PageOverlay();
    window.Currency = Currency;
    window.Cart = Cart;
    window.Modal = Modal;
    window.WistiaLoader = WistiaLoader;
    Cart.init(domDelegateInstance);
    SidebarMenu.init(domDelegateInstance);
    Intercom.init(domDelegateInstance);
    SliderContainer.initAllSliders();
    Article.initAllArticles();
    Timer.init();
    HeaderSection.init();
    FeaturedProductsSection.init();
    AnchorScrolling.init(domDelegateInstance);
    ReferenceList.init(domDelegateInstance);
    Dialog.init();
    Collection.init(domDelegateInstance);
    Drawer.init(domDelegateInstance);
    Collapsible.init(domDelegateInstance);
    ImageViewer.init(domDelegateInstance);
    DynamicPopover.init(domDelegateInstance);
    KlaviyoForm.init(domDelegateInstance);
    EmbedManager.init(domDelegateInstance);
    LoginArea.init(domDelegateInstance);
    Modal.init(domDelegateInstance);
    ModalLoading.init(domDelegateInstance);
    Loyalty.init();
    Localization.init().then();
    NutritionIngredients.init();
    FloatingWidget.init(window.theme.floatingWidgets);
    ThemeEditor.init();
    ThemeEditor.registerSection('main-product', ProductSection);
    ThemeEditor.registerSection('instagram-posts', InstagramPostsSection);
    initFunctions(document);
    domDelegateInstance.on('theme:element:init', (e, target) => {
        initFunctions(target);
    });
    window.themeJSReady = true;
})();
